import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Slider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Switch,
  Alert,
  TableSortLabel,
  TextField,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardActionArea,
  CardContent,
  Radio,
  CircularProgress,
  Grid,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LabelIcon from '@mui/icons-material/Label';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MapIcon from '@mui/icons-material/Map';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import BarChartIcon from '@mui/icons-material/BarChart';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LinkIcon from '@mui/icons-material/Link';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend,
  BarChart, Bar,
} from 'recharts';
import NetworkGraphMap, { AreaBounds } from './NetworkGraphMap';
import LinkAnalysisTimelineHeatmap from './LinkAnalysisTimelineHeatmap';
import { LinkAnalysisSettings } from '@signals/app-shell/stores/settingsStore';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import TimelineIcon from '@mui/icons-material/Timeline';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MouseIcon from '@mui/icons-material/Mouse';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import TabIcon from '@mui/icons-material/Tab';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BoltIcon from '@mui/icons-material/Bolt';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import CircleIcon from '@mui/icons-material/Circle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
  Slideshow as PowerPointIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ManageSearch as SearchTermIcon,
  Schedule as ScheduleIcon,
  ContentCopy as CopyIcon,
  OpenInFull as OpenInFullIcon,
  CloseFullscreen as CloseFullscreenIcon,
  Hub as NodesTabIcon,
  Sms as SmsTabIcon,
  Apps as AppsTabIcon,
  Language as BrowsingTabIcon,
  Tune as ControlsTabIcon,
  HelpOutline as HelpTabIcon,
  Palette as LegendTabIcon,
  Build as GraphToolsIcon,
  ExpandLess as CollapseGraphToolsIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import LinkAnalysisToolPalette, { LinkAnalysisToolItem } from './LinkAnalysisToolPalette';
import {
  ExtractedSearch,
  aggregateSearches,
  tokenizeKeywords,
} from '@signals/app-shell/utils/searchTermExtraction';
import {
  exportLinkAnalysisToPowerPoint,
  captureNetworkGraph,
  captureLinkAnalysisMap,
  generateLinkAnalysisNarrative,
  PowerPointTheme,
  THEME_DEFINITIONS,
  LinkAnalysisStats,
  LinkAnalysisNode,
  LinkAnalysisEdge,
} from '@signals/app-shell/utils/powerpointExport';
import { countGraphDegrees, isNodeExpandableInGraph } from '../../utils/graphExpansion';

// Types for react-force-graph (using any for complex generics compatibility)
type ForceGraphRef = any;

interface Node {
  id: string;
  type?: string;
  group?: number;
  label?: string;
  entity_type?: string;
  place_name?: string;
  place_type?: string;
  place_address?: string;
  domain?: string;
  sample_url?: string;
  app_name?: string;
  package_name?: string;
  // Force graph positions
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
  vx?: number;
  vy?: number;
}

interface Link {
  source: string | Node;
  target: string | Node;
  weight?: number;
  type?: string;
  comms_direction?: string;
  sms_number?: string;
  sms_message?: string;
  comms_logs_type?: string;
  call_logs_number?: string;
  call_logs_duration?: number;
  url?: string;
  timestamp?: string;
  latitude?: number;
  longitude?: number;
  package_name?: string;
  total_time?: number;
}

// App package descriptions for tooltips
const APP_DESCRIPTIONS: Record<string, string> = {
  // === TOP APPS FROM DATABASE ===
  
  // Encrypted Messaging Apps
  'org.thoughtcrime.securesms': 'Signal Private Messenger - End-to-end encrypted messaging app for secure private communications. One of the most secure messaging platforms available.',
  'network.loki.messenger': 'Session Messenger - Decentralized encrypted messenger that doesn\'t require a phone number. Uses onion routing for anonymous communications.',
  'ch.protonmail.android': 'ProtonMail - End-to-end encrypted email service based in Switzerland, known for strong privacy protections.',
  
  // Fitness & Health Tracking
  'com.ouraring.oura': 'Oura Ring - Sleep and activity tracking app for the Oura smart ring. Monitors sleep quality, heart rate, and daily activity.',
  'fi.polar.polarflow': 'Polar Flow - Fitness and sleep tracking app for Polar sports watches and sensors.',
  
  // Location & Field Apps
  'com.qp.locationtracker': 'QP Location Tracker - GPS tracking and location monitoring application.',
  'com.esri.survey123': 'ESRI Survey123 - Professional field data collection app for creating and sharing surveys with location data.',
  
  // Communication & Push-to-Talk
  'com.slacorp.eptt.android': 'SLA Corp EPTT - Enhanced Push-to-Talk (PTT) communication app for team/enterprise communications.',
  'com.gotenna.proag': 'goTenna Pro - Off-grid mesh networking app for text and GPS communication without cell service or WiFi.',
  
  // Samsung System Apps
  'com.samsung.sdm': 'Samsung Desk Phone Manager - Allows users to check call history and contact list of their Samsung desk phone from a Galaxy device.',
  'com.samsung.sdm.sdmviewer': 'Samsung SDM Viewer - Companion viewer app for Samsung Desk Phone Manager.',
  'com.sec.android.app.clockpackage': 'Samsung Clock - Alarm, timer, stopwatch, and world clock app for Samsung devices.',
  'com.sec.android.app.camera': 'Samsung Camera - Default camera application for Samsung devices.',
  'com.sec.android.daemonapp': 'Samsung Weather - Background weather service and widget for Samsung devices.',
  'com.sec.android.gallery3d': 'Samsung Gallery - Photo and video gallery app for Samsung devices.',
  'com.sec.android.app.sbrowser': 'Samsung Internet Browser - Samsung\'s web browser with ad-blocking and privacy features.',
  'com.sec.android.app.popupcalculator': 'Samsung Calculator - Calculator app with floating window support.',
  'com.sec.android.app.myfiles': 'Samsung My Files - File manager app for Samsung devices.',
  'com.sec.android.app.magnifier': 'Samsung Magnifier - Accessibility tool that uses camera as a magnifying glass.',
  'com.sec.android.app.samsungapps': 'Galaxy Store - Samsung\'s app marketplace.',
  'com.sec.android.app.soundalive': 'Samsung SoundAlive - Audio enhancement and equalizer settings.',
  'com.sec.unifiedwfc': 'Samsung WiFi Calling - Enables voice calls over WiFi networks.',
  'com.samsung.unifiedtp': 'Samsung Unified Telephony - Background telephony services for Samsung devices.',
  'com.samsung.android.dialer': 'Samsung Phone Dialer - The default phone/calling app for Samsung devices.',
  'com.samsung.android.messaging': 'Samsung Messages - Default SMS/MMS messaging app for Samsung devices.',
  'com.samsung.android.contacts': 'Samsung Contacts - Manages contact information on Samsung devices.',
  'com.samsung.android.calendar': 'Samsung Calendar - Calendar and scheduling app for Samsung devices.',
  'com.samsung.android.email.provider': 'Samsung Email - Default email client for Samsung devices.',
  'com.samsung.android.gallery': 'Samsung Gallery - Photo and video gallery app.',
  'com.samsung.android.app.camera': 'Samsung Camera - Default camera application.',
  'com.samsung.android.app.notes': 'Samsung Notes - Note-taking and memo app.',
  'com.samsung.android.voc': 'Samsung Members - Customer support and community app.',
  'com.samsung.android.bixby.agent': 'Bixby Voice - Samsung\'s AI voice assistant.',
  'com.samsung.android.spay': 'Samsung Pay - Mobile payment and wallet service.',
  'com.samsung.android.scloud': 'Samsung Cloud - Cloud storage and backup service.',
  'com.samsung.android.app.smartcapture': 'Samsung Smart Capture - Screenshot and screen recording tool.',
  'com.samsung.android.game.gamehome': 'Samsung Game Launcher - Gaming hub and game optimization tools.',
  'com.samsung.android.lool': 'Samsung Game Launcher (Legacy) - Previous version of Samsung\'s gaming hub.',
  'com.samsung.android.forest': 'Samsung Digital Wellbeing - Focus mode and screen time management features.',
  'com.samsung.android.knox.dai': 'Samsung Knox DAI - Device Admin Intelligence for enterprise security management.',
  'com.samsung.android.knox.kpu': 'Samsung Knox Platform Update - Knox security platform updates.',
  'com.samsung.android.knox.kfbp': 'Samsung Knox Firmware-Over-The-Air - Knox firmware update service.',
  'com.sds.emm.cloud.knox.samsung': 'Samsung Knox EMM - Enterprise Mobility Management for corporate device administration.',
  'com.sds.emm.sers': 'Samsung Enterprise Reporting - Enterprise device analytics and reporting.',
  'com.samsung.android.honeyboard': 'Samsung Keyboard - Default keyboard app for Samsung devices.',
  'com.samsung.android.secsoundpicker': 'Samsung Sound Picker - Ringtone and notification sound selector.',
  'com.samsung.android.privacydashboard': 'Samsung Privacy Dashboard - Privacy controls and permissions management.',
  'com.samsung.android.emergency': 'Samsung Emergency Mode - Emergency SOS and power saving features.',
  'com.samsung.android.app.dressroom': 'Samsung Theme Store - Wallpapers, themes, and icons for Samsung devices.',
  'com.samsung.android.nfc': 'Samsung NFC Service - Near Field Communication functionality.',
  'com.samsung.android.dbsc': 'Samsung Device Care - Battery and storage optimization.',
  'com.samsung.android.kmxservice': 'Samsung Knox Matrix - Advanced Knox security services.',
  'com.samsung.android.easysetup': 'Samsung Easy Setup - Initial device setup and configuration.',
  'com.samsung.android.app.spage': 'Samsung Free - News, games, and TV streaming service.',
  'com.samsung.android.app.soundpicker': 'Samsung Sound Picker - Audio file selection tool.',
  'com.samsung.android.app.interpreter': 'Samsung Interpreter - Real-time translation feature.',
  'com.samsung.android.setting.multisound': 'Samsung MultiSound - Separate audio output settings.',
  'com.samsung.vvm': 'Samsung Visual Voicemail - Visual voicemail management.',
  'com.samsung.sree': 'Samsung Smart Relay - Background smart device connectivity.',
  'com.osp.app.signin': 'Samsung Account Sign-In - Samsung account authentication.',
  'com.samsung.android.app.clockpack': 'Samsung Clock - Alarm, timer, and world clock app.',
  'com.samsung.android.app.reminder': 'Samsung Reminder - Task and reminder management.',
  'com.samsung.android.incallui': 'Samsung In-Call UI - Handles active call interface.',
  'com.samsung.android.app.telephonyui': 'Samsung Telephony UI - Phone system interface components.',
  
  // Google Apps
  'com.google.android.gms': 'Google Play Services - Core Google services and APIs that apps depend on.',
  'com.google.android.gsf': 'Google Services Framework - Syncs data with Google services.',
  'com.google.android.apps.maps': 'Google Maps - Navigation, maps, and location services.',
  'com.google.android.youtube': 'YouTube - Video streaming and sharing platform.',
  'com.google.android.gm': 'Gmail - Google\'s email service.',
  'com.google.android.apps.docs': 'Google Drive - Cloud storage and file sharing.',
  'com.google.android.apps.photos': 'Google Photos - Photo storage, backup, and editing.',
  'com.google.android.calendar': 'Google Calendar - Calendar and event scheduling.',
  'com.google.android.apps.messaging': 'Google Messages - SMS/RCS messaging app.',
  'com.google.android.dialer': 'Google Phone - Google\'s phone/dialer app.',
  'com.google.android.contacts': 'Google Contacts - Contact management synced with Google.',
  'com.google.android.keep': 'Google Keep - Note-taking and lists app.',
  'com.google.android.apps.translate': 'Google Translate - Translation service.',
  'com.google.android.apps.fitness': 'Google Fit - Health and fitness tracking.',
  'com.google.android.apps.chromecast.app': 'Google Home - Smart home device control.',
  'com.google.android.apps.meetings': 'Google Meet - Video conferencing.',
  'com.google.android.talk': 'Google Hangouts - Messaging and video calls (legacy).',
  'com.google.android.googlequicksearchbox': 'Google Search - Search and Google Assistant.',
  'com.google.android.networkstack.tethering': 'Android Tethering - Hotspot and tethering network services.',
  'com.google.android.projection.gearhead': 'Android Auto - In-car infotainment and navigation system.',
  'com.google.android.cellbroadcastreceiver': 'Emergency Alerts - Receives emergency broadcast alerts (AMBER, weather, etc.).',
  'com.google.android.captiveportallogin': 'Captive Portal Login - Handles WiFi login pages (hotels, airports, etc.).',
  'com.google.android.providers.media.module': 'Android Media Provider - System media file management.',
  'com.google.android.as': 'Android System Intelligence - AI features for Android including Now Playing.',
  'com.google.android.tts': 'Google Text-to-Speech - Converts text to spoken audio.',
  'com.google.android.documentsui': 'Android Files - System file picker and document access.',
  'com.google.android.packageinstaller': 'Package Installer - Handles app installation.',
  'com.google.android.apps.youtube.music': 'YouTube Music - Music streaming service by Google.',
  
  // Social Media & Communication
  'com.whatsapp': 'WhatsApp - Instant messaging and voice/video calls.',
  'com.facebook.katana': 'Facebook - Social networking app.',
  'com.facebook.orca': 'Facebook Messenger - Facebook\'s messaging app.',
  'com.instagram.android': 'Instagram - Photo/video sharing social network.',
  'com.twitter.android': 'X (Twitter) - Social media and news platform.',
  'com.snapchat.android': 'Snapchat - Photo/video messaging with disappearing content.',
  'com.tencent.mm': 'WeChat - Messaging, social media, and mobile payment.',
  'com.telegram.messenger': 'Telegram - Cloud-based instant messaging.',
  'org.telegram.messenger': 'Telegram - Cloud-based instant messaging.',
  'com.discord': 'Discord - Voice, video, and text communication platform.',
  'com.linkedin.android': 'LinkedIn - Professional networking.',
  'com.skype.raider': 'Skype - Video calls and messaging.',
  'com.viber.voip': 'Viber - Messaging and VoIP calls.',
  'jp.naver.line.android': 'LINE - Messaging and social platform.',
  'com.tiktok': 'TikTok - Short-form video platform.',
  'com.zhiliaoapp.musically': 'TikTok - Short-form video platform.',
  
  // Productivity & Work
  'com.microsoft.office.outlook': 'Microsoft Outlook - Email and calendar.',
  'com.microsoft.teams': 'Microsoft Teams - Business communication and collaboration.',
  'com.microsoft.office.word': 'Microsoft Word - Document editing.',
  'com.microsoft.office.excel': 'Microsoft Excel - Spreadsheet editing.',
  'com.microsoft.office.powerpoint': 'Microsoft PowerPoint - Presentation creation.',
  'com.microsoft.skydrive': 'Microsoft OneDrive - Cloud storage.',
  'com.slack': 'Slack - Team communication and collaboration.',
  'com.Slack': 'Slack - Team communication and collaboration.',
  'com.dropbox.android': 'Dropbox - Cloud storage and file sharing.',
  'com.evernote': 'Evernote - Note-taking and organization.',
  'com.todoist': 'Todoist - Task management and to-do lists.',
  'com.trello': 'Trello - Project management boards.',
  'com.notion.id': 'Notion - Notes, docs, and project management.',
  'us.zoom.videomeetings': 'Zoom - Video conferencing.',
  
  // Finance & Banking
  'com.paypal.android.p2pmobile': 'PayPal - Online payments and money transfers.',
  'com.venmo': 'Venmo - Mobile payment service.',
  'com.squareup.cash': 'Cash App - Mobile payments and investing.',
  'com.coinbase.android': 'Coinbase - Cryptocurrency exchange.',
  'com.robinhood.android': 'Robinhood - Stock and crypto trading.',
  
  // Entertainment & Media
  'com.netflix.mediaclient': 'Netflix - Streaming video service.',
  'com.spotify.music': 'Spotify - Music streaming.',
  'com.amazon.avod.thirdpartyclient': 'Amazon Prime Video - Video streaming.',
  'com.disney.disneyplus': 'Disney+ - Disney streaming service.',
  'com.hbo.hbonow': 'HBO Max - HBO streaming service.',
  'tv.twitch.android.app': 'Twitch - Live streaming platform.',
  'com.pandora.android': 'Pandora - Music streaming.',
  'com.soundcloud.android': 'SoundCloud - Music and audio platform.',
  'com.audible.application': 'Audible - Audiobooks and podcasts.',
  
  // Shopping
  'com.amazon.mShop.android.shopping': 'Amazon Shopping - Online marketplace.',
  'com.ebay.mobile': 'eBay - Online auction and shopping.',
  'com.alibaba.aliexpresshd': 'AliExpress - Online retail.',
  'com.shopify.mobile': 'Shopify - E-commerce platform.',
  
  // Travel & Transportation
  'com.ubercab': 'Uber - Ride-sharing service.',
  'com.lyft.android': 'Lyft - Ride-sharing service.',
  'com.airbnb.android': 'Airbnb - Vacation rentals and experiences.',
  'com.booking': 'Booking.com - Hotel and travel bookings.',
  'com.expedia.bookings': 'Expedia - Travel bookings.',
  
  // Health & Fitness
  'com.fitbit.FitbitMobile': 'Fitbit - Fitness tracking.',
  'com.strava': 'Strava - Running and cycling tracking.',
  'com.myfitnesspal.android': 'MyFitnessPal - Calorie and nutrition tracking.',
  'com.calm.android': 'Calm - Meditation and sleep.',
  'com.headspace.android': 'Headspace - Meditation and mindfulness.',
  
  // Utilities
  'com.android.chrome': 'Google Chrome - Web browser.',
  'org.mozilla.firefox': 'Firefox - Web browser.',
  'com.brave.browser': 'Brave Browser - Privacy-focused web browser.',
  'com.opera.browser': 'Opera Browser - Web browser with VPN.',
  'com.android.vending': 'Google Play Store - App marketplace.',
  'com.android.settings': 'Android Settings - System configuration.',
  'com.android.systemui': 'Android System UI - Core system interface.',
  
  // Security & VPN
  'com.nordvpn.android': 'NordVPN - VPN service.',
  'com.expressvpn.vpn': 'ExpressVPN - VPN service.',
  'com.lookout': 'Lookout Security - Mobile security app.',
  'com.avast.android.mobilesecurity': 'Avast Mobile Security - Antivirus.',
  
  // Carrier Apps (Verizon)
  'com.vzw.hss.myverizon': 'My Verizon - Verizon account management, bill pay, and usage tracking.',
  'com.verizon.mips.services': 'Verizon Services - Background Verizon network services.',
  'com.vzw.apnlib': 'Verizon APN Library - Verizon network configuration.',
  
  // Android System
  'android': 'Android System - Core operating system processes.',
  'com.android.phone': 'Android Phone - Core telephony service.',
  'com.android.mms': 'Android Messaging - Default SMS/MMS app.',
  'com.android.providers.contacts': 'Contacts Provider - Stores contact data.',
  'com.android.providers.calendar': 'Calendar Provider - Stores calendar data.',
  'com.android.providers.media': 'Media Provider - Manages media files.',
  'com.android.launcher': 'Android Launcher - Home screen.',
  'com.android.inputmethod.latin': 'Android Keyboard - Default keyboard.',
  'com.android.settings.intelligence': 'Settings Intelligence - Smart settings suggestions and search.',
  'com.android.dreams.basic': 'Android Daydream - Screen saver mode when device is docked or charging.',
  'com.android.intentresolver': 'Intent Resolver - System app chooser dialog.',
  'com.android.credentialmanager': 'Credential Manager - Manages passwords and passkeys.',
  
  // Reddit
  'com.reddit.frontpage': 'Reddit - Social news and discussion platform.',
  
  // Microsoft Apps (Additional)
  'com.microsoft.appmanager': 'Microsoft App Manager - Manages Microsoft apps and updates.',
};

// Helper function to get app description
const getAppDescription = (packageName: string): string | null => {
  if (!packageName) return null;
  
  // Direct match
  if (APP_DESCRIPTIONS[packageName]) {
    return APP_DESCRIPTIONS[packageName];
  }
  
  // Try matching by prefix for common vendors
  const prefixes = ['com.samsung', 'com.google', 'com.facebook', 'com.microsoft', 'com.android'];
  for (const prefix of prefixes) {
    if (packageName.startsWith(prefix)) {
      // Check if we have a generic description for this vendor
      const vendorName = prefix.split('.').pop();
      return `${vendorName?.charAt(0).toUpperCase()}${vendorName?.slice(1)} app - Part of the ${vendorName} suite of applications.`;
    }
  }
  
  return null;
};

interface NetworkGraphProps {
  nodes: Node[];
  links: Link[];
  // Original unfiltered data for timeline - so timeline can always show full extent
  originalNodes?: Node[];
  originalLinks?: Link[];
  width?: number;
  height?: number;
  onNodeClick?: (node: Node) => void;
  onSelectionChange?: (selectedNodes: Node[], autoExport?: boolean) => void;
  selectionMode?: boolean;
  onSelectionModeChange?: (active: boolean) => void;
  appearanceSettings?: LinkAnalysisSettings;
  onAddDeviceNote?: (deviceId: string, deviceLabel: string) => void;
  onSaveAsGeofence?: (location: { lat: number; lng: number; name: string; placeType?: string }) => void;
  onAddLocationNote?: (location: { lat: number; lng: number; name: string }) => void;
  onExportSubgraph?: (subgraphNodes: Node[], subgraphLinks: Link[], centerNode: Node) => void;
  selectedArea?: AreaBounds | null;
  onAreaSelect?: (area: AreaBounds | null) => void;
  expandedNodes?: Set<string>;
  onToggleExpand?: (nodeId: string) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  onExportEntityReport?: (nodeId: string, nodeLabel: string) => void | Promise<void>;
  hiddenNodes?: Set<string>;
  onToggleHide?: (nodeId: string) => void;
  selectedDates?: Set<string>;
  onDateFilter?: (dates: Set<string> | null) => void;
  selectedHours?: { day: number; hour: number }[];
  onHourFilter?: (hours: { day: number; hour: number }[] | null) => void;
  selectedConnectionTypes?: Set<string>;
  connectionType?: string; // Single filter from dropdown (e.g., 'dwell', 'all', 'digital')
  onEntityFilter?: (entityIds: string[]) => void;
  onConnectionTypeFilter?: (types: Set<string> | null) => void;
  selectedNodeTypes?: Set<string>;
  onNodeTypeFilter?: (types: Set<string> | null) => void;
  isSimulationFrozen?: boolean;
  onSimulationFreezeChange?: (frozen: boolean) => void;
  showOnlyKnownDevices?: boolean;
  onShowOnlyKnownDevicesChange?: (show: boolean) => void;
  aiHighlightedNodes?: Set<string>;
  sidePanelOpen?: boolean;
  onSidePanelToggle?: (open: boolean) => void;
  onViewDetails?: (node: Node, stats: { totalConnections: number; callConnections: number; smsConnections: number; browsingConnections: number; appConnections: number; dwellConnections: number; connectedDevices: string[]; connectedLocations: string[] }) => void;
  // Find similar entities (embedding-based similarity search)
  onFindSimilar?: (nodeId: string, nodeLabel: string) => void;
  // Callback to report the current filtered state to parent (for AI context)
  onFilteredDataChange?: (filteredNodes: Node[], filteredLinks: Link[], selectedCount: number) => void;
}

export interface NetworkGraphHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;
  restart: () => void;
  zoomToNode: (nodeId: string) => void;
  clearSelection: () => void;
  // Focus a node externally: zoom to it AND dim non-direct connections.
  // Pass null to clear any active focus.
  focusNode: (nodeId: string | null) => void;
}

// Military/Operational color palette - tactical, analytical look
const typeColorMap: Record<string, string> = {
  call: '#3D5A80',      // Navy (primary comms)
  sms: '#98C1D9',       // Cold blue (text comms)
  browsing: '#415A77',  // Gunmetal (digital activity)
  app: '#D6A86C',       // Sand (app usage)
  dwell: '#EE6C4D',     // Burnt orange (location activity)
  device: '#4A7C59',    // Military green (entities/devices)
  phone: '#3D5A80',     // Navy (external numbers)
  location: '#EE6C4D',  // Burnt orange (places)
  website: '#98C1D9',   // Cold blue (web domains)
};

const NetworkGraph = forwardRef<NetworkGraphHandle, NetworkGraphProps>(({
  nodes,
  links,
  originalNodes,
  originalLinks,
  width = 0,
  height = 0,
  onNodeClick,
  onSelectionChange,
  selectionMode = false,
  onSelectionModeChange,
  appearanceSettings,
  onAddDeviceNote,
  onSaveAsGeofence,
  onAddLocationNote,
  onExportSubgraph,
  selectedArea: externalSelectedArea,
  onAreaSelect: externalOnAreaSelect,
  expandedNodes,
  onToggleExpand,
  onExpandAll,
  onCollapseAll,
  onExportEntityReport,
  hiddenNodes,
  onToggleHide,
  selectedDates,
  onDateFilter,
  selectedHours,
  onHourFilter,
  selectedConnectionTypes,
  connectionType,
  onConnectionTypeFilter,
  selectedNodeTypes: _selectedNodeTypes,
  onNodeTypeFilter: _onNodeTypeFilter,
  onEntityFilter,
  isSimulationFrozen: externalIsSimulationFrozen,
  onSimulationFreezeChange,
  showOnlyKnownDevices: externalShowOnlyKnownDevices,
  onShowOnlyKnownDevicesChange,
  aiHighlightedNodes,
  sidePanelOpen: propSidePanelOpen,
  onSidePanelToggle,
  onViewDetails,
  onFindSimilar,
  onFilteredDataChange,
}, ref) => {
  const { singular, pluralLower } = useParticipantLabels();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  /** Recharts Tooltip defaults to dark text — sync label/item colors with MUI theme for dark/light mode */
  const rechartsTooltipProps = useMemo(
    () => ({
      contentStyle: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        fontSize: '0.75rem',
      },
      labelStyle: { color: theme.palette.text.primary },
      itemStyle: { color: theme.palette.text.secondary },
    }),
    [theme],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const graphAreaRef = useRef<HTMLDivElement>(null);
  const fgRef = useRef<ForceGraphRef>(null);
  const graphNodesRef = useRef<any[]>([]); // Stores nodes with D3-computed x/y coordinates
  const [dimensions, setDimensions] = useState({ width: width || 1200, height: height || 700 });
  const [colorSchemeMode, _setColorSchemeMode] = useState<string>('type');
  const [linkDistance, setLinkDistance] = useState<number>(appearanceSettings?.linkDistance ?? 100);
  const [chargeStrength, setChargeStrength] = useState<number>(appearanceSettings?.chargeStrength ?? -300);
  const [curvature, setCurvature] = useState<number>(appearanceSettings?.linkCurvature ?? 0.8);
  const [nodeSize, setNodeSize] = useState<number>(appearanceSettings?.nodeSize ?? 5);
  const [linkOpacity, setLinkOpacity] = useState<number>(appearanceSettings?.linkOpacity ?? 0.30);
  const [stats, setStats] = useState<{ nodes: number; links: number }>({ nodes: 0, links: 0 });
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [selectionModeActive, setSelectionModeActive] = useState(selectionMode);
  // Use controlled state if prop is provided, otherwise use local state
  const [localSidePanelOpen, setLocalSidePanelOpen] = useState(true);
  const sidePanelOpen = propSidePanelOpen !== undefined ? propSidePanelOpen : localSidePanelOpen;
  
  // Graph tool strip and jump-to-panel palette, mirroring the Pathfinder page.
  const [graphToolsOpen, setGraphToolsOpen] = useState(true);
  const [toolPaletteOpen, setToolPaletteOpen] = useState(false);

  // Resizable side panel state
  const [sidePanelWidth, setSidePanelWidth] = useState(700);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const setSidePanelOpen = (open: boolean) => {
    if (onSidePanelToggle) {
      onSidePanelToggle(open);
    } else {
      setLocalSidePanelOpen(open);
    }
  };
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [_mapSelectionInfo, setMapSelectionInfo] = useState<{ label: string; type: string; connections: number } | null>(null);
  const [mapSelectionResetNonce, _setMapSelectionResetNonce] = useState(0);
  // Matrix chart state for shared connections filtering
  const [matrixHighlightedCell, setMatrixHighlightedCell] = useState<{ i: number; j: number } | null>(null);
  const [matrixSelectedEntities, setMatrixSelectedEntities] = useState<string[] | null>(null);
  const [hoveredNode, setHoveredNode] = useState<Node | null>(null);
  
  // Flow map state - shared with NetworkGraphMap for sync
  const [flowMapEnabled, setFlowMapEnabled] = useState(false);
  
  // Map zoom location - triggers map to fly to a specific location
  const [mapZoomLocation, setMapZoomLocation] = useState<{ lat: number; lng: number; zoom?: number } | null>(null);
  
  // Box selection state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null);
  
  // Pulse animation for selected locations
  const [pulsePhase, setPulsePhase] = useState(0);
  
  // Flash animation for AI-highlighted nodes
  const [aiHighlightFlash, setAiHighlightFlash] = useState(0);
  const prevAiHighlightedRef = useRef<Set<string>>(new Set());
  
  // Label visibility override - when true, shows labels regardless of zoom
  const [forceShowLabels, setForceShowLabels] = useState(false);
  
  // Hide single-entity connections - when true, hides external nodes (websites, locations, phones, apps) 
  // that only connect to one entity (not shared between multiple entities)
  const [hideNonSharedNodes, setHideNonSharedNodes] = useState(false);
  
  // Focus state - click a node to highlight its connections, fade others
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  
  // Show only known devices state
  const [localShowOnlyKnownDevices, setLocalShowOnlyKnownDevices] = useState(false);
  const showOnlyKnownDevices = externalShowOnlyKnownDevices !== undefined ? externalShowOnlyKnownDevices : localShowOnlyKnownDevices;
  const setShowOnlyKnownDevices = (show: boolean) => {
    if (onShowOnlyKnownDevicesChange) {
      onShowOnlyKnownDevicesChange(show);
    } else {
      setLocalShowOnlyKnownDevices(show);
    }
  };

  // Simulation freeze state
  const [localIsSimulationFrozen, setLocalIsSimulationFrozen] = useState(false);
  const isSimulationFrozen = externalIsSimulationFrozen !== undefined ? externalIsSimulationFrozen : localIsSimulationFrozen;
  const setIsSimulationFrozen = (frozen: boolean) => {
    if (onSimulationFreezeChange) {
      onSimulationFreezeChange(frozen);
    } else {
      setLocalIsSimulationFrozen(frozen);
    }
  };

  // Refs for callbacks
  const onNodeClickRef = useRef(onNodeClick);
  const onSelectionChangeRef = useRef(onSelectionChange);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; node: Node } | null>(null);

  // PowerPoint export state
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<PowerPointTheme>('standard');
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string>('');

  // Area selection state
  const [localSelectedArea, setLocalSelectedArea] = useState<AreaBounds | null>(null);
  const selectedArea = externalSelectedArea !== undefined ? externalSelectedArea : localSelectedArea;
  const setSelectedArea = externalOnAreaSelect || setLocalSelectedArea;

  // Sorting state
  type SortOrder = 'asc' | 'desc';
  const [nodesSort, setNodesSort] = useState<{ field: string; order: SortOrder }>({ field: 'id', order: 'asc' });
  const [smsSort, setSmsSort] = useState<{ field: string; order: SortOrder }>({ field: 'timestamp', order: 'desc' });
  const [callsSort, setCallsSort] = useState<{ field: string; order: SortOrder }>({ field: 'timestamp', order: 'desc' });
  const [dwellsSort, setDwellsSort] = useState<{ field: string; order: SortOrder }>({ field: 'timestamp', order: 'desc' });
  const [appsSort, setAppsSort] = useState<{ field: string; order: SortOrder }>({ field: 'timestamp', order: 'desc' });
  const [browsingSort, setBrowsingSort] = useState<{ field: string; order: SortOrder }>({ field: 'timestamp', order: 'desc' });

  // Search-term extraction tab (graph-wide). Mirrors the per-domain version
  // in EntityDetailsDialog but operates over every browsing link in the graph.
  type SearchSortKey = 'count' | 'recent' | 'alpha';
  const [searchTermFilter, setSearchTermFilter] = useState('');
  const [searchSort, setSearchSort] = useState<SearchSortKey>('count');
  const [searchEngineFilter, setSearchEngineFilter] = useState<string | null>(null);
  // Entity filter for the Search tab — when set, only searches that include
  // at least one occurrence from this entity are shown, AND the per-row
  // "searched by" list is collapsed to that entity. Lets an analyst pivot
  // to "everything Student-37 searched" in one click.
  const [searchEntityFilter, setSearchEntityFilter] = useState<string | null>(null);

  // Keep callback refs up to date
  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // Sync selection mode with parent prop
  useEffect(() => {
    setSelectionModeActive(selectionMode);
  }, [selectionMode]);

  // Sync appearance settings
  useEffect(() => {
    if (appearanceSettings) {
      if (appearanceSettings.linkDistance !== undefined) setLinkDistance(appearanceSettings.linkDistance);
      if (appearanceSettings.chargeStrength !== undefined) setChargeStrength(appearanceSettings.chargeStrength);
      if (appearanceSettings.linkCurvature !== undefined) setCurvature(appearanceSettings.linkCurvature);
      if (appearanceSettings.nodeSize !== undefined) setNodeSize(appearanceSettings.nodeSize);
      if (appearanceSettings.linkOpacity !== undefined) setLinkOpacity(appearanceSettings.linkOpacity);
    }
  }, [appearanceSettings]);

  // Pulse animation for selected locations - runs when there's a selection
  useEffect(() => {
    if (selectedNodes.size === 0) return;
    
    const interval = setInterval(() => {
      setPulsePhase(p => (p + 1) % 60); // 60 frames per cycle (~1 second at 60fps)
    }, 16); // ~60fps
    
    return () => clearInterval(interval);
  }, [selectedNodes.size]);

  // Flash animation for AI-highlighted nodes - triggers when new nodes are highlighted
  useEffect(() => {
    const prevSet = prevAiHighlightedRef.current;
    const currentSet = aiHighlightedNodes || new Set();
    
    // Check if new nodes were added
    const hasNewNodes = currentSet.size > 0 && 
      (prevSet.size !== currentSet.size || 
       Array.from(currentSet).some(id => !prevSet.has(id)));
    
    if (hasNewNodes) {
      // Start flash animation (3 pulses over 1.5 seconds)
      setAiHighlightFlash(90); // 90 frames = ~1.5 seconds at 60fps
      
      const flashInterval = setInterval(() => {
        setAiHighlightFlash(prev => {
          if (prev <= 0) {
            clearInterval(flashInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 16); // ~60fps
      
      return () => clearInterval(flashInterval);
    }
    
    prevAiHighlightedRef.current = new Set(currentSet);
  }, [aiHighlightedNodes]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    zoomIn: handleZoomIn,
    zoomOut: handleZoomOut,
    zoomReset: handleZoomReset,
    restart: handleRestart,
    zoomToNode: (nodeId: string) => {
      // Use graphNodesRef which has the nodes WITH D3-computed x/y coordinates
      const graphNodes = graphNodesRef.current;
      // Find the node (case-insensitive) - try graphNodesRef first (has positions)
      let node = graphNodes.find((n: any) => 
        n.id?.toLowerCase() === nodeId.toLowerCase() || 
        n.label?.toLowerCase() === nodeId.toLowerCase()
      );
      
      // Fallback to filteredNodes if not found in graphNodesRef
      if (!node) {
        node = filteredNodes.find(n => 
          n.id.toLowerCase() === nodeId.toLowerCase() || 
          n.label?.toLowerCase() === nodeId.toLowerCase()
        );
      }
      
      if (node && fgRef.current && node.x !== undefined && node.y !== undefined) {
        fgRef.current.centerAt(node.x, node.y, 800);
        fgRef.current.zoom(4, 800);
        // DON'T select the node - that triggers filtering which moves the layout
        // The parent will handle highlighting via aiHighlightedNodes
        console.log(`[NetworkGraph] Zoomed to node: ${node.id} at (${node.x.toFixed(1)}, ${node.y.toFixed(1)})`);
      } else if (node) {
        console.warn(`[NetworkGraph] Node ${nodeId} found but has no position yet`);
      } else {
        console.warn(`[NetworkGraph] Could not find node: ${nodeId}`);
      }
    },
    clearSelection: () => {
      setSelectedNodes(new Set());
      setFocusedNodeId(null);
      setSelectionModeActive(false);
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      onSelectionModeChange?.(false);
      if (onSelectionChange) onSelectionChange([]);
    },
    // Set (or clear) the internal focused node from the parent. When setting,
    // also zoom to it after the layout has a chance to position it.
    focusNode: (nodeId: string | null) => {
      if (!nodeId) {
        setFocusedNodeId(null);
        return;
      }
      // Resolve the actual node id case-insensitively against the current graph.
      const graphNodes = graphNodesRef.current;
      const resolved =
        graphNodes.find((n: any) =>
          n.id?.toLowerCase() === nodeId.toLowerCase() ||
          n.label?.toLowerCase() === nodeId.toLowerCase()
        ) ||
        filteredNodes.find(n =>
          n.id.toLowerCase() === nodeId.toLowerCase() ||
          n.label?.toLowerCase() === nodeId.toLowerCase()
        );
      const id = resolved?.id || nodeId;
      setFocusedNodeId(id);
      // Defer zoom so D3 can position the node if data just changed.
      const doZoom = () => {
        const gn = graphNodesRef.current;
        const positioned = gn.find((n: any) => n.id === id);
        if (positioned && fgRef.current && positioned.x !== undefined && positioned.y !== undefined) {
          fgRef.current.centerAt(positioned.x, positioned.y, 800);
          fgRef.current.zoom(4, 800);
        } else if (fgRef.current) {
          // Try one more frame; layout may not be ready yet.
          requestAnimationFrame(() => {
            const gn2 = graphNodesRef.current;
            const p2 = gn2.find((n: any) => n.id === id);
            if (p2 && fgRef.current && p2.x !== undefined && p2.y !== undefined) {
              fgRef.current.centerAt(p2.x, p2.y, 800);
              fgRef.current.zoom(4, 800);
            }
          });
        }
      };
      // One animation frame is usually enough; two is safer after a data reset.
      requestAnimationFrame(() => requestAnimationFrame(doZoom));
    },
  }));

  // Dynamically size the graph using ResizeObserver for accurate measurements
  useEffect(() => {
    const targetEl = graphAreaRef.current || containerRef.current;
    if (!targetEl) return;
    
    const updateDimensions = () => {
      if (graphAreaRef.current || containerRef.current) {
        const rect = (graphAreaRef.current || containerRef.current)!.getBoundingClientRect();
        setDimensions({
          width: rect.width || 1200,
          height: rect.height || 700
        });
      }
    };

    // Initial update
    updateDimensions();
    
    // Use ResizeObserver for accurate detection of container size changes
    // This properly handles CSS transitions and panel collapse/expand
    const resizeObserver = new ResizeObserver((_entries) => {
      // Use requestAnimationFrame to batch updates and avoid layout thrashing
      requestAnimationFrame(() => {
        updateDimensions();
      });
    });
    
    resizeObserver.observe(targetEl);
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [sidePanelOpen, sidePanelWidth]);

  // Handle Escape key to clear focus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusedNodeId) {
        setFocusedNodeId(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedNodeId]);

  // Handle panel resize drag
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = { startX: e.clientX, startWidth: sidePanelWidth };
  }, [sidePanelWidth]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const delta = resizeRef.current.startX - e.clientX;
      const newWidth = Math.max(350, Math.min(1200, resizeRef.current.startWidth + delta));
      console.log('[Resize] startX:', resizeRef.current.startX, 'clientX:', e.clientX, 'delta:', delta, 'newWidth:', newWidth);
      setSidePanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Filter nodes and links
  const { filteredNodes, filteredLinks, trackedEntities, primaryLocationIds } = useMemo(() => {
    let filtered = nodes;
    let filteredL = links;
    
    // Filter by known devices toggle
    if (showOnlyKnownDevices) {
      filtered = nodes.filter(node => {
        const id = node.id;
        return !(typeof id === 'string' && id.startsWith('phone:'));
      });
      
      filteredL = links.filter(link => {
        const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
        const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
        const sourceIsPhone = typeof sourceId === 'string' && sourceId.startsWith('phone:');
        const targetIsPhone = typeof targetId === 'string' && targetId.startsWith('phone:');
        return !sourceIsPhone && !targetIsPhone;
      });
    }
    
    // Filter by geographic area
    if (selectedArea) {
      filteredL = filteredL.filter((link: any) => {
        if (link.latitude !== undefined && link.longitude !== undefined) {
          const lat = link.latitude;
          const lng = link.longitude;
          return lat >= selectedArea.minLat && lat <= selectedArea.maxLat &&
                 lng >= selectedArea.minLng && lng <= selectedArea.maxLng;
        }
        return true;
      });
      
      const connectedNodeIds = new Set<string>();
      filteredL.forEach((link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        if (sourceId) connectedNodeIds.add(sourceId);
        if (targetId) connectedNodeIds.add(targetId);
      });
      
      filtered = filtered.filter(node => connectedNodeIds.has(node.id));
    }

    // Track entities that are directly connected to selected locations (for secondary highlight)
    let trackedEntities = new Set<string>();
    // Track locations that are directly selected (for primary highlight distinction)
    let primaryLocationIds = new Set<string>();

    // When there is a selection, fan-out to show ALL locations visited by connected entities
    if (selectedNodes.size > 0) {
      // Identify selected locations and selected entities (non-locations)
      const selectedLocationIds = new Set<string>();
      const selectedEntityIds = new Set<string>();
      
      nodes.forEach((n) => {
        if (selectedNodes.has(n.id)) {
          if (n.type === 'location') {
            selectedLocationIds.add(n.id);
          } else {
            selectedEntityIds.add(n.id);
          }
        }
      });

      // Store primary locations for highlight distinction
      primaryLocationIds = new Set(selectedLocationIds);

      // First pass: find all entities connected to selected locations
      // This enables "fan-out" - showing all locations those entities ever visited
      if (selectedLocationIds.size > 0) {
        filteredL.forEach((link: any) => {
          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
          if (!sourceId || !targetId) return;
          
          // If one endpoint is a selected location, the other is an entity to track
          if (selectedLocationIds.has(sourceId)) {
            selectedEntityIds.add(targetId);
          }
          if (selectedLocationIds.has(targetId)) {
            selectedEntityIds.add(sourceId);
          }
        });
      }

      // Store tracked entities for secondary highlight (entities connected to selected locations)
      trackedEntities = new Set(selectedEntityIds);

      const allowedIds = new Set<string>(selectedNodes);

      // Second pass: keep links involving any tracked entity (fan-out to all their locations)
      filteredL = filteredL.filter((link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        if (!sourceId || !targetId) return false;

        // Fan-out: keep if either endpoint is a tracked entity (shows all their connections)
        const touchesTrackedEntity =
          selectedEntityIds.has(sourceId) || selectedEntityIds.has(targetId);

        // Also keep if it directly touches a selected location
        const touchesSelectedLocation =
          selectedLocationIds.has(sourceId) || selectedLocationIds.has(targetId);

        // Fallback: if no selected locations (only entities selected), keep links touching selection
        const touchesSelectionFallback =
          selectedLocationIds.size === 0 && (selectedNodes.has(sourceId) || selectedNodes.has(targetId));

        const keep = touchesTrackedEntity || touchesSelectedLocation || touchesSelectionFallback;
        if (keep) {
          allowedIds.add(sourceId);
          allowedIds.add(targetId);
        }
        return keep;
      });

      filtered = filtered.filter(node => allowedIds.has(node.id));
    }

    // Filter out non-shared external nodes if toggle is enabled
    // External nodes (website, location, phone, app) that only connect to one entity are hidden
    if (hideNonSharedNodes) {
      const externalNodeTypes = new Set(['website', 'location', 'phone', 'app']);
      const nodeTypeMap = new Map<string, string>();
      filtered.forEach(node => nodeTypeMap.set(node.id, node.type || 'unknown'));
      
      // Count unique entity connections for each external node
      const externalNodeEntityConnections = new Map<string, Set<string>>();
      
      filteredL.forEach((link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        const sourceType = nodeTypeMap.get(sourceId) || 'unknown';
        const targetType = nodeTypeMap.get(targetId) || 'unknown';
        
        // If source is external and target is entity (device)
        if (externalNodeTypes.has(sourceType) && !externalNodeTypes.has(targetType)) {
          if (!externalNodeEntityConnections.has(sourceId)) {
            externalNodeEntityConnections.set(sourceId, new Set());
          }
          externalNodeEntityConnections.get(sourceId)!.add(targetId);
        }
        
        // If target is external and source is entity (device)
        if (externalNodeTypes.has(targetType) && !externalNodeTypes.has(sourceType)) {
          if (!externalNodeEntityConnections.has(targetId)) {
            externalNodeEntityConnections.set(targetId, new Set());
          }
          externalNodeEntityConnections.get(targetId)!.add(sourceId);
        }
      });
      
      // Find external nodes to hide (those with only 1 entity connection)
      const nodesToHide = new Set<string>();
      filtered.forEach(node => {
        const nodeType = node.type || 'unknown';
        if (externalNodeTypes.has(nodeType)) {
          const connectedEntities = externalNodeEntityConnections.get(node.id);
          if (!connectedEntities || connectedEntities.size < 2) {
            nodesToHide.add(node.id);
          }
        }
      });
      
      // Remove non-shared external nodes
      filtered = filtered.filter(node => !nodesToHide.has(node.id));
      
      // Remove links connected to hidden nodes
      filteredL = filteredL.filter((link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        return !nodesToHide.has(sourceId) && !nodesToHide.has(targetId);
      });
    }

    return { filteredNodes: filtered, filteredLinks: filteredL, trackedEntities, primaryLocationIds };
  }, [nodes, links, showOnlyKnownDevices, selectedArea, selectedNodes, hideNonSharedNodes]);

  // Update stats
  useEffect(() => {
    setStats({ nodes: filteredNodes.length, links: filteredLinks.length });
  }, [filteredNodes, filteredLinks]);

  // Report filtered data to parent for AI context
  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredNodes, filteredLinks, selectedNodes.size);
    }
  }, [filteredNodes, filteredLinks, selectedNodes.size, onFilteredDataChange]);

  // Color scale - maps group numbers to colors
  const colorScale = useMemo(() => scaleOrdinal<number, string>(schemeCategory10), []);

  // Calculate node degrees in the visible graph and the full original dataset.
  const nodeDegrees = useMemo(() => countGraphDegrees(filteredNodes, filteredLinks), [filteredNodes, filteredLinks]);

  const originalGraphNodes = originalNodes?.length ? originalNodes : filteredNodes;
  const originalGraphLinks = originalLinks?.length ? originalLinks : filteredLinks;
  const originalNodeDegrees = useMemo(
    () => countGraphDegrees(originalGraphNodes, originalGraphLinks),
    [originalGraphNodes, originalGraphLinks]
  );

  // Which nodes have additional connections to explore in the full dataset.
  const nodeExpandability = useMemo(() => {
    const expandable = new Map<string, boolean>();
    filteredNodes.forEach((node) => {
      expandable.set(
        node.id,
        isNodeExpandableInGraph(
          node,
          nodeDegrees,
          originalNodeDegrees,
          originalGraphLinks,
          originalGraphNodes
        )
      );
    });
    return expandable;
  }, [filteredNodes, nodeDegrees, originalNodeDegrees, originalGraphLinks, originalGraphNodes]);

  // ──────────────────────────────────────────────────────────────────────
  // Graph-wide Search tab aggregations
  // ──────────────────────────────────────────────────────────────────────
  // Aggregate every browsing link's URL through the shared search-engine
  // extractor and group by `engine|term`. Each row tracks where the search
  // was performed (which entity ran it on which domain) so analysts can pivot
  // back to the underlying event in one click.
  type SearchOccurrence = {
    sourceId: string;
    domain: string;
    url: string;
    timestamp: number;
  };
  type ExtractedSearchWithSources = ExtractedSearch & {
    sources: SearchOccurrence[];
  };
  const searchExtractions = useMemo<ExtractedSearchWithSources[]>(() => {
    const browsingLinks = filteredLinks.filter(
      (l: any) => l.type === 'browsing' && typeof l.url === 'string' && l.url.length > 0,
    );

    // Group each browsing link by (engine, term) using the shared extractor,
    // attaching the source entity + domain so analysts can pivot back.
    const byKey = new Map<string, ExtractedSearchWithSources>();
    for (const link of browsingLinks) {
      const url = (link as any).url as string;
      const tsRaw = (link as any).timestamp;
      const tsMs = !tsRaw
        ? 0
        : typeof tsRaw === 'number'
        ? tsRaw
        : new Date(tsRaw).getTime() || 0;
      const weight = typeof (link as any).weight === 'number' ? (link as any).weight : 1;

      // Reuse the shared aggregator on a single URL to identify the bucket.
      const oneRow = aggregateSearches([{ url, count: weight, lastVisit: tsMs }])[0];
      if (!oneRow) continue;

      const sourceId =
        typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
      const targetId =
        typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
      const domainNode = nodes.find((n) => n.id === targetId);
      const domain =
        domainNode?.domain || domainNode?.label || targetId?.replace('domain:', '') || '';

      const key = `${oneRow.engine}|${oneRow.term.toLowerCase()}`;
      const existing = byKey.get(key);
      const occurrence: SearchOccurrence = {
        sourceId: sourceId || 'unknown',
        domain,
        url,
        timestamp: tsMs,
      };
      if (existing) {
        existing.count += weight;
        if (tsMs > existing.lastVisit) {
          existing.lastVisit = tsMs;
          existing.url = url; // sample URL = most recent
        }
        existing.sources.push(occurrence);
      } else {
        byKey.set(key, { ...oneRow, sources: [occurrence] });
      }
    }

    const result = Array.from(byKey.values());
    for (const row of result) row.sources.sort((a, b) => b.timestamp - a.timestamp);
    return result;
  }, [filteredLinks, nodes]);

  // Header tiles + per-engine breakdown for the Search tab.
  const searchAggregates = useMemo(() => {
    const totalSearches = searchExtractions.reduce((sum, s) => sum + s.count, 0);
    const engineCounts = new Map<string, { count: number; color: string }>();
    for (const s of searchExtractions) {
      const ex = engineCounts.get(s.engine);
      if (ex) ex.count += s.count;
      else engineCounts.set(s.engine, { count: s.count, color: s.engineColor });
    }
    const lastSearchMs = searchExtractions.reduce((m, s) => Math.max(m, s.lastVisit), 0);
    const uniqueEntities = new Set<string>();
    for (const s of searchExtractions) {
      for (const src of s.sources) uniqueEntities.add(src.sourceId);
    }
    return {
      uniqueTerms: searchExtractions.length,
      totalSearches,
      uniqueEntities: uniqueEntities.size,
      lastSearch: lastSearchMs > 0 ? new Date(lastSearchMs) : null,
      engines: Array.from(engineCounts.entries())
        .map(([name, v]) => ({ name, count: v.count, color: v.color }))
        .sort((a, b) => b.count - a.count),
    };
  }, [searchExtractions]);

  // Hover state for the commonalities graph. When non-null, only the matching
  // node (entity or term) and its edges stay vivid; everything else dims so
  // the analyst can read a single entity's full search pattern or a single
  // term's spread across entities.
  const [hoveredCommonality, setHoveredCommonality] = useState<
    { type: 'entity' | 'term'; id: string } | null
  >(null);
  // Floating pop-out for the Commonalities graph. Opens a non-modal dialog
  // with a much larger view + more entities/terms so analysts have breathing
  // room for serious comparison without the inline graph eating the search
  // list's space.
  const [commonalityPopoutOpen, setCommonalityPopoutOpen] = useState(false);
  const [commonalityCollapsed, setCommonalityCollapsed] = useState(false);

  // Per-entity aggregation for the entity filter pills. One row per source
  // entity (e.g. Student-37) with the total number of search occurrences
  // it contributed across all terms. Sorted by count desc so the most active
  // searchers show up first.
  const searchEntityAggregates = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of searchExtractions) {
      // Each `s.sources` entry is one underlying browsing-link occurrence,
      // so counting array entries gives true per-entity search volume.
      const perEntity = new Map<string, number>();
      for (const src of s.sources) {
        perEntity.set(src.sourceId, (perEntity.get(src.sourceId) || 0) + 1);
      }
      for (const [sid, n] of perEntity) {
        counts.set(sid, (counts.get(sid) || 0) + n);
      }
    }
    return Array.from(counts.entries())
      .map(([sourceId, count]) => ({ sourceId, count }))
      .sort((a, b) => b.count - a.count);
  }, [searchExtractions]);

  // Commonalities graph data — a bipartite (entity ↔ term) connection set
  // for the top-K entities and top-K terms within the CURRENTLY FILTERED
  // search set. When the user filters to "girl", the graph re-ranks to
  // show only entities who searched girl-related terms and only the terms
  // that contain "girl", so the visualization always reflects what the
  // user is actually looking at instead of feeling disconnected from the
  // active filter. Each edge carries the count of searches that entity
  // made of that term, used as edge weight in the visualization.
  const commonalityGraph = useMemo(() => {
    const text = searchTermFilter.trim().toLowerCase();
    const engineKey = searchEngineFilter ? searchEngineFilter.trim().toLowerCase() : null;

    // Apply text + engine filters to the input set so the visualization
    // re-ranks within the user's current topic/engine scope. We DELIBERATELY
    // skip the entity filter here: entity selection is meant to spotlight
    // ONE searcher within the cross-entity pattern, not collapse the graph
    // to a single fan (which would defeat the whole "commonalities" idea).
    // The renderer's existing dim/highlight logic already handles the
    // entity filter as a visual emphasis.
    const filtered = searchExtractions.filter((s) => {
      if (engineKey && s.engine.trim().toLowerCase() !== engineKey) return false;
      if (text && !s.term.toLowerCase().includes(text)) return false;
      return true;
    });

    // Re-rank entities by their activity WITHIN the filtered set, so the
    // top-N shows the heaviest searchers for THIS topic, not globally.
    const entityCounts = new Map<string, number>();
    for (const s of filtered) {
      const per = new Map<string, number>();
      for (const src of s.sources) {
        per.set(src.sourceId, (per.get(src.sourceId) || 0) + 1);
      }
      for (const [sid, n] of per) {
        entityCounts.set(sid, (entityCounts.get(sid) || 0) + n);
      }
    }

    const ENTITY_CAP = 20;
    const TERM_CAP = 20;
    const allFilteredEntities = Array.from(entityCounts.entries())
      .map(([sourceId, count]) => ({ sourceId, count }))
      .sort((a, b) => b.count - a.count);
    const entities = allFilteredEntities.slice(0, ENTITY_CAP);

    const terms = [...filtered]
      .sort((a, b) => b.count - a.count)
      .slice(0, TERM_CAP)
      .map((t) => ({
        term: t.term,
        engine: t.engine,
        engineColor: t.engineColor,
        count: t.count,
        sources: t.sources,
      }));

    type Edge = {
      entityId: string;
      term: string;
      engineColor: string;
      count: number;
    };
    const edges: Edge[] = [];
    let maxCount = 0;
    for (const ent of entities) {
      for (const t of terms) {
        const count = t.sources.reduce(
          (n, src) => n + (src.sourceId === ent.sourceId ? 1 : 0),
          0,
        );
        if (count > 0) {
          edges.push({
            entityId: ent.sourceId,
            term: t.term,
            engineColor: t.engineColor,
            count,
          });
          if (count > maxCount) maxCount = count;
        }
      }
    }
    return {
      entities,
      terms,
      edges,
      maxCount,
      // Overflow counts reference the FILTERED universe so the "+N more"
      // hint accurately tells the user what's hidden in the current view.
      hiddenEntities: Math.max(0, allFilteredEntities.length - ENTITY_CAP),
      hiddenTerms: Math.max(0, filtered.length - TERM_CAP),
      // Expose the filtered totals so the inline header can say e.g.
      // "6 of 12" (instead of "6 of 261") when filters are active.
      totalEntities: allFilteredEntities.length,
      totalTerms: filtered.length,
    };
  }, [searchExtractions, searchTermFilter, searchEngineFilter]);

  // Top keywords across all search terms (weighted by occurrence). Useful for
  // spotting recurring intent across the whole graph at a glance.
  const topSearchKeywords = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of searchExtractions) {
      const unique = new Set(tokenizeKeywords(s.term));
      for (const t of unique) counts.set(t, (counts.get(t) || 0) + s.count);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map(([word, n]) => ({ word, count: n }));
  }, [searchExtractions]);

  // Apply user filters (engine + entity + text + sort) to the aggregated list.
  // Engine + entity comparisons are case-insensitive and trimmed so any
  // stray whitespace or casing diff between a chip label and the extracted
  // value can never accidentally zero out the list.
  const visibleSearches = useMemo(() => {
    const text = searchTermFilter.trim().toLowerCase();
    const engineKey = searchEngineFilter ? searchEngineFilter.trim().toLowerCase() : null;
    const entityKey = searchEntityFilter ? searchEntityFilter.trim().toLowerCase() : null;
    let list = searchExtractions.filter((s) => {
      if (engineKey && s.engine.trim().toLowerCase() !== engineKey) return false;
      if (entityKey && !s.sources.some((src) => src.sourceId.trim().toLowerCase() === entityKey)) {
        return false;
      }
      if (text) {
        return (
          s.term.toLowerCase().includes(text) ||
          s.engine.toLowerCase().includes(text) ||
          s.sources.some(
            (src) =>
              src.sourceId.toLowerCase().includes(text) ||
              src.domain.toLowerCase().includes(text),
          )
        );
      }
      return true;
    });
    list.sort((a, b) => {
      if (searchSort === 'count') return b.count - a.count;
      if (searchSort === 'recent') return b.lastVisit - a.lastVisit;
      return a.term.localeCompare(b.term);
    });
    return list;
  }, [searchExtractions, searchTermFilter, searchSort, searchEngineFilter, searchEntityFilter]);

  // Calculate which nodes and links are connected to the focused node
  const focusedConnections = useMemo(() => {
    if (!focusedNodeId) {
      return { connectedNodeIds: new Set<string>(), connectedLinkIndices: new Set<number>() };
    }
    
    const connectedNodeIds = new Set<string>();
    const connectedLinkIndices = new Set<number>();
    
    // The focused node itself is always "connected"
    connectedNodeIds.add(focusedNodeId);
    
    // Find all links connected to the focused node
    filteredLinks.forEach((link: any, index: number) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      
      if (sourceId === focusedNodeId || targetId === focusedNodeId) {
        connectedNodeIds.add(sourceId);
        connectedNodeIds.add(targetId);
        connectedLinkIndices.add(index);
      }
    });
    
    return { connectedNodeIds, connectedLinkIndices };
  }, [focusedNodeId, filteredLinks]);

  // When there is an active selection, compute 1-hop neighbors so connected nodes stay visible too.
  const connectedToSelection = useMemo(() => {
    if (selectedNodes.size === 0) return new Set<string>();
    const neighbors = new Set<string>();
    filteredLinks.forEach((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      if (!sourceId || !targetId) return;
      if (selectedNodes.has(sourceId)) neighbors.add(targetId);
      if (selectedNodes.has(targetId)) neighbors.add(sourceId);
    });
    return neighbors;
  }, [selectedNodes, filteredLinks]);

  // Detailed connection analysis for selected entities
  const selectionAnalysis = useMemo(() => {
    if (selectedNodes.size === 0) return null;

    // Categorize selected items
    const selectedEntities: Array<{ id: string; label: string; type: string }> = [];
    const selectedLocationIds: string[] = [];
    
    // Use coordinate matching for locations
    const extractCoordsLocal = (str: string): { lat: number; lng: number } | null => {
      if (!str) return null;
      const coordMatch = str.match(/coord_(-?\d+\.?\d*)_(-?\d+\.?\d*)/);
      if (coordMatch) return { lat: parseFloat(coordMatch[1]), lng: parseFloat(coordMatch[2]) };
      const labelMatch = str.match(/Location\s*\((-?\d+\.?\d*),?\s*(-?\d+\.?\d*)\)/);
      if (labelMatch) return { lat: parseFloat(labelMatch[1]), lng: parseFloat(labelMatch[2]) };
      return null;
    };

    selectedNodes.forEach(id => {
      const node = nodes.find(n => n.id === id);
      if (node) {
        if (node.type === 'location') {
          selectedLocationIds.push(id);
        } else if (node.type === 'device') {
          selectedEntities.push({ 
            id: node.id, 
            label: node.label || node.id,
            type: node.entity_type || 'Device'
          });
        }
      } else {
        // Check if it's a coordinate ID (from map selection)
        const coords = extractCoordsLocal(id);
        if (coords) {
          selectedLocationIds.push(id);
        }
      }
    });

    // Find all dwell links for selected entities
    const entityDwells: Record<string, Array<{
      locationId: string;
      locationLabel: string;
      timestamp: string;
      duration: number;
      lat?: number;
      lng?: number;
    }>> = {};

    // Also track which locations each entity visited
    const entityLocations: Record<string, Set<string>> = {};

    links.forEach((link: any) => {
      if (link.type !== 'dwell') return;
      
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      
      // Check if source is a selected entity (or tracked via fan-out)
      const entityId = trackedEntities.has(sourceId) ? sourceId : 
                       trackedEntities.has(targetId) ? targetId : null;
      
      if (!entityId) return;
      
      const locationId = entityId === sourceId ? targetId : sourceId;
      const locationNode = nodes.find(n => n.id === locationId);
      
      if (!entityDwells[entityId]) {
        entityDwells[entityId] = [];
        entityLocations[entityId] = new Set();
      }
      
      entityDwells[entityId].push({
        locationId,
        locationLabel: locationNode?.place_name || locationNode?.label || locationId,
        timestamp: link.timestamp || '',
        duration: link.weight || 1,
        lat: link.latitude,
        lng: link.longitude,
      });
      
      entityLocations[entityId].add(locationId);
    });

    // Find shared locations (locations visited by multiple tracked entities)
    const locationVisitors: Record<string, string[]> = {};
    Object.entries(entityLocations).forEach(([entityId, locs]) => {
      locs.forEach(locId => {
        if (!locationVisitors[locId]) locationVisitors[locId] = [];
        locationVisitors[locId].push(entityId);
      });
    });

    const sharedLocations = Object.entries(locationVisitors)
      .filter(([_, visitors]) => visitors.length > 1)
      .map(([locId, visitors]) => {
        const locNode = nodes.find(n => n.id === locId);
        
        // Get visit details for this shared location
        const visitDetails = visitors.map(entityId => {
          const visits = entityDwells[entityId]?.filter(d => d.locationId === locId) || [];
          const entityNode = nodes.find(n => n.id === entityId);
          return {
            entityId,
            entityLabel: entityNode?.label || entityId,
            visitCount: visits.length,
            totalDuration: visits.reduce((sum, v) => sum + v.duration, 0),
            firstVisit: visits.length > 0 ? visits.sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            )[0]?.timestamp : null,
            lastVisit: visits.length > 0 ? visits.sort((a, b) => 
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            )[0]?.timestamp : null,
          };
        });

        return {
          locationId: locId,
          locationLabel: locNode?.place_name || locNode?.label || locId,
          placeType: locNode?.place_type,
          visitors,
          visitorCount: visitors.length,
          visitDetails,
        };
      })
      .sort((a, b) => b.visitorCount - a.visitorCount);

    // Summary stats
    const totalSharedLocations = sharedLocations.length;
    const trackedEntityCount = trackedEntities.size;

    return {
      selectedEntities,
      selectedLocationIds,
      trackedEntityCount,
      entityDwells,
      sharedLocations,
      totalSharedLocations,
      summary: totalSharedLocations > 0 
        ? `${trackedEntityCount} entities share ${totalSharedLocations} location${totalSharedLocations > 1 ? 's' : ''}`
        : `${trackedEntityCount} tracked entities`,
    };
  }, [selectedNodes, nodes, links, trackedEntities]);

  // Get top connected nodes for filter overlay
  // Note: Currently unused but kept for potential future filtering features
  // const topConnectedNodes = useMemo(() => {
  //   const sortedByDegree = Array.from(nodeDegrees.entries())
  //     .sort((a, b) => b[1] - a[1])
  //     .slice(0, 5);
  //   return sortedByDegree.map(([nodeId, degree]) => {
  //     const node = filteredNodes.find(n => n.id === nodeId);
  //     return {
  //       id: nodeId,
  //       label: node?.label || nodeId,
  //       type: node?.type,
  //       connections: degree
  //     };
  //   });
  // }, [nodeDegrees, filteredNodes]);

  // Helper to extract coordinates from various ID/label formats
  // Handles: "coord_33.5142_-111.6821", "Location (33.5142, -111.6821)", "named_Place_33.51_-111.68"
  const extractCoords = useCallback((str: string): { lat: number; lng: number } | null => {
    if (!str) return null;
    
    // Match coord_LAT_LNG format (map IDs)
    const coordMatch = str.match(/coord_(-?\d+\.?\d*)_(-?\d+\.?\d*)/);
    if (coordMatch) {
      return { lat: parseFloat(coordMatch[1]), lng: parseFloat(coordMatch[2]) };
    }
    
    // Match named_NAME_LAT_LNG format
    const namedMatch = str.match(/named_.*?_(-?\d+\.?\d*)_(-?\d+\.?\d*)$/);
    if (namedMatch) {
      return { lat: parseFloat(namedMatch[1]), lng: parseFloat(namedMatch[2]) };
    }
    
    // Match "Location (LAT, LNG)" format (graph labels)
    const labelMatch = str.match(/Location\s*\((-?\d+\.?\d*),?\s*(-?\d+\.?\d*)\)/);
    if (labelMatch) {
      return { lat: parseFloat(labelMatch[1]), lng: parseFloat(labelMatch[2]) };
    }
    
    // Match raw "LAT, LNG" or "LAT_LNG" patterns
    const rawMatch = str.match(/(-?\d+\.\d{3,})[\s,_]+(-?\d+\.\d{3,})/);
    if (rawMatch) {
      return { lat: parseFloat(rawMatch[1]), lng: parseFloat(rawMatch[2]) };
    }
    
    return null;
  }, []);

  // Build a lookup of selected coordinates for flexible matching
  const selectedCoords = useMemo(() => {
    const coords: Array<{ lat: number; lng: number; originalId: string }> = [];
    selectedNodes.forEach(id => {
      const c = extractCoords(id);
      if (c) coords.push({ ...c, originalId: id });
    });
    // Debug: log extracted coords when selection changes
    if (coords.length > 0) {
      console.log('🎯 Selected coordinates for matching:', coords);
    }
    return coords;
  }, [selectedNodes, extractCoords]);

  // Check if a node matches any selected location by coordinates (with tolerance)
  const isSelectedByCoords = useCallback((nodeId: string, nodeLabel?: string): boolean => {
    if (selectedNodes.has(nodeId)) return true;
    if (selectedCoords.length === 0) return false;
    
    // Try to extract coords from node ID or label
    const nodeCoords = extractCoords(nodeId) || (nodeLabel ? extractCoords(nodeLabel) : null);
    if (!nodeCoords) return false;
    
    // Check if any selected coord matches (within ~50m tolerance)
    const TOLERANCE = 0.0018; // ~100 meters
    return selectedCoords.some(sc => 
      Math.abs(sc.lat - nodeCoords.lat) < TOLERANCE && 
      Math.abs(sc.lng - nodeCoords.lng) < TOLERANCE
    );
  }, [selectedNodes, selectedCoords, extractCoords]);

  // Custom node rendering
  const nodeCanvasObject = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const n = node as Node & { x: number; y: number };
    // Use flexible coordinate matching for selection check
    const isSelected = selectedNodes.has(n.id) || isSelectedByCoords(n.id, n.label);
    const isConnectedToSelected = connectedToSelection.has(n.id);
    const isAIHighlighted = aiHighlightedNodes?.has(n.id);
    const isHovered = hoveredNode?.id === n.id;
    const selectionActive = selectedNodes.size > 0;
    
    // Focus highlight check
    const isFocused = focusedNodeId === n.id;
    const isConnectedToFocus = focusedConnections.connectedNodeIds.has(n.id);
    const hasFocus = focusedNodeId !== null;
    
    // Check if this node is a tracked entity (directly connected to selected locations)
    // or a primary location (directly selected) - these get secondary highlight
    const isTrackedEntity = trackedEntities.has(n.id) && !isSelected;
    const isPrimaryLocation = primaryLocationIds.has(n.id) || isSelectedByCoords(n.id, n.label);
    const isPrimaryConnection = isTrackedEntity || isPrimaryLocation;
    
    // Calculate fadeAlpha: focus takes precedence, then selection
    let fadeAlpha = 1;
    if (hasFocus) {
      fadeAlpha = isConnectedToFocus ? 1 : 0.08;
    } else if (selectionActive) {
      fadeAlpha = isSelected ? 1 : isPrimaryConnection ? 0.9 : isConnectedToSelected ? 0.65 : 0.10;
    }
    
    // Detect if this is a location node (by type OR by ID pattern)
    const isLocationNode = n.type === 'location' || 
      (typeof n.id === 'string' && (n.id.startsWith('coord_') || n.id.startsWith('Location'))) ||
      (typeof n.label === 'string' && n.label.startsWith('Location'));
    
    // Calculate size - subtle size differences for hierarchy
    let size = nodeSize;
    if (isSelected && isLocationNode) size = nodeSize * 1.3; // Selected locations - subtle increase
    else if (isSelected) size = nodeSize * 1.4;
    else if (isFocused) size = nodeSize * 1.4;
    else if (isPrimaryConnection) size = nodeSize * 1.2;
    else if (isAIHighlighted) size = nodeSize * 1.2;
    else if (isHovered) size = nodeSize * 1.15;
    else if (isConnectedToSelected) size = nodeSize * 1.05;
    
    // Get color
    let color = colorScale(n.group || 0);
    if (colorSchemeMode === 'type' && n.type) {
      color = typeColorMap[n.type] || color;
    }
    
    // Override color for selected locations - make them WHITE to stand out
    if (isSelected && isLocationNode) {
      color = '#FFFFFF';
    }

    // Draw selection ring first (behind node)
    if (isSelected) {
      // For locations, use subtle pulsing ring
      if (isLocationNode) {
        // Calculate gentle pulse effect (0 to 1 sine wave)
        const pulse = Math.sin((pulsePhase / 60) * Math.PI * 2) * 0.5 + 0.5;
        const pulseSize = 2 + pulse * 3; // Ring expands from +2 to +5 (subtle)
        const pulseOpacity = 0.3 + pulse * 0.3; // Opacity from 0.3 to 0.6 (subtle)
        
        // Outer pulsing cyan glow (subtle)
        ctx.beginPath();
        ctx.arc(n.x, n.y, size + pulseSize, 0, 2 * Math.PI);
        ctx.strokeStyle = `rgba(0, 255, 255, ${pulseOpacity})`;
        ctx.lineWidth = (1.5 + pulse * 1) / globalScale;
        ctx.stroke();
        
        // Inner solid gold ring
        ctx.beginPath();
        ctx.arc(n.x, n.y, size + 2, 0, 2 * Math.PI);
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 1.5 / globalScale;
        ctx.stroke();
      } else {
        // Standard gold ring for non-locations (entities like students)
        ctx.beginPath();
        ctx.arc(n.x, n.y, size + 2, 0, 2 * Math.PI);
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 2 / globalScale;
        ctx.stroke();
      }
    }
    
    // Draw secondary highlight ring for tracked entities / primary connections (cyan)
    if (isPrimaryConnection && !isSelected) {
      ctx.beginPath();
      ctx.arc(n.x, n.y, size + 1.5, 0, 2 * Math.PI);
      ctx.strokeStyle = '#00BFFF'; // Deep sky blue - distinct from gold primary
      ctx.lineWidth = 1.5 / globalScale;
      ctx.stroke();
    }
    
    // Draw AI highlight ring - bright green pulsing ring for Sentinel-highlighted nodes
    if (isAIHighlighted && !isSelected && !isPrimaryConnection) {
      // Calculate flash intensity (pulses 3 times then settles)
      const flashIntensity = aiHighlightFlash > 0 
        ? Math.sin((aiHighlightFlash / 15) * Math.PI) * 0.5 + 0.5  // Pulsing during flash
        : 1; // Solid after flash
      const flashScale = aiHighlightFlash > 0 
        ? 1 + Math.sin((aiHighlightFlash / 15) * Math.PI) * 0.3  // Size pulse
        : 1;
      
      // Outer glow (larger during flash)
      ctx.beginPath();
      ctx.arc(n.x, n.y, (size + 6) * flashScale, 0, 2 * Math.PI);
      ctx.strokeStyle = `rgba(0, 255, 136, ${0.3 + flashIntensity * 0.4})`;
      ctx.lineWidth = (4 + flashIntensity * 4) / globalScale;
      ctx.stroke();
      
      // Middle glow
      ctx.beginPath();
      ctx.arc(n.x, n.y, (size + 4) * flashScale, 0, 2 * Math.PI);
      ctx.strokeStyle = `rgba(0, 255, 136, ${0.5 + flashIntensity * 0.3})`;
      ctx.lineWidth = (3 + flashIntensity * 2) / globalScale;
      ctx.stroke();
      
      // Inner bright ring
      ctx.beginPath();
      ctx.arc(n.x, n.y, (size + 2) * flashScale, 0, 2 * Math.PI);
      ctx.strokeStyle = aiHighlightFlash > 0 ? '#ffffff' : '#00ff88';
      ctx.lineWidth = (2 + flashIntensity) / globalScale;
      ctx.stroke();
    }
    
    // Draw focus ring (cyan glow) for the clicked/focused node
    if (isFocused) {
      // Outer glow
      ctx.beginPath();
      ctx.arc(n.x, n.y, size + 8, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(0, 255, 255, 0.4)';
      ctx.lineWidth = 4 / globalScale;
      ctx.stroke();
      
      // Inner bright ring
      ctx.beginPath();
      ctx.arc(n.x, n.y, size + 4, 0, 2 * Math.PI);
      ctx.strokeStyle = '#00ffff';
      ctx.lineWidth = 2 / globalScale;
      ctx.stroke();
    }

    // Draw white halo ring for nodes with hidden connections in the full dataset
    const isExpandable = nodeExpandability.get(n.id) || false;
    const showHalo = isExpandable && !expandedNodes?.has(n.id);
    
    if (showHalo && !isSelected && !isPrimaryConnection) {
      // Standard halo for non-selected nodes
      ctx.beginPath();
      ctx.arc(n.x, n.y, size + 3, 0, 2 * Math.PI);
      ctx.strokeStyle = isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 2 / globalScale;
      ctx.globalAlpha = fadeAlpha;
      ctx.stroke();
      ctx.globalAlpha = 1;
    } else if (showHalo && (isSelected || isPrimaryConnection) && isExpandable) {
      // Larger outer halo for selected expandable nodes
      ctx.beginPath();
      ctx.arc(n.x, n.y, size + 5, 0, 2 * Math.PI);
      ctx.strokeStyle = isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 2 / globalScale;
      ctx.globalAlpha = fadeAlpha;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    
    // Draw node circle
    ctx.beginPath();
    ctx.arc(n.x, n.y, size, 0, 2 * Math.PI);
    ctx.globalAlpha = fadeAlpha;
    ctx.fillStyle = color;
    ctx.fill();
    
    // Draw border
    ctx.strokeStyle = isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 1 / globalScale;
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Draw label only when zoomed in (or override is on)
    // Selected/connected/focused nodes get labels at a lower zoom threshold for better context
    const minZoomForLabel = isSelected || isPrimaryConnection || isFocused ? 0.8 : 
                           isConnectedToSelected || isHovered || isAIHighlighted || (hasFocus && isConnectedToFocus) ? 1.2 : 
                           2.0;
    const showLabel = forceShowLabels || globalScale > minZoomForLabel || (hasFocus && isConnectedToFocus);
    if (showLabel) {
      const label = n.label || n.id;
      const fontSize = Math.max(10 / globalScale, 3);
      ctx.font = `${fontSize}px Sans-Serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      
      const labelY = n.y - size - 4; // Position above node
      
      // Label text with subtle shadow for readability (no background)
      ctx.shadowColor = isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      // When there is an active selection, dim labels for non-selected nodes too.
      // When focus is active (e.g. filtered-to-device), dim labels for nodes not
      // connected to the focused node so only the focused + direct-connection
      // labels stay fully legible.
      let labelAlpha = 1;
      if (hasFocus) {
        labelAlpha = isFocused ? 1 : isConnectedToFocus ? 0.9 : 0.15;
      } else if (selectionActive) {
        labelAlpha = isSelected ? 1 : isPrimaryConnection ? 0.9 : isConnectedToSelected ? 0.75 : 0.12;
      }
      ctx.globalAlpha = labelAlpha;
      ctx.fillStyle = isDarkMode ? '#e6edf3' : '#333';
      ctx.fillText(label, n.x, labelY);
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
    }
  }, [selectedNodes, connectedToSelection, aiHighlightedNodes, aiHighlightFlash, hoveredNode, nodeSize, colorSchemeMode, colorScale, isDarkMode, trackedEntities, primaryLocationIds, pulsePhase, isSelectedByCoords, selectedCoords, forceShowLabels, nodeDegrees, nodeExpandability, expandedNodes, focusedNodeId, focusedConnections]);

  // Custom link rendering
  const linkCanvasObject = useCallback((link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const l = link as Link & { source: Node & { x: number; y: number }; target: Node & { x: number; y: number } };
    if (!l.source?.x || !l.target?.x) return;

    const color = typeColorMap[l.type || ''] || '#999';
    const weight = l.weight || 1;
    const selectionActive = selectedNodes.size > 0;
    const sourceId = (l.source as any).id;
    const targetId = (l.target as any).id;
    
    // Focus state check
    const hasFocus = focusedNodeId !== null;
    const isConnectedToFocus = sourceId === focusedNodeId || targetId === focusedNodeId;
    
    // Determine if this is a primary connection (touches selected node or primary location)
    const touchesSelected = selectedNodes.has(sourceId) || selectedNodes.has(targetId);
    const touchesPrimaryLocation = primaryLocationIds.has(sourceId) || primaryLocationIds.has(targetId);
    const isPrimaryLink = touchesSelected || touchesPrimaryLocation;
    
    // Links touching tracked entities are secondary (fan-out)
    const touchesTrackedEntity = trackedEntities.has(sourceId) || trackedEntities.has(targetId);
    const isSecondaryLink = !isPrimaryLink && touchesTrackedEntity;
    
    // Only show links that are relevant to selection (but not when focus is active - focus handles its own visibility)
    const linkInFocus = !selectionActive || isPrimaryLink || isSecondaryLink;

    // When a selection is active (and no focus override), hard-hide any links that are not connected to selection/fan-out
    if (!hasFocus && selectionActive && !linkInFocus) return;
    
    // Calculate line width based on weight
    let lineWidth = 1;
    if (weight <= 1) lineWidth = 1;
    else if (weight <= 5) lineWidth = 1.5;
    else if (weight <= 20) lineWidth = 2;
    else if (weight <= 50) lineWidth = 2.5;
    else if (weight <= 100) lineWidth = 3;
    else lineWidth = 3.5;

    // Calculate distance and direction
    const dx = l.target.x - l.source.x;
    const dy = l.target.y - l.source.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (dist < 1) return; // Skip zero-length links

    // Normalize direction
    const nx = dx / dist;
    const ny = dy / dist;

    // Arrow sizing
    const arrowLength = Math.max(6, Math.min(10, 8 / globalScale));
    
    // Calculate offset positions - start from node edge
    const sourceX = l.source.x + nx * (nodeSize + 2);
    const sourceY = l.source.y + ny * (nodeSize + 2);
    
    // End point is at the edge of the target node
    const endX = l.target.x - nx * (nodeSize + 2);
    const endY = l.target.y - ny * (nodeSize + 2);

    // Calculate curve control point (perpendicular offset for curvature)
    const midX = (sourceX + endX) / 2 - ny * curvature * dist * 0.5;
    const midY = (sourceY + endY) / 2 + nx * curvature * dist * 0.5;
    
    // Calculate the tangent direction at the end of the curve (t=1)
    // For quadratic bezier P0->P1->P2, tangent at t=1 is direction from P1 to P2
    const tangentDx = endX - midX;
    const tangentDy = endY - midY;
    const tangentDist = Math.sqrt(tangentDx * tangentDx + tangentDy * tangentDy);
    
    if (tangentDist < 1) return;
    
    const tangentNx = tangentDx / tangentDist;
    const tangentNy = tangentDy / tangentDist;
    
    // Line ends where arrow base starts (arrow tip touches node edge)
    const lineEndX = endX - tangentNx * arrowLength;
    const lineEndY = endY - tangentNy * arrowLength;

    // Draw the curved line (ends at arrow base)
    // Focus takes precedence, then selection
    // Primary links (touching selected nodes/locations) get full opacity
    // Secondary links (fan-out from tracked entities) get reduced opacity
    let effectiveLinkOpacity = linkOpacity;
    if (hasFocus) {
      effectiveLinkOpacity = isConnectedToFocus ? Math.min(linkOpacity * 3, 0.9) : linkOpacity * 0.08;
    } else if (selectionActive) {
      effectiveLinkOpacity = isPrimaryLink ? linkOpacity : isSecondaryLink ? linkOpacity * 0.4 : linkOpacity * 0.15;
    }
    
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.globalAlpha = effectiveLinkOpacity;
    ctx.lineWidth = lineWidth / globalScale;
    ctx.lineCap = 'round';

    ctx.moveTo(sourceX, sourceY);
    // Adjust control point for the shortened curve
    const adjustedMidX = (sourceX + lineEndX) / 2 - ny * curvature * dist * 0.5;
    const adjustedMidY = (sourceY + lineEndY) / 2 + nx * curvature * dist * 0.5;
    ctx.quadraticCurveTo(adjustedMidX, adjustedMidY, lineEndX, lineEndY);
    ctx.stroke();

    // Reset alpha for arrow (keep arrows fully visible)
    ctx.globalAlpha = 1;

    // Arrow tip is at the edge of the target node
    const tipX = endX;
    const tipY = endY;
    
    // Arrow base points (perpendicular to tangent direction)
    const arrowWidth = arrowLength * 0.5;
    const baseX = tipX - tangentNx * arrowLength;
    const baseY = tipY - tangentNy * arrowLength;
    
    // Draw arrow head as a filled triangle
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(baseX - tangentNy * arrowWidth, baseY + tangentNx * arrowWidth);
    ctx.lineTo(baseX + tangentNy * arrowWidth, baseY - tangentNx * arrowWidth);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();

    ctx.globalAlpha = 1;
  }, [linkOpacity, curvature, nodeSize, selectedNodes, connectedToSelection, trackedEntities, primaryLocationIds, focusedNodeId]);

  // Handle node click - only used in selection mode, otherwise just shows info
  // Main interactions (expand, hide, view details) are via right-click menu
  const handleNodeClick = useCallback((node: any, event: MouseEvent) => {
    const n = node as Node;
    
    if (selectionModeActive) {
      // In selection mode: toggle selection for multi-select
      const newSelected = new Set(selectedNodes);
      if (newSelected.has(n.id)) {
        newSelected.delete(n.id);
      } else {
        newSelected.add(n.id);
      }
      setSelectedNodes(newSelected);
      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current(nodes.filter(node => newSelected.has(node.id)));
      }
    } else {
      // Normal mode: toggle focus highlight on click
      // Click same node to unfocus, click different node to focus it
      setFocusedNodeId(prev => prev === n.id ? null : n.id);
      
      // Ctrl+click to add to selection without filtering
      if (event.ctrlKey) {
        const newSelected = new Set(selectedNodes);
        if (newSelected.has(n.id)) {
          newSelected.delete(n.id);
        } else {
          newSelected.add(n.id);
        }
        setSelectedNodes(newSelected);
      }
      // Always trigger callback for node info panel
      if (onNodeClickRef.current) {
        onNodeClickRef.current(n);
      }
    }
  }, [selectionModeActive, selectedNodes, nodes]);

  // Handle node right-click
  const handleNodeRightClick = useCallback((node: any, event: MouseEvent) => {
    event.preventDefault();
    const n = node as Node;
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      node: n
    });
  }, []);

  // Handle node hover
  const handleNodeHover = useCallback((node: any) => {
    setHoveredNode(node as Node | null);
  }, []);

  // Box selection mouse handlers
  const handleBoxSelectionMouseDown = useCallback((event: React.MouseEvent) => {
    if (!selectionModeActive) return;
    
    // Prevent ForceGraph from handling this event
    event.preventDefault();
    event.stopPropagation();
    
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    setIsSelecting(true);
    setSelectionStart({ x, y });
    setSelectionEnd({ x, y });
  }, [selectionModeActive]);

  const handleBoxSelectionMouseMove = useCallback((event: React.MouseEvent) => {
    if (!isSelecting || !selectionStart) return;
    
    event.preventDefault();
    
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    setSelectionEnd({ x, y });
  }, [isSelecting, selectionStart]);

  const handleBoxSelectionMouseUp = useCallback((event: React.MouseEvent) => {
    if (!isSelecting || !selectionStart || !fgRef.current) {
      setIsSelecting(false);
      return;
    }
    
    // Get current position from event (don't rely on selectionEnd state which may be stale)
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const endX = event.clientX - rect.left;
    const endY = event.clientY - rect.top;
    
    // Get the screen to graph coordinate conversion
    const screen2GraphCoords = fgRef.current.screen2GraphCoords;
    
    // Calculate selection box bounds in screen coordinates
    const minX = Math.min(selectionStart.x, endX);
    const maxX = Math.max(selectionStart.x, endX);
    const minY = Math.min(selectionStart.y, endY);
    const maxY = Math.max(selectionStart.y, endY);
    
    // Only process if box is meaningful size (at least 10px)
    if (maxX - minX > 10 && maxY - minY > 10) {
      // Convert corners to graph coordinates
      const corner1 = screen2GraphCoords(minX, minY);
      const corner2 = screen2GraphCoords(maxX, maxY);
      
      // Graph coordinates may have inverted Y-axis, so use min/max
      const graphMinX = Math.min(corner1.x, corner2.x);
      const graphMaxX = Math.max(corner1.x, corner2.x);
      const graphMinY = Math.min(corner1.y, corner2.y);
      const graphMaxY = Math.max(corner1.y, corner2.y);
      
      console.log('[Selection] Screen box:', { minX, maxX, minY, maxY });
      console.log('[Selection] Graph box:', { graphMinX, graphMaxX, graphMinY, graphMaxY });
      
      // Use graphNodesRef which has the nodes WITH D3-computed x/y coordinates
      const graphNodes = graphNodesRef.current;
      console.log('[Selection] Total nodes:', graphNodes.length);
      
      // Log first few nodes to see if they have coordinates
      if (graphNodes.length > 0) {
        console.log('[Selection] Sample nodes:', graphNodes.slice(0, 3).map((n: any) => ({ id: n.id, x: n.x, y: n.y })));
      }
      
      // Find all nodes within the selection box
      const selectedInBox = new Set<string>();
      graphNodes.forEach((node: any) => {
        if (node.x !== undefined && node.y !== undefined) {
          if (node.x >= graphMinX && node.x <= graphMaxX &&
              node.y >= graphMinY && node.y <= graphMaxY) {
            selectedInBox.add(node.id);
            console.log('[Selection] Node in box:', node.id, { x: node.x, y: node.y });
          }
        }
      });
      
      console.log('[Selection] Found', selectedInBox.size, 'nodes in selection');
      
      if (selectedInBox.size > 0) {
        setSelectedNodes(selectedInBox);
        // Get the full node objects with D3 coordinates
        const selectedNodeObjects = graphNodes.filter((n: any) => selectedInBox.has(n.id));
        
        console.log('[Selection] Exporting', selectedNodeObjects.length, 'nodes');
        
        // Auto-export to new tab
        if (onSelectionChangeRef.current) {
          onSelectionChangeRef.current(selectedNodeObjects, true); // true = auto-export
        } else {
          console.log('[Selection] WARNING: onSelectionChangeRef.current is not set!');
        }
        
        // Exit selection mode after successful selection
        setSelectionModeActive(false);
      }
    }
    
    // Reset selection state
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  }, [isSelecting, selectionStart]);

  // Zoom controls
  const handleZoomIn = () => {
    if (fgRef.current) {
      fgRef.current.zoom(fgRef.current.zoom() * 1.3, 300);
    }
  };

  const handleZoomOut = () => {
    if (fgRef.current) {
      fgRef.current.zoom(fgRef.current.zoom() / 1.3, 300);
    }
  };

  const handleZoomReset = () => {
    if (fgRef.current) {
      fgRef.current.zoomToFit(400);
    }
  };

  const handleRestart = () => {
    if (fgRef.current) {
      // Unpin any manually placed nodes so the simulation can fully reorganize.
      // Dragged nodes retain fx/fy via onNodeDragEnd; clearing them here gives the
      // user an explicit "reset my manual placements" affordance via the existing
      // Restart Simulation button.
      const nodes = graphNodesRef.current;
      if (nodes && nodes.length) {
        nodes.forEach((n: any) => {
          n.fx = undefined;
          n.fy = undefined;
        });
      }
      fgRef.current.d3ReheatSimulation();
    }
  };

  // Open PowerPoint theme selection dialog
  const handlePowerPointClick = () => {
    setThemeDialogOpen(true);
  };

  // Export to PowerPoint (called after theme selection)
  const exportToPowerPoint = async (theme: PowerPointTheme) => {
    setThemeDialogOpen(false);
    setIsExporting(true);
    setExportStatus('Capturing network graph...');

    try {
      // Capture graph screenshot
      const graphScreenshot = await captureNetworkGraph('network-graph-container');
      
      // Capture map screenshot
      setExportStatus('Capturing map...');
      const mapScreenshot = await captureLinkAnalysisMap('network-map-container');

      // Calculate stats from filtered nodes and links
      const deviceCount = filteredNodes.filter(n => n.type === 'device').length;
      const locationCount = filteredNodes.filter(n => n.type === 'location').length;
      const phoneCount = filteredNodes.filter(n => n.type === 'phone').length;
      const websiteCount = filteredNodes.filter(n => n.type === 'url' || n.type === 'website').length;
      const appCount = filteredNodes.filter(n => n.type === 'app').length;

      const callCount = filteredLinks.filter((l: Link) => l.type === 'call').length;
      const smsCount = filteredLinks.filter((l: Link) => l.type === 'sms').length;
      const browsingCount = filteredLinks.filter((l: Link) => l.type === 'browsing').length;
      const appUsageCount = filteredLinks.filter((l: Link) => l.type === 'app').length;
      const dwellCount = filteredLinks.filter((l: Link) => l.type === 'dwell').length;

      // Get selected entities (device nodes)
      const selectedEntities = filteredNodes
        .filter(n => n.type === 'device')
        .map(n => n.label || n.id);

      const linkStats: LinkAnalysisStats = {
        totalNodes: filteredNodes.length,
        totalLinks: filteredLinks.length,
        deviceCount,
        locationCount,
        phoneCount,
        websiteCount,
        appCount,
        callCount,
        smsCount,
        browsingCount,
        appUsageCount,
        dwellCount,
        selectedEntities,
      };

      // Generate AI narrative
      setExportStatus('Generating AI insights... (this may take a moment)');
      const exportNodes: LinkAnalysisNode[] = filteredNodes.map(n => ({
        id: n.id,
        type: n.type as LinkAnalysisNode['type'] || 'device',
        label: n.label,
        entity_id: n.entity_type,
        entity_type: n.entity_type,
        place_name: n.place_name,
        app_name: n.app_name,
      }));

      const exportLinks: LinkAnalysisEdge[] = filteredLinks.map((l: Link) => ({
        source: typeof l.source === 'string' ? l.source : l.source?.id || '',
        target: typeof l.target === 'string' ? l.target : l.target?.id || '',
        type: l.type,
        timestamp: l.timestamp,
        duration: l.call_logs_duration,
        weight: l.weight,
        sms_number: l.sms_number,
        sms_message: l.sms_message,
        comms_direction: l.comms_direction,
        call_logs_number: l.call_logs_number,
        call_logs_duration: l.call_logs_duration,
        url: l.url,
        package_name: l.package_name,
        total_time: l.total_time,
      }));

      let aiNarrative: string | undefined;
      try {
        aiNarrative = await generateLinkAnalysisNarrative(
          exportNodes,
          exportLinks,
          linkStats,
          selectedEntities
        );
      } catch (aiError) {
        console.warn('AI narrative generation failed, continuing without it:', aiError);
        setExportStatus('AI generation skipped, building presentation...');
      }

      // Build PowerPoint
      setExportStatus(`Building PowerPoint (${THEME_DEFINITIONS[theme].name})...`);
      await exportLinkAnalysisToPowerPoint({
        title: 'Link Analysis Report',
        subtitle: `Generated ${new Date().toLocaleDateString()} • ${selectedEntities.length} Entities`,
        nodes: exportNodes,
        links: exportLinks,
        stats: linkStats,
        graphScreenshot,
        mapScreenshot,
        aiNarrative,
        theme,
        includeGraph: true,
        includeMap: true,
        includeStats: true,
      });

      setExportStatus('Export complete!');
      setTimeout(() => {
        setIsExporting(false);
        setExportStatus('');
      }, 2000);
    } catch (error) {
      console.error('PowerPoint export failed:', error);
      setExportStatus('Export failed');
      setTimeout(() => {
        setIsExporting(false);
        setExportStatus('');
      }, 3000);
    }
  };

  // Handle simulation freeze
  useEffect(() => {
    if (fgRef.current) {
      if (isSimulationFrozen) {
        fgRef.current.pauseAnimation();
      } else {
        fgRef.current.resumeAnimation();
      }
    }
  }, [isSimulationFrozen]);

  const handleToggleSelectionMode = () => {
    const nextSelectionMode = !selectionModeActive;
    setSelectionModeActive(nextSelectionMode);
    onSelectionModeChange?.(nextSelectionMode);
    if (!nextSelectionMode) {
      setSelectedNodes(new Set());
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      if (onSelectionChange) onSelectionChange([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedNodes(new Set());
    setFocusedNodeId(null);
    setSelectionModeActive(false);
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    onSelectionModeChange?.(false);
    if (onSelectionChange) onSelectionChange([]);
  };

  const handleTableNodeClick = (nodeId: string) => {
    const newSelected = new Set<string>([nodeId]);
    setSelectedNodes(newSelected);

    if (onSelectionChange) {
      onSelectionChange(nodes.filter(n => newSelected.has(n.id)));
    }

    // Center on node
    const node = filteredNodes.find(n => n.id === nodeId);
    if (node && fgRef.current && node.x !== undefined && node.y !== undefined) {
      fgRef.current.centerAt(node.x, node.y, 1000);
      fgRef.current.zoom(4, 1000);
    }
  };

  /**
   * Click handler for rows in the Nodes tab table. In addition to the regular
   * select + zoom behavior, this also:
   *   1. Filters the graph by the clicked node (same as setting focus via
   *      the graph itself) so the analyst sees an immediately scoped view.
   *   2. Opens the View Details dialog populated with the same connection
   *      stats the right-click menu produces.
   *
   * The other tables (SMS, Calls, Browsing, etc.) intentionally still use
   * the lightweight `handleTableNodeClick` because their source/target
   * cells are navigations -- chaining a series of clicks shouldn't drown
   * the analyst in dialogs and graph re-focuses.
   */
  const handleNodeRowClick = useCallback((nodeId: string) => {
    handleTableNodeClick(nodeId);

    const node = filteredNodes.find(n => n.id === nodeId) || nodes.find(n => n.id === nodeId);
    if (!node) return;

    setFocusedNodeId(nodeId);

    if (onViewDetails) {
      const nodeLinks = filteredLinks.filter((l: any) => {
        const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
        const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
        return sourceId === node.id || targetId === node.id;
      });

      const stats = {
        totalConnections: nodeLinks.length,
        callConnections: nodeLinks.filter((l: any) => l.type === 'call').length,
        smsConnections: nodeLinks.filter((l: any) => l.type === 'sms').length,
        browsingConnections: nodeLinks.filter((l: any) => l.type === 'browsing').length,
        appConnections: nodeLinks.filter((l: any) => l.type === 'app').length,
        dwellConnections: nodeLinks.filter((l: any) => l.type === 'dwell').length,
        connectedDevices: Array.from(new Set(nodeLinks.map((l: any) => {
          const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
          const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
          const otherId = sourceId === node.id ? targetId : sourceId;
          const otherNode = filteredNodes.find((n: any) => n.id === otherId);
          return otherNode?.type === 'device' ? otherId : null;
        }).filter(Boolean))) as string[],
        connectedLocations: Array.from(new Set(nodeLinks.map((l: any) => {
          const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
          const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
          const otherId = sourceId === node.id ? targetId : sourceId;
          const otherNode = filteredNodes.find((n: any) => n.id === otherId);
          return otherNode?.type === 'location' ? (otherNode.label || otherId) : null;
        }).filter(Boolean))) as string[],
      };

      onViewDetails(node, stats);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredNodes, nodes, filteredLinks, onViewDetails]);

  // ──────────────────────────────────────────────────────────────────────
  // Commonalities Graph click handlers
  // ──────────────────────────────────────────────────────────────────────
  // When the analyst clicks an entity or term node in the Commonalities
  // bipartite graph, we want BOTH things to happen:
  //   1. The Search-tab list scopes to that entity/term (existing behavior).
  //   2. The MAIN link-analysis graph also reflects the same scope so the
  //      two views stay in sync: clicking Student-36 should both filter the
  //      search list AND focus the network graph on Student-36 and its
  //      neighborhood. Clicking the same node a second time clears both.
  //
  // For entity clicks we use focusedNodeId + selectedNodes (single-entity
  // focus). For term clicks we *select* every entity that searched that
  // term so the network graph highlights the entire cluster of analysts-
  // of-interest who share that search interest.
  const handleCommonalityEntityClick = useCallback(
    (entityId: string) => {
      const willClear = searchEntityFilter === entityId;
      setSearchEntityFilter(willClear ? null : entityId);
      if (willClear) {
        setFocusedNodeId(null);
        setSelectedNodes(new Set());
        if (onSelectionChange) onSelectionChange([]);
      } else {
        // Select + zoom + focus on the clicked entity.
        handleTableNodeClick(entityId);
        setFocusedNodeId(entityId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchEntityFilter, onSelectionChange],
  );

  const handleCommonalityTermClick = useCallback(
    (term: string, sources: Array<{ sourceId: string }>) => {
      const willClear = searchTermFilter.trim().toLowerCase() === term.toLowerCase();
      setSearchTermFilter(willClear ? '' : term);
      if (willClear) {
        setSelectedNodes(new Set());
        setFocusedNodeId(null);
        if (onSelectionChange) onSelectionChange([]);
        return;
      }
      // Multi-select every entity that searched this term so the network
      // graph lights up the entire shared-interest cluster.
      const uniqueIds = new Set<string>();
      for (const src of sources) {
        if (src.sourceId) uniqueIds.add(src.sourceId);
      }
      setSelectedNodes(uniqueIds);
      setFocusedNodeId(null); // clear single-node focus so neighborhood-filter doesn't fight the multiselect
      if (onSelectionChange) {
        onSelectionChange(nodes.filter((n) => uniqueIds.has(n.id)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchTermFilter, nodes, onSelectionChange],
  );

  // Top-keyword click handler: toggle the text filter AND light up every
  // entity in the network graph that searched a term containing this
  // keyword. Mirrors handleCommonalityTermClick but operates on extracted
  // tokens (keywords) instead of full search terms. Uses the SAME substring
  // match the visible-search list uses (s.term.includes(kw)) so the graph
  // selection stays perfectly in sync with what the user sees in the list.
  const handleKeywordClick = useCallback(
    (keyword: string) => {
      const kw = keyword.trim().toLowerCase();
      const isActive = searchTermFilter.trim().toLowerCase() === kw;

      if (isActive) {
        setSearchTermFilter('');
        setSelectedNodes(new Set());
        setFocusedNodeId(null);
        if (onSelectionChange) onSelectionChange([]);
        return;
      }

      setSearchTermFilter(keyword);

      const matchingIds = new Set<string>();
      for (const s of searchExtractions) {
        if (!s.term.toLowerCase().includes(kw)) continue;
        for (const src of s.sources) {
          if (src.sourceId) matchingIds.add(src.sourceId);
        }
      }

      setSelectedNodes(matchingIds);
      setFocusedNodeId(null);
      if (onSelectionChange) {
        onSelectionChange(nodes.filter((n) => matchingIds.has(n.id)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchTermFilter, searchExtractions, nodes, onSelectionChange],
  );

  // ──────────────────────────────────────────────────────────────────────
  // Commonalities Graph renderer
  // ──────────────────────────────────────────────────────────────────────
  // Returns the bipartite (entity ↔ term) visualization Box. Pulled out
  // here so we can render it twice -- compact inline AND expanded in a
  // pop-out dialog -- without copy-pasting ~200 lines of JSX.
  //
  // Captures state (filters, hover, edges, click handlers) via closure so
  // every instance stays in sync with the rest of the Search tab. Caller
  // supplies the visible row caps and row height; everything else (curve
  // math, highlight logic) recomputes for that slice.
  const renderCommonalityGraphBody = (opts: {
    entityLimit: number;
    termLimit: number;
    rowHeight: number;
    /** Override the wrapper height; defaults to fit-to-rows. */
    minHeight?: number;
  }) => {
    const ent = commonalityGraph.entities.slice(0, opts.entityLimit);
    const trm = commonalityGraph.terms.slice(0, opts.termLimit);
    const rowH = opts.rowHeight;
    const padTop = 10;
    const calcHeight = Math.max(ent.length, trm.length) * rowH + padTop * 2;
    const height = opts.minHeight ? Math.max(calcHeight, opts.minHeight) : calcHeight;
    const entityY = (i: number) => padTop + i * rowH + rowH / 2;
    const termY = (i: number) => padTop + i * rowH + rowH / 2;
    const entityIndex = (id: string) => ent.findIndex((e) => e.sourceId === id);
    const termIndex = (term: string) => trm.findIndex((t) => t.term === term);

    // Only include edges between currently visible nodes; otherwise we'd
    // draw curves from y=NaN positions for off-screen nodes.
    const visibleEdges = commonalityGraph.edges.filter(
      (e) => entityIndex(e.entityId) >= 0 && termIndex(e.term) >= 0,
    );
    const visibleMaxCount = Math.max(1, ...visibleEdges.map((e) => e.count));
    const edgeOpacity = (count: number) => {
      const t = count / visibleMaxCount;
      return 0.22 + t * 0.55;
    };
    const edgeWidth = (count: number) => {
      const t = count / visibleMaxCount;
      return 1 + t * 2.5;
    };
    const isHighlighted = (e: (typeof commonalityGraph.edges)[number]) => {
      if (hoveredCommonality) {
        return hoveredCommonality.type === 'entity'
          ? e.entityId === hoveredCommonality.id
          : e.term === hoveredCommonality.id;
      }
      if (searchEntityFilter && e.entityId !== searchEntityFilter) return false;
      if (
        searchTermFilter.trim() &&
        e.term.toLowerCase() !== searchTermFilter.trim().toLowerCase()
      ) {
        return false;
      }
      return true;
    };
    const dimEverything =
      hoveredCommonality !== null ||
      searchEntityFilter !== null ||
      searchTermFilter.trim() !== '';

    return (
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: 'minmax(110px, 1.2fr) minmax(120px, 1.8fr) minmax(140px, 1.4fr)',
          alignItems: 'stretch',
          height,
          bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
        onMouseLeave={() => setHoveredCommonality(null)}
      >
        {/* Entities column */}
        <Box sx={{ position: 'relative', py: `${padTop}px`, pr: 0.5 }}>
          {ent.map((e, i) => {
            const did = e.sourceId.startsWith('phone:')
              ? e.sourceId.replace('phone:', '')
              : e.sourceId;
            const isActiveFilter = searchEntityFilter === e.sourceId;
            const isHovered =
              hoveredCommonality?.type === 'entity' && hoveredCommonality.id === e.sourceId;
            const isConnectedToHover =
              hoveredCommonality?.type === 'term'
                ? visibleEdges.some(
                    (ed) => ed.entityId === e.sourceId && ed.term === hoveredCommonality.id,
                  )
                : false;
            const dimmed =
              dimEverything && !isActiveFilter && !isHovered && !isConnectedToHover;
            return (
              <Box
                key={e.sourceId}
                onMouseEnter={() => setHoveredCommonality({ type: 'entity', id: e.sourceId })}
                onClick={() => handleCommonalityEntityClick(e.sourceId)}
                sx={{
                  position: 'absolute',
                  right: 4,
                  top: entityY(i) - rowH / 2,
                  height: rowH,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                  px: 0.75,
                  borderRadius: 0.75,
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  fontSize: rowH >= 28 ? '0.8rem' : '0.72rem',
                  fontWeight: 600,
                  color: isActiveFilter ? 'primary.contrastText' : 'text.primary',
                  bgcolor: isActiveFilter
                    ? 'primary.main'
                    : isHovered || isConnectedToHover
                    ? isDarkMode
                      ? 'rgba(100,181,246,0.18)'
                      : 'rgba(25,118,210,0.12)'
                    : 'transparent',
                  opacity: dimmed ? 0.3 : 1,
                  transition: 'opacity 0.15s, background-color 0.15s',
                  '&:hover': {
                    bgcolor: isActiveFilter
                      ? 'primary.dark'
                      : isDarkMode
                      ? 'rgba(100,181,246,0.22)'
                      : 'rgba(25,118,210,0.15)',
                  },
                }}
              >
                <span
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {did}
                </span>
                <Box
                  component="span"
                  sx={{ fontSize: rowH >= 28 ? '0.7rem' : '0.65rem', opacity: 0.7, fontFamily: 'monospace' }}
                >
                  ×{e.count}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Connection curves */}
        <Box sx={{ position: 'relative' }}>
          <svg
            viewBox={`0 0 100 ${height}`}
            preserveAspectRatio="none"
            width="100%"
            height={height}
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
          >
            {visibleEdges.map((edge, i) => {
              const ey = entityY(entityIndex(edge.entityId));
              const ty = termY(termIndex(edge.term));
              const highlighted = isHighlighted(edge);
              const op = dimEverything && !highlighted ? 0.06 : edgeOpacity(edge.count);
              return (
                <path
                  key={`${edge.entityId}-${edge.term}-${i}`}
                  d={`M 0 ${ey} C 50 ${ey} 50 ${ty} 100 ${ty}`}
                  stroke={edge.engineColor}
                  strokeWidth={edgeWidth(edge.count)}
                  strokeOpacity={op}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                  style={{ transition: 'stroke-opacity 0.15s' }}
                />
              );
            })}
          </svg>
        </Box>

        {/* Terms column */}
        <Box sx={{ position: 'relative', py: `${padTop}px`, pl: 0.5 }}>
          {trm.map((t, i) => {
            const isActiveFilter =
              searchTermFilter.trim().toLowerCase() === t.term.toLowerCase();
            const isHovered =
              hoveredCommonality?.type === 'term' && hoveredCommonality.id === t.term;
            const isConnectedToHover =
              hoveredCommonality?.type === 'entity'
                ? visibleEdges.some(
                    (ed) => ed.term === t.term && ed.entityId === hoveredCommonality.id,
                  )
                : false;
            const dimmed =
              dimEverything && !isActiveFilter && !isHovered && !isConnectedToHover;
            return (
              <Box
                key={t.term}
                onMouseEnter={() => setHoveredCommonality({ type: 'term', id: t.term })}
                onClick={() => handleCommonalityTermClick(t.term, t.sources)}
                sx={{
                  position: 'absolute',
                  left: 4,
                  top: termY(i) - rowH / 2,
                  height: rowH,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.75,
                  borderRadius: 0.75,
                  cursor: 'pointer',
                  fontSize: rowH >= 28 ? '0.82rem' : '0.74rem',
                  fontWeight: 600,
                  maxWidth: 'calc(100% - 8px)',
                  color: isActiveFilter ? 'primary.contrastText' : 'text.primary',
                  bgcolor: isActiveFilter
                    ? 'primary.main'
                    : isHovered || isConnectedToHover
                    ? `${t.engineColor}22`
                    : 'transparent',
                  opacity: dimmed ? 0.3 : 1,
                  transition: 'opacity 0.15s, background-color 0.15s',
                  '&:hover': { bgcolor: isActiveFilter ? 'primary.dark' : `${t.engineColor}33` },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: t.engineColor,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {t.term}
                </span>
                <Box
                  component="span"
                  sx={{
                    ml: 'auto',
                    fontSize: rowH >= 28 ? '0.7rem' : '0.65rem',
                    opacity: 0.7,
                    fontFamily: 'monospace',
                  }}
                >
                  ×{t.count}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Helper to extract coordinates from node ID/label
  const extractCoordsFromNode = useCallback((node: Node): { lat: number; lng: number } | null => {
    let lat: number | null = null;
    let lng: number | null = null;
    
    // Pattern 1: "Location (lat, lng)" format
    const locationMatch = node.id.match(/Location\s*\(\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*\)/i);
    if (locationMatch) {
      lat = parseFloat(locationMatch[1]);
      lng = parseFloat(locationMatch[2]);
    }
    
    // Pattern 2: "coord_lat_lng" format (flow map style)
    if (lat === null) {
      const coordMatch = node.id.match(/coord_(-?\d+\.?\d*)_(-?\d+\.?\d*)/);
      if (coordMatch) {
        lat = parseFloat(coordMatch[1]);
        lng = parseFloat(coordMatch[2]);
      }
    }
    
    // Pattern 3: Try extracting from label
    if (lat === null && node.label) {
      const labelMatch = node.label.match(/\(\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*\)/);
      if (labelMatch) {
        lat = parseFloat(labelMatch[1]);
        lng = parseFloat(labelMatch[2]);
      }
    }
    
    // Pattern 4: Check if node has direct lat/lng properties
    if (lat === null) {
      const anyNode = node as any;
      if (typeof anyNode.latitude === 'number' && typeof anyNode.longitude === 'number') {
        lat = anyNode.latitude;
        lng = anyNode.longitude;
      }
    }
    
    if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
    return null;
  }, []);

  // Zoom to location(s) on map - handles single location or entity with multiple locations
  const handleZoomToLocation = useCallback((targetNode: Node) => {
    const node = filteredNodes.find(n => n.id === targetNode.id);
    
    // Also center on the node in the force graph
    if (node && fgRef.current && node.x !== undefined && node.y !== undefined) {
      fgRef.current.centerAt(node.x, node.y, 800);
      fgRef.current.zoom(5, 800);
    }
    
    // Check if this is a location node
    const isLocationNode = targetNode.type === 'location' || 
                           targetNode.id.includes('Location') || 
                           targetNode.id.includes('coord_');
    
    if (isLocationNode) {
      // Single location - zoom directly to it
      const coords = extractCoordsFromNode(targetNode);
      if (coords) {
        setActiveTab(0);
        setTimeout(() => {
          setMapZoomLocation({ lat: coords.lat, lng: coords.lng, zoom: 15 });
        }, 100);
      }
    } else {
      // Entity node - find all connected locations and zoom to fit them
      const connectedLocations: { lat: number; lng: number }[] = [];
      
      // Find all links connected to this node
      filteredLinks.forEach((link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        
        // Check if this link connects to our target node
        if (sourceId === targetNode.id || targetId === targetNode.id) {
          const otherNodeId = sourceId === targetNode.id ? targetId : sourceId;
          const otherNode = filteredNodes.find(n => n.id === otherNodeId);
          
          if (otherNode) {
            // Check if the other node is a location
            const isOtherLocation = otherNode.type === 'location' || 
                                    otherNode.id.includes('Location') || 
                                    otherNode.id.includes('coord_');
            
            if (isOtherLocation) {
              const coords = extractCoordsFromNode(otherNode);
              if (coords) {
                connectedLocations.push(coords);
              }
            }
          }
          
          // Also check if link itself has coordinates (e.g., dwell links)
          if (link.latitude !== undefined && link.longitude !== undefined) {
            connectedLocations.push({ lat: link.latitude, lng: link.longitude });
          }
        }
      });
      
      if (connectedLocations.length > 0) {
        setActiveTab(0);
        
        if (connectedLocations.length === 1) {
          // Single location - zoom to it
          setTimeout(() => {
            setMapZoomLocation({ lat: connectedLocations[0].lat, lng: connectedLocations[0].lng, zoom: 15 });
          }, 100);
        } else {
          // Multiple locations - zoom to center with appropriate zoom level
          const lats = connectedLocations.map(c => c.lat);
          const lngs = connectedLocations.map(c => c.lng);
          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
          
          // Calculate zoom level based on spread
          const latSpread = Math.max(...lats) - Math.min(...lats);
          const lngSpread = Math.max(...lngs) - Math.min(...lngs);
          const maxSpread = Math.max(latSpread, lngSpread);
          
          // Approximate zoom: larger spread = lower zoom
          let zoom = 12;
          if (maxSpread > 1) zoom = 8;
          else if (maxSpread > 0.5) zoom = 9;
          else if (maxSpread > 0.2) zoom = 10;
          else if (maxSpread > 0.1) zoom = 11;
          else if (maxSpread > 0.05) zoom = 12;
          else zoom = 13;
          
          setTimeout(() => {
            setMapZoomLocation({ lat: centerLat, lng: centerLng, zoom });
          }, 100);
        }
      }
    }
    
    handleCloseContextMenu();
  }, [filteredNodes, filteredLinks, extractCoordsFromNode]);

  // Handle exporting a subgraph
  const handleExportSubgraph = (centerNode: Node) => {
    const centerNodeId = centerNode.id;
    
    const getLinkNodeId = (nodeRef: any): string => {
      if (typeof nodeRef === 'string') return nodeRef;
      if (nodeRef && typeof nodeRef === 'object') {
        return nodeRef.id || nodeRef.index?.toString() || '';
      }
      return '';
    };
    
    const connectedNodeIds = new Set<string>([centerNodeId]);
    
    links.forEach((link: any) => {
      const sourceId = getLinkNodeId(link.source);
      const targetId = getLinkNodeId(link.target);
      
      if (sourceId === centerNodeId) connectedNodeIds.add(targetId);
      if (targetId === centerNodeId) connectedNodeIds.add(sourceId);
    });
    
    const connectedLinks: Link[] = [];
    links.forEach((link: any) => {
      const sourceId = getLinkNodeId(link.source);
      const targetId = getLinkNodeId(link.target);
      
      if (connectedNodeIds.has(sourceId) && connectedNodeIds.has(targetId)) {
        connectedLinks.push({
          ...link,
          source: sourceId,
          target: targetId
        });
      }
    });
    
    const connectedNodes = nodes
      .filter(n => connectedNodeIds.has(n.id))
      .map(n => ({ ...n }));
    
    setContextMenu(null);
    
    if (onExportSubgraph) {
      onExportSubgraph(connectedNodes, connectedLinks, centerNode);
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // CSV export helper
  const exportToCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportNodes = () => {
    const data = nodes.map((node) => {
      const degree = links.filter((link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        return sourceId === node.id || targetId === node.id;
      }).length;

      return {
        'Node ID': node.id,
        'Type': node.type || '-',
        'Group': node.group || '-',
        'Connections': degree
      };
    });
    exportToCSV(data, 'network-graph-nodes.csv');
  };

  const exportSMS = () => {
    const formatNodeId = (id: string) => {
      if (id && id.startsWith && id.startsWith('phone:')) {
        return id.replace('phone:', '');
      }
      return id;
    };

    const data = links.filter(link => link.type === 'sms').map((link) => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;

      return {
        'Source': formatNodeId(sourceId),
        'Target': formatNodeId(targetId),
        'SMS Number': link.sms_number || '-',
        'Direction': link.comms_direction || '-',
        'Message': link.sms_message || '-',
        'Timestamp': parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-',
        'Weight': link.weight || 1
      };
    });
    exportToCSV(data, 'network-graph-sms.csv');
  };

  const exportCalls = () => {
    const formatNodeId = (id: string) => {
      if (id && id.startsWith && id.startsWith('phone:')) {
        return id.replace('phone:', '');
      }
      return id;
    };

    const data = links.filter(link => link.type === 'call').map((link) => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;

      return {
        'Source': formatNodeId(sourceId),
        'Target': formatNodeId(targetId),
        'Call Number': link.call_logs_number || '-',
        'Type': link.comms_logs_type || '-',
        'Duration': link.call_logs_duration ? `${link.call_logs_duration}s` : '-',
        'Timestamp': parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-',
        'Weight': link.weight || 1
      };
    });
    exportToCSV(data, 'network-graph-calls.csv');
  };

  const exportDwells = () => {
    const data = links.filter(link => link.type === 'dwell').map((link) => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;

      return {
        'Source': sourceId,
        'Target': targetId,
        'Location': targetId,
        'Timestamp': parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-',
        'Weight': link.weight || 1
      };
    });
    exportToCSV(data, 'network-graph-dwells.csv');
  };

  const exportApps = () => {
    const data = links.filter(link => link.type === 'app').map((link) => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
      const appNode = nodes.find(n => n.id === targetId);

      return {
        'Entity': sourceId,
        'App': appNode?.app_name || appNode?.label || targetId,
        'Package': link.package_name || targetId?.replace('app:', '') || '-',
        'Use Count': link.weight || 1,
        'Total Time (sec)': link.total_time || 0,
        'Timestamp': parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-',
      };
    });
    exportToCSV(data, 'network-graph-apps.csv');
  };

  const exportBrowsing = () => {
    const data = links.filter(link => link.type === 'browsing').map((link) => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
      const domainNode = nodes.find(n => n.id === targetId);

      return {
        'Entity': sourceId,
        'Domain': domainNode?.domain || domainNode?.label || targetId?.replace('domain:', '') || '-',
        'URL': (link as any).url || '-',
        'Visits': link.weight || 1,
        'Last Visited': parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-',
      };
    });
    exportToCSV(data, 'network-graph-browsing.csv');
  };

  // CSV export for the Search tab. One row per (term, occurrence) so the
  // resulting CSV can be filtered/pivoted in Excel like the other exports.
  const exportSearches = () => {
    const data: Array<Record<string, string | number>> = [];
    for (const s of visibleSearches) {
      if (s.sources.length === 0) {
        data.push({
          'Engine': s.engine,
          'Search Term': s.term,
          'Entity': '-',
          'Domain': '-',
          'Search URL': s.url || '-',
          'Total Count': s.count,
          'Last Searched': s.lastVisit ? new Date(s.lastVisit).toLocaleString() : '-',
        });
        continue;
      }
      for (const src of s.sources) {
        data.push({
          'Engine': s.engine,
          'Search Term': s.term,
          'Entity': src.sourceId,
          'Domain': src.domain || '-',
          'Search URL': src.url || s.url || '-',
          'Total Count': s.count,
          'Last Searched': src.timestamp ? new Date(src.timestamp).toLocaleString() : '-',
        });
      }
    }
    exportToCSV(data, 'network-graph-searches.csv');
  };

  // Graph data with proper structure
  const graphData = useMemo(() => {
    // Create node objects that D3 will mutate to add x/y coordinates
    const nodeObjects = filteredNodes.map(n => ({ ...n }));
    // Store reference so selection handler can access nodes with coordinates
    graphNodesRef.current = nodeObjects;
    return {
      nodes: nodeObjects,
      links: filteredLinks.map(l => ({
        ...l,
        source: typeof l.source === 'string' ? l.source : (l.source as Node).id,
        target: typeof l.target === 'string' ? l.target : (l.target as Node).id,
      }))
    };
  }, [filteredNodes, filteredLinks]);

  // Configure d3 forces - dynamically adjust spacing based on node count
  useEffect(() => {
    if (fgRef.current) {
      const nodeCount = filteredNodes.length;
      
      // Scale up spacing when there are fewer nodes
      // More space for small graphs, normal spacing for large graphs
      const spacingMultiplier = nodeCount < 10 ? 3 : 
                                nodeCount < 20 ? 2.5 :
                                nodeCount < 50 ? 2 :
                                nodeCount < 100 ? 1.5 : 1;
      
      const dynamicLinkDistance = linkDistance * spacingMultiplier;
      const dynamicChargeStrength = chargeStrength * spacingMultiplier * 1.5;
      
      fgRef.current.d3Force('charge')?.strength(dynamicChargeStrength);
      fgRef.current.d3Force('link')?.distance(dynamicLinkDistance);
      fgRef.current.d3ReheatSimulation();
    }
  }, [chargeStrength, linkDistance, filteredNodes.length]);

  // Ctrl/Cmd+K opens the jump-to-panel palette while the graph is mounted.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'k') return;
      e.preventDefault();
      setToolPaletteOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Side-panel tool rail (Pathfinder style): grouped icon strip that replaces
  // the horizontal tab row. `index` must match the activeTab panel bodies.
  // Counts live in the tooltip labels since the rail only shows icons.
  const sideRailGroups: {
    label: string;
    tools: { index: number; label: string; icon: React.ReactNode }[];
  }[] = [
    {
      label: 'View',
      tools: [
        { index: 0, label: 'Map', icon: <MapIcon /> },
        { index: 1, label: 'Charts', icon: <BarChartIcon /> },
        { index: 2, label: 'Timeline', icon: <TimelineIcon /> },
      ],
    },
    {
      label: 'Data',
      tools: [
        { index: 3, label: `Nodes (${stats.nodes})`, icon: <NodesTabIcon /> },
        { index: 4, label: `SMS (${filteredLinks.filter(l => l.type === 'sms').length})`, icon: <SmsTabIcon /> },
        { index: 5, label: `Calls (${filteredLinks.filter(l => l.type === 'call').length})`, icon: <PhoneIcon /> },
        { index: 6, label: `Dwells (${filteredLinks.filter(l => l.type === 'dwell').length})`, icon: <LocationOnIcon /> },
        { index: 7, label: `Apps (${filteredLinks.filter(l => l.type === 'app').length})`, icon: <AppsTabIcon /> },
        { index: 8, label: `Browsing (${filteredLinks.filter(l => l.type === 'browsing').length})`, icon: <BrowsingTabIcon /> },
        { index: 9, label: `Search (${searchExtractions.length})`, icon: <SearchTermIcon /> },
      ],
    },
    {
      label: 'Info',
      tools: [
        { index: 10, label: 'Controls', icon: <ControlsTabIcon /> },
        { index: 11, label: 'Help', icon: <HelpTabIcon /> },
        { index: 12, label: 'Legend', icon: <LegendTabIcon /> },
      ],
    },
  ];

  // Flattened rail order, used by the panel header and the jump-to-panel
  // palette so both read the same as the icon rail.
  const sideRailTools: LinkAnalysisToolItem[] = sideRailGroups.flatMap((group) =>
    group.tools.map((tool) => ({ ...tool, group: group.label })),
  );
  const activeRailTool = sideRailTools.find((tool) => tool.index === activeTab) ?? null;

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        cursor: isResizing ? 'col-resize' : 'auto',
        userSelect: isResizing ? 'none' : 'auto',
      }}
    >
      {/* Main Graph Area */}
      <Box sx={{ 
        flex: 1, 
        minWidth: 0, // Allow shrinking below content size
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        transition: isResizing ? 'none' : 'all 0.3s',
        overflow: 'hidden',
      }}>

        {/* Graph */}
        <Paper elevation={2} sx={{ 
          flex: 1,
          overflow: 'hidden', 
          bgcolor: isDarkMode ? '#1a1a2e' : '#fafafa',
          display: 'flex',
          width: '100%',
          height: '100%'
        }} ref={graphAreaRef}>
          {nodes.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No network data available. Adjust filters or date range.
              </Typography>
            </Box>
          ) : (
            <Box 
              id="network-graph-container"
              sx={{ 
              width: '100%', 
              height: '100%', 
              border: selectionModeActive 
                ? '2px solid #ff9800' 
                : `1px solid ${isDarkMode ? '#333' : '#e0e0e0'}`,
              cursor: selectionModeActive ? 'crosshair' : 'grab',
              backgroundColor: isDarkMode ? '#1a1a2e' : '#fafafa',
                position: 'relative',
                userSelect: 'none'
              }}
            >
              {/* Selection Mode Interaction Overlay - captures mouse events when in selection mode */}
              {selectionModeActive && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999,
                    cursor: 'crosshair',
                  }}
                  onMouseDown={handleBoxSelectionMouseDown}
                  onMouseMove={handleBoxSelectionMouseMove}
                  onMouseUp={handleBoxSelectionMouseUp}
                  onMouseLeave={() => {
                    if (isSelecting) {
                      setIsSelecting(false);
                      setSelectionStart(null);
                      setSelectionEnd(null);
                    }
                  }}
                />
              )}
              {/* Selection Box Visual */}
              {isSelecting && selectionStart && selectionEnd && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: Math.min(selectionStart.x, selectionEnd.x),
                    top: Math.min(selectionStart.y, selectionEnd.y),
                    width: Math.abs(selectionEnd.x - selectionStart.x),
                    height: Math.abs(selectionEnd.y - selectionStart.y),
                    border: '2px dashed #ff9800',
                    backgroundColor: 'rgba(255, 152, 0, 0.15)',
                    pointerEvents: 'none',
                    zIndex: 1001,
                  }}
                />
              )}
              <ForceGraph2D
                ref={fgRef}
                graphData={graphData as any}
                width={dimensions.width}
                height={dimensions.height}
                backgroundColor={isDarkMode ? '#1a1a2e' : '#fafafa'}
                nodeCanvasObject={nodeCanvasObject}
                nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
                  ctx.fillStyle = color;
                  ctx.beginPath();
                  ctx.arc(node.x, node.y, nodeSize * 2, 0, 2 * Math.PI);
                  ctx.fill();
                }}
                linkCanvasObject={linkCanvasObject}
                linkDirectionalArrowLength={0} // We draw arrows manually
                onNodeClick={handleNodeClick}
                onNodeRightClick={handleNodeRightClick}
                onNodeHover={handleNodeHover}
                onNodeDragEnd={(node: any) => {
                  // Pin the node at the drop position so it no longer snaps back.
                  // The rest of the graph continues to rebalance around it via a
                  // gentle reheat. To release all manual placements, use the
                  // "Restart Simulation" button (clears fx/fy on every node).
                  if (node) {
                    node.fx = node.x;
                    node.fy = node.y;
                  }
                  if (fgRef.current && !isSimulationFrozen) {
                    // Small alpha nudge so neighbors reorganize without a jarring reset.
                    fgRef.current.d3ReheatSimulation();
                  }
                }}
                enableNodeDrag={!selectionModeActive}
                enableZoomInteraction={!selectionModeActive}
                enablePanInteraction={!selectionModeActive}
                cooldownTicks={isSimulationFrozen ? 0 : 100}
                d3AlphaDecay={0.02}
                d3VelocityDecay={0.4}
                warmupTicks={50}
              />
              
              {/* Performance indicator */}
              <Box sx={{ 
                position: 'absolute', 
                top: 8, 
                left: 8, 
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }}>
                <Chip 
                  size="small" 
                  label={`${stats.nodes} nodes, ${stats.links} links`}
                  sx={{ 
                    bgcolor: isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
                    fontSize: '0.7rem'
                  }}
                />
              </Box>

              {/* Focused Node Info Panel */}
              {focusedNodeId && (() => {
                const focusedNode = filteredNodes.find(n => n.id === focusedNodeId);
                const connectionCount = focusedConnections.connectedNodeIds.size - 1;
                if (!focusedNode) return null;
                
                return (
                  <Box sx={{
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 20,
                    bgcolor: isDarkMode ? 'rgba(0, 50, 70, 0.95)' : 'rgba(230, 250, 255, 0.95)',
                    border: '2px solid #00ffff',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
                  }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#00ffff' }}>
                        🔍 {focusedNode.label || focusedNode.id}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
                        {connectionCount} connection{connectionCount !== 1 ? 's' : ''} • {focusedNode.type || 'node'}
                      </Typography>
                    </Box>
                    <Tooltip title="Clear focus (Esc)">
                      <IconButton 
                        size="small" 
                        onClick={() => setFocusedNodeId(null)}
                        sx={{ 
                          color: '#00ffff',
                          '&:hover': { bgcolor: 'rgba(0, 255, 255, 0.2)' }
                        }}
                      >
                        <CenterFocusStrongIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })()}

              {/* Selection Active Indicator - shows when nodes are selected */}
              {selectedNodes.size > 0 && !focusedNodeId && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 20,
                  bgcolor: isDarkMode ? 'rgba(50, 30, 0, 0.95)' : 'rgba(255, 248, 230, 0.95)',
                  border: '2px solid #ff9800',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  boxShadow: '0 0 20px rgba(255, 152, 0, 0.4)',
                  animation: 'selectionPulse 3s ease-in-out infinite',
                  '@keyframes selectionPulse': {
                    '0%, 100%': {
                      boxShadow: '0 0 10px rgba(255, 152, 0, 0.3)',
                    },
                    '50%': {
                      boxShadow: '0 0 25px rgba(255, 152, 0, 0.6)',
                    },
                  },
                }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ff9800' }}>
                      ✓ {selectedNodes.size} node{selectedNodes.size !== 1 ? 's' : ''} selected
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
                      Clear selection when done
                    </Typography>
                  </Box>
                  <Tooltip title="Clear Selection">
                    <IconButton 
                      size="small" 
                      onClick={handleClearSelection}
                      sx={{ 
                        color: '#ff9800',
                        bgcolor: 'rgba(255, 152, 0, 0.15)',
                        '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.3)' }
                      }}
                    >
                      <DeselectIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {/* Zoom Controls */}
              <Box sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8, 
                zIndex: 1100,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                bgcolor: isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
                borderRadius: 1,
                p: 0.5
              }}
              onMouseDown={(event) => event.stopPropagation()}
              onMouseUp={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              >
                <Tooltip title={graphToolsOpen ? 'Hide graph tools' : 'Show graph tools'} placement="left">
                  <IconButton
                    size="small"
                    aria-label={graphToolsOpen ? 'Hide graph tools' : 'Show graph tools'}
                    aria-expanded={graphToolsOpen}
                    onClick={() => setGraphToolsOpen(!graphToolsOpen)}
                  >
                    {graphToolsOpen ? <CollapseGraphToolsIcon fontSize="small" /> : <GraphToolsIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                {graphToolsOpen && (
                  <>
                <Divider sx={{ my: 0.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }} />
                <Tooltip title="Zoom In" placement="left">
                  <IconButton size="small" onClick={handleZoomIn}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Zoom Out" placement="left">
                  <IconButton size="small" onClick={handleZoomOut}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Fit to View" placement="left">
                  <IconButton size="small" onClick={handleZoomReset}>
                    <CenterFocusStrongIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Restart Simulation" placement="left">
                  <IconButton size="small" onClick={handleRestart}>
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isSimulationFrozen ? "Unfreeze" : "Freeze"} placement="left">
                  <IconButton 
                    size="small" 
                    onClick={() => setIsSimulationFrozen(!isSimulationFrozen)}
                    color={isSimulationFrozen ? "warning" : "default"}
                  >
                    {isSimulationFrozen ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={forceShowLabels ? "Auto Labels (Zoom)" : "Force Show All Labels"} placement="left">
                  <IconButton 
                    size="small" 
                    onClick={() => setForceShowLabels(!forceShowLabels)}
                    color={forceShowLabels ? "primary" : "default"}
                  >
                    {forceShowLabels ? <LabelIcon fontSize="small" /> : <LabelOffIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={hideNonSharedNodes ? "Show All Nodes" : "Hide Single-Entity Nodes"} placement="left">
                  <IconButton 
                    size="small" 
                    onClick={() => setHideNonSharedNodes(!hideNonSharedNodes)}
                    color={hideNonSharedNodes ? "secondary" : "default"}
                  >
                    <FilterAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={selectionModeActive ? "Exit Selection Mode" : "Select Mode (Box Select)"} placement="left">
                  <IconButton 
                    size="small" 
                    onClick={handleToggleSelectionMode}
                    color={selectionModeActive ? "warning" : "default"}
                  >
                    {selectionModeActive ? <DeselectIcon fontSize="small" /> : <SelectAllIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={selectedNodes.size > 0 ? `Clear Selection (${selectedNodes.size} selected)` : selectionModeActive ? "Exit Selection Mode" : "Clear Selection"} placement="left">
                  <span>
                    <IconButton 
                      size="small" 
                      onClick={handleClearSelection}
                      disabled={selectedNodes.size === 0 && !selectionModeActive}
                      sx={(selectedNodes.size > 0 || selectionModeActive) ? {
                        color: '#ff9800',
                        backgroundColor: 'rgba(255, 152, 0, 0.15)',
                        animation: 'pulseGlow 2s ease-in-out infinite',
                        '@keyframes pulseGlow': {
                          '0%, 100%': {
                            boxShadow: '0 0 4px rgba(255, 152, 0, 0.4)',
                          },
                          '50%': {
                            boxShadow: '0 0 12px rgba(255, 152, 0, 0.8)',
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 152, 0, 0.3)',
                        },
                      } : undefined}
                    >
                      <DeselectIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Divider sx={{ my: 0.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }} />
                {onExpandAll && (
                  <Tooltip title="Expand All Nodes" placement="left">
                    <IconButton 
                      size="small" 
                      onClick={onExpandAll}
                    >
                      <UnfoldMoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {onCollapseAll && (
                  <Tooltip title="Collapse All Nodes" placement="left">
                    <IconButton 
                      size="small" 
                      onClick={onCollapseAll}
                    >
                      <UnfoldLessIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {onExportEntityReport && (() => {
                  const deviceNodes = filteredNodes.filter(n => n.type === 'device');

                  const selectedDeviceNodes = Array.from(selectedNodes)
                    .map(id => filteredNodes.find(n => n.id === id))
                    .filter((n): n is Node => !!n && n.type === 'device');

                  const getExportNodes = (): Node[] => {
                    if (selectedDeviceNodes.length > 0) {
                      return selectedDeviceNodes;
                    }
                    return deviceNodes;
                  };

                  const exportNodes = getExportNodes();
                  const canExport = exportNodes.length > 0;

                  const tooltipText = !canExport
                    ? 'No entity to export'
                    : exportNodes.length === 1
                      ? `Export ${exportNodes[0].label || exportNodes[0].id} Report (PDF)`
                      : `Export ${exportNodes.length} Entity Reports (PDF)`;

                  return (
                    <Tooltip title={tooltipText} placement="left">
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={async () => {
                            const nodesToExport = getExportNodes();
                            for (let i = 0; i < nodesToExport.length; i++) {
                              const node = nodesToExport[i];
                              await onExportEntityReport(node.id, node.label || node.id);
                              if (i < nodesToExport.length - 1) {
                                await new Promise(resolve => setTimeout(resolve, 800));
                              }
                            }
                          }}
                          disabled={!canExport}
                          color={canExport ? 'primary' : 'default'}
                        >
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                })()}
                <Tooltip title={isExporting ? exportStatus : "Export PowerPoint Report"} placement="left">
                  <span>
                    <IconButton 
                      size="small" 
                      onClick={handlePowerPointClick}
                      disabled={isExporting}
                      color="primary"
                    >
                      {isExporting ? <CircularProgress size={18} color="inherit" /> : <PowerPointIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                  </>
                )}
              </Box>

              {/* Tooltip for hovered node */}
              {hoveredNode && (
                <Box sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.95)',
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${isDarkMode ? '#333' : '#ccc'}`,
                  maxWidth: 300,
                  boxShadow: 2
                }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {hoveredNode.label || hoveredNode.id}
                  </Typography>
                  {hoveredNode.type && (
                    <Chip 
                      label={hoveredNode.type} 
                      size="small" 
                      sx={{ 
                        mt: 0.5,
                        bgcolor: typeColorMap[hoveredNode.type] || '#666',
                        color: '#fff',
                        height: 20,
                        fontSize: '0.7rem'
                      }}
                    />
                  )}
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Connections: {nodeDegrees.get(hoveredNode.id) || 0}
                  </Typography>
                  {hoveredNode.place_name && (
                    <Typography variant="caption" display="block">
                      {hoveredNode.place_name}
                    </Typography>
                  )}
                  {!focusedNodeId && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#00bcd4', fontStyle: 'italic' }}>
                      Click to highlight connections
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Resize Handle */}
      {sidePanelOpen && (
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            width: 6,
            cursor: 'col-resize',
            backgroundColor: isResizing ? 'primary.main' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: 'primary.main',
            },
            flexShrink: 0,
            zIndex: 10,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 4,
              height: 40,
              borderRadius: 2,
              backgroundColor: isResizing ? 'primary.contrastText' : 'action.disabled',
              opacity: 0.7,
              transition: 'opacity 0.15s ease',
            },
            '&:hover::after': {
              opacity: 1,
              backgroundColor: 'primary.contrastText',
            },
          }}
        />
      )}

      {/* Right Side Panel */}
      <Paper
        elevation={3}
        sx={{
          width: sidePanelOpen ? sidePanelWidth : 48,
          minWidth: sidePanelOpen ? 350 : 48,
          maxWidth: sidePanelOpen ? 1200 : 48,
          flexShrink: 0,
          flexGrow: 0,
          height: '100%',
          transition: isResizing ? 'none' : 'width 0.3s ease, min-width 0.3s ease',
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
          borderLeft: 1,
          borderColor: 'divider',
          boxSizing: 'border-box',
          userSelect: isResizing ? 'none' : 'auto',
        }}
      >
        {/* Panel Content (tab selection moved to the tool rail on the right edge) */}
        {sidePanelOpen && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {/* Panel header: names the active tool now that the tab labels
                live in the icon rail. Matches the Pathfinder panel header. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.75,
                minHeight: 44,
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'action.hover'),
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                  {activeRailTool?.icon}
                </Box>
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, letterSpacing: '0.01em' }}>
                  {activeRailTool?.label ?? 'Panels'}
                </Typography>
              </Box>
              <Tooltip title="Search panels (Ctrl/Cmd+K)">
                <IconButton
                  size="small"
                  aria-label="Search panels"
                  onClick={() => setToolPaletteOpen(true)}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Search and Export for data tabs */}
            {activeTab >= 3 && activeTab <= 8 && (
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1, bgcolor: 'background.default' }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    if (activeTab === 3) exportNodes();
                    else if (activeTab === 4) exportSMS();
                    else if (activeTab === 5) exportCalls();
                    else if (activeTab === 6) exportDwells();
                    else if (activeTab === 7) exportApps();
                    else if (activeTab === 8) exportBrowsing();
                  }}
                >
                  Export
                </Button>
              </Box>
            )}

            {/* Search tab's Export action has been inlined into the
                result-counter row inside the tab body to reclaim header
                vertical space. See activeTab === 9 render below. */}

            {/* Map Tab - Always mounted to preserve state, hidden when not active */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'hidden', 
              display: activeTab === 0 ? 'flex' : 'none',  // Keep mounted but hide when not active
              flexDirection: 'column' 
            }}>
                {/* Info Panel - shows contextual info as graph is manipulated */}
                <Box sx={{ 
                  flexShrink: 0, 
                  p: 1.5, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  bgcolor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
                }}>
                  {selectedNodes.size > 0 ? (
                    // Selection active - show selection info with detailed analysis
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Chip 
                          label={`${selectedNodes.size} Selected`} 
                          size="small" 
                          color="warning"
                          onDelete={handleClearSelection}
                          sx={{ fontWeight: 600 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          from {stats.nodes} nodes • {stats.links} links
                        </Typography>
                      </Stack>
                      
                      {/* Connection Analysis Summary */}
                      {selectionAnalysis && (
                        <Box sx={{ 
                          bgcolor: isDarkMode ? 'rgba(0,191,255,0.1)' : 'rgba(0,123,255,0.08)',
                          borderRadius: 1,
                          p: 1,
                          mb: 1,
                          border: '1px solid',
                          borderColor: isDarkMode ? 'rgba(0,191,255,0.3)' : 'rgba(0,123,255,0.2)',
                        }}>
                          <Typography variant="caption" fontWeight="bold" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LinkIcon sx={{ fontSize: 14 }} /> Connection Analysis
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                            {selectionAnalysis.summary}
                          </Typography>
                          
                          {/* Tracked Entities */}
                          {selectionAnalysis.selectedEntities.length > 0 && (
                            <Box sx={{ mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                Tracked: {selectionAnalysis.selectedEntities.map(e => e.label).join(', ')}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Shared Locations Details */}
                          {selectionAnalysis.sharedLocations.length > 0 && (
                            <Box sx={{ 
                              mt: 1, 
                              pt: 1, 
                              borderTop: '1px solid',
                              borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                            }}>
                              <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5, color: 'warning.main' }}>
                                Shared Locations ({selectionAnalysis.sharedLocations.length})
                              </Typography>
                              <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                                {selectionAnalysis.sharedLocations.slice(0, 5).map((loc, idx) => (
                                  <Box key={idx} sx={{ 
                                    mb: 0.75, 
                                    pb: 0.75, 
                                    borderBottom: idx < Math.min(4, selectionAnalysis.sharedLocations.length - 1) ? '1px dashed' : 'none',
                                    borderColor: 'divider',
                                  }}>
                                    <Typography variant="caption" fontWeight="medium" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                      {loc.locationLabel}
                                      {loc.placeType && <span style={{ opacity: 0.7 }}> ({loc.placeType})</span>}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                                      Visited by: {loc.visitDetails.map(v => v.entityLabel).join(', ')}
                                    </Typography>
                                    {loc.visitDetails.map((v, vIdx) => (
                                      <Typography key={vIdx} variant="caption" sx={{ fontSize: '0.6rem', display: 'block', pl: 1, color: 'text.secondary' }}>
                                        • {v.entityLabel}: {v.visitCount} visit{v.visitCount > 1 ? 's' : ''}
                                        {v.totalDuration > 0 && `, ~${Math.round(v.totalDuration)} min total`}
                                      </Typography>
                                    ))}
                                  </Box>
                                ))}
                                {selectionAnalysis.sharedLocations.length > 5 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontStyle: 'italic' }}>
                                    +{selectionAnalysis.sharedLocations.length - 5} more shared locations...
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          
                          {/* No shared locations message */}
                          {selectionAnalysis.sharedLocations.length === 0 && selectionAnalysis.trackedEntityCount > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                              No shared locations found between selected entities
                            </Typography>
                          )}
                        </Box>
                      )}
                      
                      <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', fontSize: '0.65rem' }}>
                      Shared locations (within 200m) highlighted
                      </Typography>
                    </Box>
                  ) : (
                    // No selection - show overview
                    <Box>
                      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        <Chip 
                          label={`${stats.nodes} Nodes`} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                        <Chip 
                          label={`${stats.links} Links`} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          • Use Select Mode to filter
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  <NetworkGraphMap 
                    links={filteredLinks as any}
                    nodes={filteredNodes as any}
                    selectedNodes={selectedNodes}
                    trackedEntities={trackedEntities}
                    onAreaSelect={setSelectedArea}
                    selectedArea={selectedArea}
                    showLegend={false}
                    showPopup={false}
                    selectionResetNonce={mapSelectionResetNonce}
                    selectedConnectionTypes={selectedConnectionTypes}
                    connectionType={connectionType}
                    flowMapEnabled={flowMapEnabled}
                    onFlowMapToggle={setFlowMapEnabled}
                    zoomToLocation={mapZoomLocation}
                    onNodeClick={(_, connectedEntityIds, ctrlKey) => {
                      // Map click selects the connected entities in the graph
                      // Ctrl+click adds to existing selection instead of replacing
                      if (ctrlKey && connectedEntityIds && connectedEntityIds.length > 0) {
                        setSelectedNodes(prev => {
                          const newSet = new Set(prev);
                          connectedEntityIds.forEach(id => newSet.add(id));
                          return newSet;
                        });
                      } else {
                        setSelectedNodes(new Set(connectedEntityIds ?? []));
                      }
                    }}
                    onSelectionInfoChange={(info) => setMapSelectionInfo(info)}
                  />
                </Box>
            </Box>

            {/* Charts Tab - Latest Version from Git */}
            {activeTab === 1 && (
              <Box id="network-charts-panel" sx={{ 
                flex: 1, 
                overflow: 'auto', 
                p: 1, 
                bgcolor: isDarkMode ? '#0a0a1a' : '#fafafa',
                // Custom minimalist scrollbar
                '&::-webkit-scrollbar': {
                  width: '6px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.25)',
                  borderRadius: '3px',
                  '&:hover': {
                    background: isDarkMode 
                      ? 'linear-gradient(180deg, #40f8ff 0%, #40a0ff 100%)'
                      : 'linear-gradient(180deg, #00b8c9 0%, #008090 100%)',
                    boxShadow: isDarkMode ? '0 0 8px rgba(0,240,255,0.5)' : 'none',
                  }
                },
                scrollbarWidth: 'thin',
                scrollbarColor: isDarkMode ? 'rgba(255, 255, 255, 0.3) transparent' : 'rgba(0, 0, 0, 0.25) transparent',
              }}>
                <Stack spacing={1.5}>
                  
                  {/* Summary Stats - Compact Inline Cards */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                    {[
                      { label: 'Nodes', value: stats.nodes, color: '#00f0ff', icon: <FiberManualRecordIcon sx={{ fontSize: 12 }} /> },
                      { label: 'Links', value: stats.links, color: '#f50057', icon: <LinkIcon sx={{ fontSize: 12 }} /> },
                      { label: 'Devices', value: filteredNodes.filter(n => n.type === 'device').length, color: '#00e676', icon: <PhoneIphoneIcon sx={{ fontSize: 12 }} /> },
                      { label: 'Locations', value: filteredNodes.filter(n => n.type === 'location').length, color: '#ff9100', icon: <LocationOnIcon sx={{ fontSize: 12 }} /> },
                    ].map((stat, i) => (
                      <Box 
                        key={i}
                        sx={{ 
                          px: 1.5,
                          py: 1,
                          borderRadius: 1.5,
                          background: isDarkMode 
                            ? `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}05 100%)`
                            : `linear-gradient(135deg, ${stat.color}20 0%, ${stat.color}08 100%)`,
                          border: `1px solid ${stat.color}40`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: stat.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {stat.icon} {stat.label}
                        </Typography>
                        <Typography variant="h6" sx={{ color: stat.color, fontWeight: 700, fontSize: '0.9rem' }}>
                          {stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Activity Calendar + Pie Charts Row */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 1.5, order: 3 }}>
                    {/* Activity Calendar Heatmap - Horizontal Span Layout */}
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      bgcolor: isDarkMode ? '#0d1117' : '#fff',
                      border: `1px solid ${isDarkMode ? '#30363d' : '#e0e0e0'}`,
                      boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                    {(() => {
                      const today = new Date();
                      const dateCounts = new Map<string, number>();
                      let minDate: Date | null = null;
                      let maxDate: Date | null = null;
                      
                      // Helper to get LOCAL date key (YYYY-MM-DD) to avoid timezone issues
                      const getLocalDateKey = (date: Date) => {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                      };
                      
                      // Build date counts and find date range from filtered data
                      filteredLinks.forEach((link: any) => {
                        if (link.timestamp) {
                          const date = parseBackendDateTime(link.timestamp);
                          if (!date) return;
                          const dateKey = getLocalDateKey(date);
                          dateCounts.set(dateKey, (dateCounts.get(dateKey) || 0) + 1);
                          
                          if (!minDate || date < minDate) minDate = new Date(date);
                          if (!maxDate || date > maxDate) maxDate = new Date(date);
                        }
                      });
                      
                      // If no data, show message
                      if (!minDate || !maxDate) {
                        return (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">No activity data available</Typography>
                          </Box>
                        );
                      }
                      
                      const rangeStart: Date = minDate;
                      const rangeEnd: Date = maxDate;
                      
                      const daySpan = Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                      const totalEvents = Array.from(dateCounts.values()).reduce((a, b) => a + b, 0);
                      const maxCount = Math.max(...Array.from(dateCounts.values()), 1);
                      
                      // Extend to full weeks (start on Sunday)
                      const startDate = new Date(rangeStart);
                      startDate.setDate(startDate.getDate() - startDate.getDay());
                      
                      const endDate = new Date(rangeEnd);
                      endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
                      
                      // Generate weeks - transposed: array of 7 days, each containing week cells
                      const weeks: { date: Date; count: number; inRange: boolean; dateKey: string }[][] = [];
                      const currentDate = new Date(startDate);
                      
                      while (currentDate <= endDate) {
                        const week: { date: Date; count: number; inRange: boolean; dateKey: string }[] = [];
                        for (let d = 0; d < 7; d++) {
                          const dateKey = getLocalDateKey(currentDate);
                          const inRange = currentDate >= rangeStart && currentDate <= rangeEnd;
                          week.push({ 
                            date: new Date(currentDate), 
                            count: dateCounts.get(dateKey) || 0,
                            inRange,
                            dateKey
                          });
                          currentDate.setDate(currentDate.getDate() + 1);
                        }
                        weeks.push(week);
                      }
                      
                      // Transpose: organize by day of week (rows) containing weeks (columns)
                      const dayRows: { date: Date; count: number; inRange: boolean; dateKey: string }[][] = [[], [], [], [], [], [], []];
                      weeks.forEach(week => {
                        week.forEach((day, dayIndex) => {
                          dayRows[dayIndex].push(day);
                        });
                      });
                      
                      const getColor = (count: number, inRange: boolean) => {
                        if (!inRange) return isDarkMode ? '#0d1117' : '#f6f8fa';
                        if (count === 0) return isDarkMode ? '#161b22' : '#ebedf0';
                        const intensity = Math.min(count / maxCount, 1);
                        if (intensity < 0.25) return isDarkMode ? '#0e4429' : '#9be9a8';
                        if (intensity < 0.5) return isDarkMode ? '#006d32' : '#40c463';
                        if (intensity < 0.75) return isDarkMode ? '#26a641' : '#30a14e';
                        return isDarkMode ? '#39d353' : '#216e39';
                      };
                      
                      const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      
                      // Handle date click for filtering
                      const handleDateClick = (dateKey: string, inRange: boolean) => {
                        if (!inRange || !onDateFilter) return;
                        
                        const newSelected = new Set(selectedDates || []);
                        if (newSelected.has(dateKey)) {
                          newSelected.delete(dateKey);
                        } else {
                          newSelected.add(dateKey);
                        }
                        onDateFilter(newSelected.size > 0 ? newSelected : null);
                      };
                      
                      const hasDateFilter = selectedDates && selectedDates.size > 0;
                      
                      return (
                        <Box>
                          {/* Header with inline legend */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                                color: isDarkMode ? '#e6edf3' : '#24292f',
                                fontSize: '0.85rem'
                              }}>
                                <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5 }} /> Activity Calendar
                                {hasDateFilter && (
                                  <Typography 
                                    component="span" 
                                    sx={{ 
                                      ml: 1, 
                                      fontSize: '0.65rem', 
                                      color: '#58a6ff', 
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                    onClick={() => onDateFilter?.(null)}
                                  >
                                    Clear ({selectedDates?.size})
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="caption" sx={{ color: isDarkMode ? '#7d8590' : '#656d76', fontSize: '0.65rem' }}>
                                Click cells to filter • {totalEvents.toLocaleString()} events in {daySpan} days
                              </Typography>
                            </Box>
                            {/* Legend - inline */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: isDarkMode ? '#7d8590' : '#656d76' }}>
                                Less
                              </Typography>
                              {[0, 0.25, 0.5, 0.75, 1].map((intensity, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '3px',
                                    bgcolor: getColor(intensity * maxCount, true),
                                  }}
                                />
                              ))}
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: isDarkMode ? '#7d8590' : '#656d76' }}>
                                More
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Date labels at top - show first and last */}
                          <Box sx={{ display: 'flex', mb: 0.5, pl: 5 }}>
                            <Typography variant="caption" sx={{ 
                              flex: 1, 
                              fontSize: '0.7rem', 
                              color: isDarkMode ? '#7d8590' : '#656d76' 
                            }}>
                              {rangeStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontSize: '0.7rem', 
                              color: isDarkMode ? '#7d8590' : '#656d76' 
                            }}>
                              {rangeEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </Typography>
                          </Box>
                          
                          {/* Calendar grid - days as rows, weeks as columns */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {dayRows.map((row, dayIndex) => (
                              <Box key={dayIndex} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {/* Day label */}
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    width: 32,
                                    fontSize: '0.75rem',
                                    color: isDarkMode ? '#7d8590' : '#656d76',
                                    fontWeight: 500
                                  }}
                                >
                                  {dayLabels[dayIndex]}
                                </Typography>
                                
                                {/* Week cells - spanning horizontally */}
                                <Box sx={{ display: 'flex', flex: 1, gap: '4px' }}>
                                  {row.map((cell, weekIndex) => {
                                    const isSelected = selectedDates?.has(cell.dateKey);
                                    const isToday = cell.date.toDateString() === today.toDateString();
                                    
                                    return (
                                      <Tooltip 
                                        key={weekIndex}
                                        title={cell.inRange ? (
                                          <Box sx={{ textAlign: 'center', color: 'inherit' }}>
                                            <Typography variant="caption" component="div" sx={{ fontWeight: 600, display: 'block', color: 'inherit' }}>
                                              {cell.count} connection{cell.count !== 1 ? 's' : ''}
                                            </Typography>
                                            <Typography variant="caption" component="div" sx={{ display: 'block', mt: 0.25, color: 'inherit', opacity: 0.9 }}>
                                              {cell.date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                                            </Typography>
                                            <Typography variant="caption" component="div" sx={{ display: 'block', mt: 0.5, color: 'inherit', opacity: 0.8 }}>
                                              Click to {isSelected ? 'remove from' : 'add to'} filter
                                            </Typography>
                                          </Box>
                                        ) : (
                                          <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 500, display: 'block' }}>
                                            Outside selected range
                                          </Typography>
                                        )}
                                        arrow
                                        placement="top"
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              bgcolor: isDarkMode ? 'grey.900' : 'grey.800',
                                              color: 'common.white',
                                              maxWidth: 280,
                                              py: 1,
                                              px: 1.25,
                                              fontSize: '0.75rem',
                                              border: isDarkMode ? '1px solid' : 'none',
                                              borderColor: 'grey.700',
                                              '& .MuiTypography-root': { color: 'inherit' },
                                            },
                                          },
                                          arrow: {
                                            sx: { color: isDarkMode ? 'grey.900' : 'grey.800' },
                                          },
                                        }}
                                      >
                                        <Box
                                          onClick={(e) => { e.stopPropagation(); handleDateClick(cell.dateKey, cell.inRange); }}
                                          sx={{
                                            flex: 1,
                                            height: 18,
                                            borderRadius: '6px',
                                            bgcolor: getColor(cell.count, cell.inRange),
                                            cursor: cell.inRange ? 'pointer' : 'default',
                                            transition: 'all 0.15s ease',
                                            opacity: cell.inRange ? (hasDateFilter && !isSelected ? 0.4 : 1) : 0.15,
                                            outline: isSelected 
                                              ? `2px solid ${isDarkMode ? '#58a6ff' : '#0969da'}` 
                                              : isToday 
                                                ? `2px solid ${isDarkMode ? '#f0883e' : '#bf8700'}` 
                                                : 'none',
                                            outlineOffset: '-1px',
                                            '&:hover': cell.inRange ? { 
                                              transform: 'scaleY(1.15)', 
                                              zIndex: 10,
                                              boxShadow: `0 2px 8px ${getColor(cell.count, cell.inRange)}80`
                                            } : {}
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  })}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    })()}
                    </Box>

                    {/* Connection Types - Radial Bar Chart */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {/* Connection Types - Clickable */}
                      {(() => {
                        const rawData = [
                          { name: 'SMS', type: 'sms', value: filteredLinks.filter(l => l.type === 'sms').length, fill: '#2196f3' },
                          { name: 'Calls', type: 'call', value: filteredLinks.filter(l => l.type === 'call').length, fill: '#4caf50' },
                          { name: 'Dwells', type: 'dwell', value: filteredLinks.filter(l => l.type === 'dwell').length, fill: '#ff9800' },
                          { name: 'Browse', type: 'browsing', value: filteredLinks.filter(l => l.type === 'browsing').length, fill: '#9c27b0' },
                        ].filter(d => d.value > 0);
                        
                        // Sort by value descending so largest is outermost
                        const connectionTypeData = rawData.sort((a, b) => b.value - a.value);
                        
                        const hasConnTypeFilter = selectedConnectionTypes && selectedConnectionTypes.size > 0;
                        
                        const handleConnTypeClick = (type: string) => {
                          if (!onConnectionTypeFilter) return;
                          const newSelected = new Set(selectedConnectionTypes || []);
                          if (newSelected.has(type)) {
                            newSelected.delete(type);
                          } else {
                            newSelected.add(type);
                          }
                          onConnectionTypeFilter(newSelected.size > 0 ? newSelected : null);
                        };
                        
                        return (
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            bgcolor: isDarkMode ? '#0d1117' : '#fff',
                            border: `1px solid ${isDarkMode ? '#30363d' : '#e0e0e0'}`,
                            boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                            flex: 1
                          }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDarkMode ? '#00e676' : '#2e7d32', fontSize: '0.75rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinkIcon sx={{ fontSize: 14 }} /> Connection Types
                              {hasConnTypeFilter && (
                                <Typography 
                                  component="span" 
                                  sx={{ ml: 1, fontSize: '0.65rem', color: '#58a6ff', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                  onClick={() => onConnectionTypeFilter?.(null)}
                                >
                                  Clear ({selectedConnectionTypes?.size})
                                </Typography>
                              )}
                            </Typography>
                            <Box sx={{ height: 150 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={connectionTypeData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="55%"
                                    outerRadius="85%"
                                    paddingAngle={4}
                                    dataKey="value"
                                    cornerRadius={10}
                                    onClick={(data) => handleConnTypeClick(data.type)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {connectionTypeData.map((entry, index) => (
                                      <Cell 
                                        key={`cell-${index}`} 
                                        fill={entry.fill}
                                        opacity={hasConnTypeFilter && !selectedConnectionTypes?.has(entry.type) ? 0.3 : 1}
                                        stroke="none"
                                      />
                                    ))}
                                  </Pie>
                                  <RechartsTooltip
                                    {...rechartsTooltipProps}
                                    formatter={(value: number, _name: string, props: any) => [value, props.payload.name]}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            </Box>
                            {/* Legend - bottom */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                              {connectionTypeData.map((item, i) => {
                                const isSelected = selectedConnectionTypes?.has(item.type);
                                return (
                                  <Tooltip key={i} title={`Click to ${isSelected ? 'remove from' : 'add to'} filter`}>
                                    <Box 
                                      onClick={(e) => { e.stopPropagation(); handleConnTypeClick(item.type); }}
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.3, 
                                        cursor: 'pointer',
                                        opacity: hasConnTypeFilter && !isSelected ? 0.4 : 1,
                                        px: 0.5,
                                        py: 0.25,
                                        borderRadius: 1,
                                        border: isSelected ? '1px solid' : '1px solid transparent',
                                        borderColor: isSelected ? item.fill : 'transparent',
                                        '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }
                                      }}
                                    >
                                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.fill }} />
                                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: isDarkMode ? '#7d8590' : '#656d76' }}>
                                        {item.name} ({item.value})
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })()}

                    </Box>
                  </Box>

                  {/* Hourly Activity Heatmap - Day x Hour with filtering */}
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: isDarkMode ? '#0d1117' : '#fff',
                    border: `1px solid ${isDarkMode ? '#30363d' : '#e0e0e0'}`,
                    boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                    order: 4
                  }}>
                    {(() => {
                      // Build hourly activity grid: 7 days x 24 hours (from filtered data)
                      const hourlyGrid: number[][] = Array(7).fill(null).map(() => Array(24).fill(0));
                      
                      filteredLinks.forEach((link: any) => {
                        if (link.timestamp) {
                          const date = parseBackendDateTime(link.timestamp);
                          if (!date) return;
                          const dayOfWeek = date.getDay();
                          const hour = date.getHours();
                          hourlyGrid[dayOfWeek][hour]++;
                        }
                      });
                      
                      const maxHourly = Math.max(...hourlyGrid.flat(), 1);
                      const totalHourlyEvents = hourlyGrid.flat().reduce((a, b) => a + b, 0);
                      
                      let peakDay = 0, peakHour = 0, peakCount = 0;
                      hourlyGrid.forEach((day, di) => {
                        day.forEach((count, hi) => {
                          if (count > peakCount) {
                            peakCount = count;
                            peakDay = di;
                            peakHour = hi;
                          }
                        });
                      });
                      
                      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      
                      const getHourColor = (count: number) => {
                        if (count === 0) return isDarkMode ? '#161b22' : '#ebedf0';
                        const intensity = Math.min(count / maxHourly, 1);
                        if (intensity < 0.25) return isDarkMode ? '#1e3a5f' : '#c5cae9';
                        if (intensity < 0.5) return isDarkMode ? '#2e5a9f' : '#7986cb';
                        if (intensity < 0.75) return isDarkMode ? '#3f7ae0' : '#5c6bc0';
                        return isDarkMode ? '#58a6ff' : '#3f51b5';
                      };
                      
                      const formatHour = (h: number) => {
                        if (h === 0) return '12a';
                        if (h === 12) return '12p';
                        return h < 12 ? `${h}a` : `${h-12}p`;
                      };
                      
                      // Handle hour click for filtering
                      const handleHourClick = (day: number, hour: number) => {
                        if (!onHourFilter) return;
                        
                        const newSelected = [...(selectedHours || [])];
                        const existingIndex = newSelected.findIndex(h => h.day === day && h.hour === hour);
                        
                        if (existingIndex >= 0) {
                          newSelected.splice(existingIndex, 1);
                        } else {
                          newSelected.push({ day, hour });
                        }
                        onHourFilter(newSelected.length > 0 ? newSelected : null);
                      };
                      
                      const isHourSelected = (day: number, hour: number) => {
                        return selectedHours?.some(h => h.day === day && h.hour === hour) || false;
                      };
                      
                      const hasHourFilter = selectedHours && selectedHours.length > 0;
                      
                      return (
                        <Box>
                          {/* Header */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                                color: isDarkMode ? '#e6edf3' : '#24292f',
                                fontSize: '0.95rem'
                              }}>
                                <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5 }} /> Hourly Patterns
                                {hasHourFilter && (
                                  <Typography 
                                    component="span" 
                                    sx={{ 
                                      ml: 1, 
                                      fontSize: '0.7rem', 
                                      color: '#58a6ff', 
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                    onClick={() => onHourFilter?.(null)}
                                  >
                                    Clear filter ({selectedHours?.length})
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="caption" sx={{ color: isDarkMode ? '#7d8590' : '#656d76', fontSize: '0.7rem' }}>
                                Click cells to filter • {totalHourlyEvents.toLocaleString()} events
                              </Typography>
                            </Box>
                            {peakCount > 0 && (
                              <Box sx={{ 
                                px: 1.5, 
                                py: 0.5, 
                                borderRadius: 2, 
                                bgcolor: isDarkMode ? '#1f6feb33' : '#e3f2fd',
                                color: isDarkMode ? '#58a6ff' : '#1976d2',
                                fontSize: '0.7rem',
                                fontWeight: 600
                              }}>
                                🔥 Peak: {fullDayNames[peakDay]} {formatHour(peakHour)}
                              </Box>
                            )}
                          </Box>
                          
                          {/* Hour labels */}
                          <Box sx={{ display: 'flex', mb: 0.5, pl: 4 }}>
                            {Array(24).fill(0).map((_, h) => (
                              <Typography 
                                key={h} 
                                variant="caption" 
                                sx={{ 
                                  flex: 1,
                                  textAlign: 'center',
                                  fontSize: '0.55rem',
                                  color: isDarkMode ? '#7d8590' : '#656d76',
                                  visibility: h % 3 === 0 ? 'visible' : 'hidden'
                                }}
                              >
                                {formatHour(h)}
                              </Typography>
                            ))}
                          </Box>
                          
                          {/* Grid */}
                          <Box>
                            {hourlyGrid.map((dayData, dayIndex) => (
                              <Box key={dayIndex} sx={{ display: 'flex', alignItems: 'center', mb: '3px' }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    width: 32,
                                    fontSize: '0.7rem',
                                    color: isDarkMode ? '#7d8590' : '#656d76',
                                    fontWeight: 500
                                  }}
                                >
                                  {dayNames[dayIndex]}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flex: 1, gap: '2px' }}>
                                  {dayData.map((count, hourIndex) => {
                                    const isSelected = isHourSelected(dayIndex, hourIndex);
                                    return (
                                      <Tooltip 
                                        key={hourIndex}
                                        title={
                                          <Box sx={{ textAlign: 'center', color: 'inherit' }}>
                                            <Typography variant="caption" component="div" sx={{ fontWeight: 600, display: 'block', color: 'inherit' }}>
                                              {count} event{count !== 1 ? 's' : ''}
                                            </Typography>
                                            <Typography variant="caption" component="div" sx={{ display: 'block', mt: 0.25, color: 'inherit', opacity: 0.9 }}>
                                              {fullDayNames[dayIndex]} {formatHour(hourIndex)} - {formatHour(hourIndex + 1)}
                                            </Typography>
                                            <Typography variant="caption" component="div" sx={{ display: 'block', mt: 0.5, color: 'inherit', opacity: 0.8 }}>
                                              Click to {isSelected ? 'remove from' : 'add to'} filter
                                            </Typography>
                                          </Box>
                                        }
                                        arrow
                                        placement="top"
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              bgcolor: isDarkMode ? 'grey.900' : 'grey.800',
                                              color: 'common.white',
                                              maxWidth: 280,
                                              py: 1,
                                              px: 1.25,
                                              fontSize: '0.75rem',
                                              border: isDarkMode ? '1px solid' : 'none',
                                              borderColor: 'grey.700',
                                              '& .MuiTypography-root': { color: 'inherit' },
                                            },
                                          },
                                          arrow: {
                                            sx: { color: isDarkMode ? 'grey.900' : 'grey.800' },
                                          },
                                        }}
                                      >
                                        <Box
                                          onClick={(e) => { e.stopPropagation(); handleHourClick(dayIndex, hourIndex); }}
                                          sx={{
                                            flex: 1,
                                            height: 20,
                                            borderRadius: '3px',
                                            bgcolor: getHourColor(count),
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease',
                                            opacity: hasHourFilter && !isSelected ? 0.4 : 1,
                                            outline: isSelected ? `2px solid ${isDarkMode ? '#58a6ff' : '#0969da'}` : 'none',
                                            outlineOffset: '-1px',
                                            '&:hover': { 
                                              transform: 'scaleY(1.2)', 
                                              zIndex: 10,
                                              boxShadow: `0 0 8px ${getHourColor(count)}`
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  })}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                          
                          {/* Legend */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mt: 2, gap: 1 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: isDarkMode ? '#7d8590' : '#656d76' }}>
                              Less
                            </Typography>
                            {[0, 0.25, 0.5, 0.75, 1].map((intensity, i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '4px',
                                  bgcolor: getHourColor(intensity * maxHourly),
                                }}
                              />
                            ))}
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: isDarkMode ? '#7d8590' : '#656d76' }}>
                              More
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>

                  {/* Activity Timeline - Recharts */}
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: isDarkMode ? '#12122a' : '#fff',
                    border: `1px solid ${isDarkMode ? '#2a2a4a' : '#e0e0e0'}`,
                    order: 5
                  }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDarkMode ? '#f50057' : '#d32f2f' }}>
                      <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} /> Activity Over Time
                    </Typography>
                    <Box sx={{ height: 200 }}>
                      {(() => {
                        const dayCounts = new Map<string, { sms: number; call: number; dwell: number; browsing: number }>();
                        filteredLinks.forEach((link: any) => {
                          if (link.timestamp) {
                            const date = parseBackendDateTime(link.timestamp);
                            if (!date) return;
                            const dayKey = date.toISOString().split('T')[0];
                            const current = dayCounts.get(dayKey) || { sms: 0, call: 0, dwell: 0, browsing: 0 };
                            if (link.type === 'sms') current.sms++;
                            else if (link.type === 'call') current.call++;
                            else if (link.type === 'dwell') current.dwell++;
                            else if (link.type === 'browsing') current.browsing++;
                            dayCounts.set(dayKey, current);
                          }
                        });
                        const timelineData = Array.from(dayCounts.entries())
                          .sort((a, b) => a[0].localeCompare(b[0]))
                          .slice(-14)
                          .map(([day, counts]) => ({ day: day.slice(5), ...counts }));
                        
                        return timelineData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={timelineData}>
                              <defs>
                                <linearGradient id="smsGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#2196f3" stopOpacity={0.1}/>
                                </linearGradient>
                                <linearGradient id="callGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
                                </linearGradient>
                                <linearGradient id="dwellGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#ff9800" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#ff9800" stopOpacity={0.1}/>
                                </linearGradient>
                                <linearGradient id="browsingGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#9c27b0" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#9c27b0" stopOpacity={0.1}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#eee'} />
                              <XAxis dataKey="day" tick={{ fill: isDarkMode ? '#888' : '#666', fontSize: 10 }} />
                              <YAxis tick={{ fill: isDarkMode ? '#888' : '#666', fontSize: 10 }} />
                              <RechartsTooltip {...rechartsTooltipProps} />
                              <Area type="monotone" dataKey="sms" stackId="1" stroke="#2196f3" fill="url(#smsGrad)" name="SMS" />
                              <Area type="monotone" dataKey="call" stackId="1" stroke="#4caf50" fill="url(#callGrad)" name="Calls" />
                              <Area type="monotone" dataKey="dwell" stackId="1" stroke="#ff9800" fill="url(#dwellGrad)" name="Dwells" />
                              <Area type="monotone" dataKey="browsing" stackId="1" stroke="#9c27b0" fill="url(#browsingGrad)" name="Browse" />
                              <Legend wrapperStyle={{ color: theme.palette.text.primary, fontSize: '0.7rem' }} />
                            </AreaChart>
                          </ResponsiveContainer>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography color="text.secondary">No timeline data available</Typography>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>

                  {/* Node Degree Distribution - Bar chart showing connection distribution */}
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: isDarkMode ? '#12122a' : '#fff',
                    border: `1px solid ${isDarkMode ? '#2a2a4a' : '#e0e0e0'}`,
                    order: 2
                  }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDarkMode ? typeColorMap.device : '#8B7355', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BarChartIcon sx={{ fontSize: 16 }} /> Node Degree Distribution
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      How many connections each entity has • Log scale for better visibility
                    </Typography>
                    <Box sx={{ height: 200 }}>
                      {(() => {
                        // Calculate degree (connection count) for each node
                        const nodeDegreeMap = new Map<string, number>();
                        filteredNodes.forEach((node: any) => {
                          nodeDegreeMap.set(node.id, 0);
                        });
                        
                        filteredLinks.forEach((link: any) => {
                          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
                          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
                          if (sourceId && nodeDegreeMap.has(sourceId)) {
                            nodeDegreeMap.set(sourceId, (nodeDegreeMap.get(sourceId) || 0) + 1);
                          }
                          if (targetId && nodeDegreeMap.has(targetId)) {
                            nodeDegreeMap.set(targetId, (nodeDegreeMap.get(targetId) || 0) + 1);
                          }
                        });
                        
                        // Group nodes by degree into buckets
                        const degreeCounts = new Map<string, number>();
                        const degrees = Array.from(nodeDegreeMap.values());
                        const maxDegree = Math.max(...degrees, 1);
                        
                        // Create sensible bucket sizes based on max degree
                        const bucketSize = maxDegree <= 10 ? 1 : maxDegree <= 50 ? 5 : maxDegree <= 100 ? 10 : 25;
                        
                        degrees.forEach(degree => {
                          const bucket = Math.floor(degree / bucketSize) * bucketSize;
                          const label = bucketSize === 1 ? `${bucket}` : `${bucket}-${bucket + bucketSize - 1}`;
                          degreeCounts.set(label, (degreeCounts.get(label) || 0) + 1);
                        });
                        
                        // Convert to sorted array for chart
                        const distributionData = Array.from(degreeCounts.entries())
                          .map(([range, count]) => ({ 
                            range, 
                            count,
                            sortKey: parseInt(range.split('-')[0]) 
                          }))
                          .sort((a, b) => a.sortKey - b.sortKey)
                          .slice(0, 15); // Limit to 15 buckets for readability
                        
                        // Calculate stats
                        const totalNodes = degrees.length;
                        const avgDegree = totalNodes > 0 ? (degrees.reduce((a, b) => a + b, 0) / totalNodes).toFixed(1) : 0;
                        const medianDegree = totalNodes > 0 ? degrees.sort((a, b) => a - b)[Math.floor(totalNodes / 2)] : 0;
                        
                        // Find top connected nodes
                        const topConnected = Array.from(nodeDegreeMap.entries())
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 3)
                          .map(([id, deg]) => {
                            const node = filteredNodes.find((n: any) => n.id === id);
                            return { label: node?.label || id, degree: deg };
                          });
                        
                        return distributionData.length > 0 ? (
                          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* Stats row */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ color: isDarkMode ? '#7d8590' : '#656d76' }}>
                                Avg: <strong style={{ color: isDarkMode ? typeColorMap.device : '#8B7355' }}>{avgDegree}</strong>
                              </Typography>
                              <Typography variant="caption" sx={{ color: isDarkMode ? '#7d8590' : '#656d76' }}>
                                Median: <strong style={{ color: isDarkMode ? typeColorMap.device : '#8B7355' }}>{medianDegree}</strong>
                              </Typography>
                              <Typography variant="caption" sx={{ color: isDarkMode ? '#7d8590' : '#656d76' }}>
                                Max: <strong style={{ color: isDarkMode ? typeColorMap.device : '#8B7355' }}>{maxDegree}</strong>
                              </Typography>
                              {topConnected.length > 0 && (
                                <Typography variant="caption" sx={{ color: isDarkMode ? '#7d8590' : '#656d76' }}>
                                  Top: <strong style={{ color: isDarkMode ? '#f59e0b' : '#d97706' }}>{topConnected[0].label}</strong> ({topConnected[0].degree})
                                </Typography>
                              )}
                            </Box>
                            {/* Chart - using log scale to show skewed distribution better */}
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={distributionData} margin={{ top: 20, right: 10, left: 5, bottom: 25 }}>
                                  <defs>
                                    <linearGradient id="degreeGrad" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor={typeColorMap.device} stopOpacity={0.9}/>
                                      <stop offset="95%" stopColor={typeColorMap.device} stopOpacity={0.4}/>
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#eee'} />
                                  <XAxis 
                                    dataKey="range" 
                                    tick={{ fill: isDarkMode ? '#888' : '#666', fontSize: 9 }} 
                                    label={{ value: 'Connections', position: 'insideBottom', offset: -5, fill: isDarkMode ? '#666' : '#999', fontSize: 10 }}
                                  />
                                  <YAxis 
                                    scale="log"
                                    domain={[1, 'auto']}
                                    allowDataOverflow
                                    tick={{ fill: isDarkMode ? '#888' : '#666', fontSize: 9 }}
                                    label={{ value: 'Nodes (log)', angle: -90, position: 'insideLeft', fill: isDarkMode ? '#666' : '#999', fontSize: 10 }}
                                    tickFormatter={(value) => value >= 1 ? value.toLocaleString() : ''}
                                  />
                                  <RechartsTooltip
                                    {...rechartsTooltipProps}
                                    formatter={(value: any) => [`${value} nodes`, 'Count']}
                                    labelFormatter={(label) => `${label} connections`}
                                  />
                                  <Bar 
                                    dataKey="count" 
                                    fill="url(#degreeGrad)" 
                                    radius={[4, 4, 0, 0]} 
                                    name="Nodes"
                                    label={{ 
                                      position: 'top', 
                                      fill: isDarkMode ? '#aaa' : '#666', 
                                      fontSize: 9,
                                      formatter: (value: number) => value > 0 ? value : ''
                                    }}
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography color="text.secondary">No node data available</Typography>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>

                  {/* Connection Matrix - Shows shared connections between entities */}
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: isDarkMode ? '#12122a' : '#fff', border: `1px solid ${isDarkMode ? '#2a2a4a' : '#e0e0e0'}`, width: '100%', order: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDarkMode ? '#00f0ff' : '#0097a7', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LinkIcon sx={{ fontSize: 14 }} /> Shared Connections
                    </Typography>
                        <Typography variant="caption" sx={{ color: isDarkMode ? '#7d8590' : '#656d76' }}>
                          Locations/contacts in common
                        </Typography>
                      </Box>
                      {matrixSelectedEntities && matrixSelectedEntities.length > 0 && (
                        <Chip
                          label="Clear Filter"
                          size="small"
                          onDelete={() => {
                            setMatrixHighlightedCell(null);
                            setMatrixSelectedEntities(null);
                            if (onEntityFilter) onEntityFilter([]);
                          }}
                          sx={{ height: 20, fontSize: '0.6rem' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ 
                      overflow: 'auto', 
                      width: '100%',
                      '&::-webkit-scrollbar': {
                        width: '4px',
                        height: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode 
                          ? 'linear-gradient(180deg, #00f0ff 0%, #0080ff 100%)'
                          : 'linear-gradient(180deg, #0097a7 0%, #006070 100%)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: isDarkMode 
                            ? 'linear-gradient(180deg, #40f8ff 0%, #40a0ff 100%)'
                            : 'linear-gradient(180deg, #00b8c9 0%, #008090 100%)',
                        }
                      },
                      scrollbarWidth: 'thin',
                      scrollbarColor: isDarkMode ? 'rgba(255, 255, 255, 0.3) transparent' : 'rgba(0, 0, 0, 0.25) transparent',
                    }}>
                      {(() => {
                        // Get only device nodes (entities like Students, Trainees, etc.) from filtered data
                        const deviceNodes = filteredNodes.filter((n: any) => n.type === 'device');
                        
                        // Get connection counts for each device node from filtered links
                        const connectionCounts = new Map<string, number>();
                        filteredLinks.forEach((link: any) => {
                          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
                          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
                          const sourceNode = filteredNodes.find((n: any) => n.id === sourceId);
                          const targetNode = filteredNodes.find((n: any) => n.id === targetId);
                          // Only count if at least one end is a device
                          if (sourceNode?.type === 'device') {
                          connectionCounts.set(sourceId, (connectionCounts.get(sourceId) || 0) + 1);
                          }
                          if (targetNode?.type === 'device') {
                          connectionCounts.set(targetId, (connectionCounts.get(targetId) || 0) + 1);
                          }
                        });
                        
                        // Get top 20 device nodes by connection count
                        const topNodeIds = Array.from(connectionCounts.entries())
                          .filter(([id]) => deviceNodes.some((n: any) => n.id === id))
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 20)
                          .map(([id]) => id);
                        
                        if (topNodeIds.length < 2) {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100 }}>
                              <Typography color="text.secondary" variant="caption">Not enough data</Typography>
                            </Box>
                          );
                        }
                        
                        // Build a map of what each device connects to, categorized by type
                        const deviceConnections = new Map<string, { locations: Set<string>; phones: Set<string>; others: Set<string> }>();
                        topNodeIds.forEach(id => deviceConnections.set(id, { locations: new Set(), phones: new Set(), others: new Set() }));
                        
                        filteredLinks.forEach((link: any) => {
                          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
                          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
                          const sourceNode = filteredNodes.find((n: any) => n.id === sourceId);
                          const targetNode = filteredNodes.find((n: any) => n.id === targetId);
                          
                          // Categorize the connection
                          const categorize = (connId: string, connNode: any) => {
                            if (connNode?.type === 'location') return 'locations';
                            if (connId.startsWith('phone:') || connNode?.type === 'phone') return 'phones';
                            return 'others';
                          };
                          
                          // If source is a top device, add target to its connections
                          if (deviceConnections.has(sourceId)) {
                            const category = categorize(targetId, targetNode);
                            deviceConnections.get(sourceId)![category as 'locations' | 'phones' | 'others'].add(targetId);
                          }
                          // If target is a top device, add source to its connections
                          if (deviceConnections.has(targetId)) {
                            const category = categorize(sourceId, sourceNode);
                            deviceConnections.get(targetId)![category as 'locations' | 'phones' | 'others'].add(sourceId);
                          }
                        });
                        
                        // Build node labels for display
                        const nodeLabels = topNodeIds.map(id => {
                          const node = nodes.find((n: any) => n.id === id);
                            let label = node?.label || id;
                            if (label.startsWith('phone:')) label = label.replace('phone:', '');
                          if (label.startsWith('device:')) label = label.replace('device:', '');
                          return { id, label };
                        });
                        
                        // Build connection matrix based on SHARED connections
                        const matrix: { total: number; locations: number; phones: number; others: number }[][] = [];
                        const sharedConnectionDetails = new Map<string, { locations: string[]; phones: string[]; others: string[] }>();
                        let maxCount = 0;
                        
                        for (let i = 0; i < topNodeIds.length; i++) {
                          matrix[i] = [];
                          for (let j = 0; j < topNodeIds.length; j++) {
                            if (i === j) {
                              matrix[i][j] = { total: -1, locations: 0, phones: 0, others: 0 }; // Diagonal
                            } else {
                              // Count shared connections between device i and device j by category
                              const connI = deviceConnections.get(topNodeIds[i]) || { locations: new Set(), phones: new Set(), others: new Set() };
                              const connJ = deviceConnections.get(topNodeIds[j]) || { locations: new Set(), phones: new Set(), others: new Set() };
                              
                              // Find intersection for each category - store actual IDs
                              const sharedLocs: string[] = [];
                              const sharedPhs: string[] = [];
                              const sharedOths: string[] = [];
                              
                              connI.locations.forEach(loc => { if (connJ.locations.has(loc)) sharedLocs.push(loc); });
                              connI.phones.forEach(ph => { if (connJ.phones.has(ph)) sharedPhs.push(ph); });
                              connI.others.forEach(o => { if (connJ.others.has(o)) sharedOths.push(o); });
                              
                              const total = sharedLocs.length + sharedPhs.length + sharedOths.length;
                              matrix[i][j] = { total, locations: sharedLocs.length, phones: sharedPhs.length, others: sharedOths.length };
                              
                              // Store the actual shared connection IDs
                              const pairKey = `${i}-${j}`;
                              sharedConnectionDetails.set(pairKey, { locations: sharedLocs, phones: sharedPhs, others: sharedOths });
                              
                              if (total > maxCount) maxCount = total;
                            }
                          }
                        }
                        
                        // Helper to get label for a connection ID
                        const getConnectionLabel = (connId: string): string => {
                          const node = nodes.find((n: any) => n.id === connId);
                          if (node?.label) {
                            let label = node.label;
                            if (label.startsWith('phone:')) label = label.replace('phone:', '');
                            if (label.startsWith('url:')) label = label.replace('url:', '');
                            if (label.startsWith('location:')) label = label.replace('location:', '');
                            return label;
                          }
                          if (connId.startsWith('phone:')) return connId.replace('phone:', '');
                          if (connId.startsWith('url:')) return connId.replace('url:', '');
                          if (connId.startsWith('location:')) return connId.replace('location:', '');
                          return connId;
                        };
                        
                        const getMatrixColor = (cell: { total: number; locations: number; phones: number; others: number }) => {
                          if (cell.total === -1) return isDarkMode ? '#1a1a2e' : '#f5f5f5'; // Diagonal
                          if (cell.total === 0) return isDarkMode ? '#1e1e3a' : '#fafafa';
                          const intensity = Math.min(cell.total / Math.max(maxCount, 1), 1);
                          if (isDarkMode) {
                            const r = Math.round(0 + intensity * 0);
                            const g = Math.round(30 + intensity * 210);
                            const b = Math.round(60 + intensity * 195);
                            return `rgb(${r}, ${g}, ${b})`;
                          } else {
                            const r = Math.round(255 - intensity * 55);
                            const g = Math.round(255 - intensity * 108);
                            const b = Math.round(255 - intensity * 88);
                            return `rgb(${r}, ${g}, ${b})`;
                          }
                        };
                        
                        const getMatrixTooltip = (i: number, j: number, cell: { total: number; locations: number; phones: number; others: number }) => {
                          if (cell.total === -1) return '';
                          if (cell.total === 0) return `${nodeLabels[i].label} ↔ ${nodeLabels[j].label}: No shared connections`;
                          const parts: string[] = [];
                          if (cell.locations > 0) parts.push(`${cell.locations} location${cell.locations > 1 ? 's' : ''}`);
                          if (cell.phones > 0) parts.push(`${cell.phones} phone${cell.phones > 1 ? 's' : ''}`);
                          if (cell.others > 0) parts.push(`${cell.others} other`);
                          return `${nodeLabels[i].label} ↔ ${nodeLabels[j].label}\n${parts.join(', ')}`;
                        };
                        
                        // Calculate cell size based on number of nodes (responsive)
                        const numNodes = nodeLabels.length;
                        const labelWidth = 75;
                        const headerHeight = 80;
                        const minCellSize = 18;
                        const maxCellSize = 28;
                        const availableWidth = 450 - labelWidth;
                        const calculatedCellSize = Math.floor(availableWidth / numNodes);
                        const cellSize = Math.max(minCellSize, Math.min(maxCellSize, calculatedCellSize));
                        
                        // Handler for cell click - highlight and filter
                        const handleCellClick = (i: number, j: number) => {
                          const entityI = nodeLabels[i].id;
                          const entityJ = nodeLabels[j].id;
                          
                          if (matrixHighlightedCell?.i === i && matrixHighlightedCell?.j === j) {
                            // Click same cell to deselect - clear filters
                            setMatrixHighlightedCell(null);
                            setMatrixSelectedEntities(null);
                            if (onEntityFilter) {
                              onEntityFilter([]); // Clear filter
                            }
                          } else {
                            // Select new cell - apply filters
                            setMatrixHighlightedCell({ i, j });
                            setMatrixSelectedEntities([entityI, entityJ]);
                            if (onEntityFilter) {
                              onEntityFilter([entityI, entityJ]); // Filter to these two entities
                            }
                          }
                        };
                        
                        // Check if a cell should be highlighted
                        const isHighlighted = (i: number, j: number) => {
                          if (!matrixHighlightedCell) return false;
                          return i === matrixHighlightedCell.i || j === matrixHighlightedCell.j;
                        };
                        
                        const isSelectedCell = (i: number, j: number) => {
                          if (!matrixHighlightedCell) return false;
                          return (i === matrixHighlightedCell.i && j === matrixHighlightedCell.j) ||
                                 (i === matrixHighlightedCell.j && j === matrixHighlightedCell.i);
                        };
                        
                        // Build a flat list of all shared connections across all pairs
                        const allSharedConnections: { id: string; type: 'location' | 'phone' | 'other'; label: string; pairCount: number }[] = [];
                        const connectionCountsForList = new Map<string, { type: 'location' | 'phone' | 'other'; count: number }>();
                        sharedConnectionDetails.forEach((detail) => {
                          detail.locations.forEach(loc => {
                            const existing = connectionCountsForList.get(loc);
                            connectionCountsForList.set(loc, { type: 'location', count: (existing?.count || 0) + 1 });
                          });
                          detail.phones.forEach(ph => {
                            const existing = connectionCountsForList.get(ph);
                            connectionCountsForList.set(ph, { type: 'phone', count: (existing?.count || 0) + 1 });
                          });
                          detail.others.forEach(o => {
                            const existing = connectionCountsForList.get(o);
                            connectionCountsForList.set(o, { type: 'other', count: (existing?.count || 0) + 1 });
                          });
                        });
                        connectionCountsForList.forEach((value, id) => {
                          allSharedConnections.push({ id, type: value.type, label: getConnectionLabel(id), pairCount: value.count });
                        });
                        allSharedConnections.sort((a, b) => b.pairCount - a.pairCount);
                        
                        // Get the selected pair's shared connections
                        const selectedI = matrixHighlightedCell?.i;
                        const selectedJ = matrixHighlightedCell?.j;
                        const pairKey = selectedI !== undefined && selectedJ !== undefined 
                          ? `${Math.min(selectedI, selectedJ)}-${Math.max(selectedI, selectedJ)}`
                          : null;
                        const details = pairKey ? sharedConnectionDetails.get(pairKey) || sharedConnectionDetails.get(`${selectedJ}-${selectedI}`) : null;
                        const hasValidDetails = details && (details.locations.length > 0 || details.phones.length > 0 || details.others.length > 0);
                        const selectedLabel1 = selectedI !== undefined && nodeLabels[selectedI] ? nodeLabels[selectedI].label : '';
                        const selectedLabel2 = selectedJ !== undefined && nodeLabels[selectedJ] ? nodeLabels[selectedJ].label : '';
                        
                        return (
                          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                            {/* Matrix Section */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}>
                              {/* Column headers */}
                              <Box sx={{ display: 'flex', ml: `${labelWidth}px`, mb: 0.5 }}>
                                {nodeLabels.map((node, j) => (
                                  <Box
                                    key={`header-${j}`}
                                    sx={{
                                      flex: `0 0 ${cellSize}px`,
                                      minWidth: cellSize,
                                      height: headerHeight,
                                      position: 'relative'
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: '0.5rem',
                                        color: matrixHighlightedCell?.i === j || matrixHighlightedCell?.j === j 
                                          ? (isDarkMode ? '#00f0ff' : '#0097a7') 
                                          : (isDarkMode ? '#aaa' : '#555'),
                                        fontWeight: matrixHighlightedCell?.i === j || matrixHighlightedCell?.j === j ? 'bold' : 'normal',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: cellSize / 2,
                                        transform: 'rotate(-60deg)',
                                        transformOrigin: 'bottom left',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.15s'
                                      }}
                                    >
                                      {node.label}
                                    </Typography>
                          </Box>
                                ))}
                    </Box>
                              
                              {/* Matrix rows */}
                              {matrix.map((row, i) => (
                                <Box key={`row-${i}`} sx={{ display: 'flex', alignItems: 'center' }}>
                                  {/* Row label */}
                                  <Box sx={{ flex: `0 0 ${labelWidth}px`, pr: 1, textAlign: 'right' }}>
                                    <Typography sx={{
                                      fontSize: '0.55rem',
                                      color: matrixHighlightedCell?.i === i || matrixHighlightedCell?.j === i
                                        ? (isDarkMode ? '#00f0ff' : '#0097a7')
                                        : (isDarkMode ? '#888' : '#666'),
                                      fontWeight: matrixHighlightedCell?.i === i || matrixHighlightedCell?.j === i ? 'bold' : 'normal',
                                      transition: 'all 0.15s',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {nodeLabels[i].label}
                                    </Typography>
                  </Box>

                                  {/* Row cells */}
                                  {row.map((cell, j) => (
                                    <Tooltip 
                                      key={`cell-${i}-${j}`}
                                      title={getMatrixTooltip(i, j, cell)}
                                      arrow
                                      placement="top"
                                    >
                                      <Box
                                        onClick={(e) => { e.stopPropagation(); if (i !== j) handleCellClick(i, j); }}
                                        sx={{
                                          flex: `0 0 ${cellSize}px`,
                                          minWidth: cellSize,
                                          height: cellSize,
                                          bgcolor: getMatrixColor(cell),
                                          borderRadius: 0.5,
                                          cursor: i !== j ? 'pointer' : 'default',
                                          transition: 'all 0.15s',
                                          opacity: isHighlighted(i, j) ? 1 : (matrixHighlightedCell ? 0.4 : 1),
                                          outline: isSelectedCell(i, j) ? `2px solid ${isDarkMode ? '#00f0ff' : '#0097a7'}` : 'none',
                                          outlineOffset: '-1px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          '&:hover': i !== j ? { 
                                            transform: 'scale(1.15)', 
                                            zIndex: 10,
                                            boxShadow: `0 0 8px ${getMatrixColor(cell)}`
                                          } : {}
                                        }}
                                      >
                                        {cell.total > 0 && (
                                          <Typography sx={{ 
                                            fontSize: '0.5rem', 
                                            color: isDarkMode ? '#fff' : '#333',
                                            fontWeight: 'bold',
                                            textShadow: isDarkMode ? '0 0 2px rgba(0,0,0,0.5)' : 'none'
                                          }}>
                                            {cell.total}
                    </Typography>
                                        )}
                                      </Box>
                                    </Tooltip>
                                  ))}
                                </Box>
                              ))}
                              
                              {/* Legend */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, ml: `${labelWidth}px`, gap: 0.5 }}>
                                <Typography sx={{ fontSize: '0.55rem', color: isDarkMode ? '#666' : '#999' }}>Less</Typography>
                                {[0, 0.25, 0.5, 0.75, 1].map((intensity, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      width: 14,
                                      height: 14,
                                      borderRadius: 0.5,
                                      bgcolor: getMatrixColor({ total: intensity * maxCount, locations: 0, phones: 0, others: 0 }),
                                    }}
                                  />
                                ))}
                                <Typography sx={{ fontSize: '0.55rem', color: isDarkMode ? '#666' : '#999' }}>More</Typography>
                              </Box>
                            </Box>
                            
                            {/* Shared Connections Details List */}
                            <Box sx={{ 
                              flex: 1, 
                              minWidth: 200,
                              display: 'flex', 
                              flexDirection: 'column',
                              borderLeft: `1px solid ${isDarkMode ? '#2a2a4a' : '#e0e0e0'}`,
                              pl: 1.5
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', color: isDarkMode ? '#888' : '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ListAltIcon sx={{ fontSize: 14 }} />
                                  {hasValidDetails && selectedLabel1 && selectedLabel2
                                    ? `${selectedLabel1} ↔ ${selectedLabel2}`
                                    : 'All Shared Connections'
                                  }
                                </Typography>
                                <Chip 
                                  label={hasValidDetails && details
                                    ? `${details.locations.length + details.phones.length + details.others.length} items`
                                    : `${allSharedConnections.length} items`
                                  }
                                  size="small"
                                  sx={{ 
                                    height: 16, 
                                    fontSize: '0.55rem',
                                    bgcolor: isDarkMode ? '#2a2a4a' : '#e8e8e8'
                                  }}
                                />
                              </Box>
                              <Box sx={{ 
                                flex: 1, 
                                overflow: 'auto', 
                                maxHeight: headerHeight + (matrix.length * cellSize) - 20,
                                pr: 0.5,
                                '&::-webkit-scrollbar': {
                                  width: '4px',
                                },
                                '&::-webkit-scrollbar-track': {
                                  background: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  background: isDarkMode 
                                    ? 'linear-gradient(180deg, #00f0ff 0%, #0080ff 100%)'
                                    : 'linear-gradient(180deg, #0097a7 0%, #006070 100%)',
                                  borderRadius: '4px',
                                  '&:hover': {
                                    background: isDarkMode 
                                      ? 'linear-gradient(180deg, #40f8ff 0%, #40a0ff 100%)'
                                      : 'linear-gradient(180deg, #00b8c9 0%, #008090 100%)',
                                  }
                                },
                                scrollbarWidth: 'thin',
                                scrollbarColor: isDarkMode ? 'rgba(255, 255, 255, 0.3) transparent' : 'rgba(0, 0, 0, 0.25) transparent',
                              }}>
                                {hasValidDetails && details ? (
                                  // Show detailed list for selected pair
                                  <Stack spacing={0.75}>
                                    {/* Locations */}
                                    {details.locations.length > 0 && (
                                      <Box>
                                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 'bold', color: isDarkMode ? '#4caf50' : '#2e7d32', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <LocationOnIcon sx={{ fontSize: 12 }} /> Locations ({details.locations.length})
                                        </Typography>
                                        <Stack spacing={0.25}>
                                          {details.locations.map((loc, idx) => (
                                            <Box
                                              key={`loc-${idx}`}
                                              sx={{
                                                p: 0.5,
                                                borderRadius: 0.5,
                                                bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : 'rgba(76,175,80,0.08)',
                                                border: `1px solid ${isDarkMode ? 'rgba(76,175,80,0.3)' : 'rgba(76,175,80,0.2)'}`
                                              }}
                                            >
                                              <Typography sx={{ fontSize: '0.6rem', color: isDarkMode ? '#ccc' : '#333' }}>
                                                {getConnectionLabel(loc)}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Box>
                                    )}
                                    {/* Phones */}
                                    {details.phones.length > 0 && (
                                      <Box>
                                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 'bold', color: isDarkMode ? '#2196f3' : '#1565c0', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <PhoneIcon sx={{ fontSize: 12 }} /> Phone Numbers ({details.phones.length})
                                        </Typography>
                                        <Stack spacing={0.25}>
                                          {details.phones.map((ph, idx) => (
                                            <Box
                                              key={`ph-${idx}`}
                                              sx={{
                                                p: 0.5,
                                                borderRadius: 0.5,
                                                bgcolor: isDarkMode ? 'rgba(33,150,243,0.1)' : 'rgba(33,150,243,0.08)',
                                                border: `1px solid ${isDarkMode ? 'rgba(33,150,243,0.3)' : 'rgba(33,150,243,0.2)'}`
                                              }}
                                            >
                                              <Typography sx={{ fontSize: '0.6rem', color: isDarkMode ? '#ccc' : '#333' }}>
                                                {getConnectionLabel(ph)}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Box>
                                    )}
                                    {/* Others */}
                                    {details.others.length > 0 && (
                                      <Box>
                                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 'bold', color: isDarkMode ? '#ff9800' : '#e65100', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <LinkIcon sx={{ fontSize: 12 }} /> Other Connections ({details.others.length})
                                        </Typography>
                                        <Stack spacing={0.25}>
                                          {details.others.map((o, idx) => (
                                            <Box
                                              key={`other-${idx}`}
                                              sx={{
                                                p: 0.5,
                                                borderRadius: 0.5,
                                                bgcolor: isDarkMode ? 'rgba(255,152,0,0.1)' : 'rgba(255,152,0,0.08)',
                                                border: `1px solid ${isDarkMode ? 'rgba(255,152,0,0.3)' : 'rgba(255,152,0,0.2)'}`
                                              }}
                                            >
                                              <Typography sx={{ fontSize: '0.6rem', color: isDarkMode ? '#ccc' : '#333' }}>
                                                {getConnectionLabel(o)}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Box>
                                    )}
                                    {details.locations.length === 0 && details.phones.length === 0 && details.others.length === 0 && (
                                      <Typography sx={{ fontSize: '0.6rem', color: isDarkMode ? '#666' : '#999', textAlign: 'center', mt: 2 }}>
                                        No shared connections
                                      </Typography>
                                    )}
                                  </Stack>
                                ) : allSharedConnections.length > 0 ? (
                                  // Show all shared connections when no cell is selected
                                  <Stack spacing={0.25}>
                                    {allSharedConnections.slice(0, 50).map((conn, idx) => (
                                      <Box
                                        key={`all-${idx}`}
                                        sx={{
                                          p: 0.5,
                                          borderRadius: 0.5,
                                          bgcolor: conn.type === 'location' 
                                            ? (isDarkMode ? 'rgba(76,175,80,0.1)' : 'rgba(76,175,80,0.08)')
                                            : conn.type === 'phone'
                                              ? (isDarkMode ? 'rgba(33,150,243,0.1)' : 'rgba(33,150,243,0.08)')
                                              : (isDarkMode ? 'rgba(255,152,0,0.1)' : 'rgba(255,152,0,0.08)'),
                                          border: `1px solid ${
                                            conn.type === 'location' 
                                              ? (isDarkMode ? 'rgba(76,175,80,0.3)' : 'rgba(76,175,80,0.2)')
                                              : conn.type === 'phone'
                                                ? (isDarkMode ? 'rgba(33,150,243,0.3)' : 'rgba(33,150,243,0.2)')
                                                : (isDarkMode ? 'rgba(255,152,0,0.3)' : 'rgba(255,152,0,0.2)')
                                          }`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: 1
                                        }}
                                      >
                                        <Typography sx={{ 
                                          fontSize: '0.6rem', 
                                          color: isDarkMode ? '#ccc' : '#333',
                                          flex: 1,
                                          wordBreak: 'break-word',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5
                                        }}>
                                          {conn.type === 'location' ? <LocationOnIcon sx={{ fontSize: 12 }} /> : conn.type === 'phone' ? <PhoneIcon sx={{ fontSize: 12 }} /> : <LinkIcon sx={{ fontSize: 12 }} />}
                                          {conn.label}
                                        </Typography>
                                        <Chip 
                                          label={`${conn.pairCount} pairs`}
                                          size="small"
                                          sx={{ 
                                            height: 14, 
                                            fontSize: '0.5rem',
                                            bgcolor: isDarkMode ? '#2a2a4a' : '#e0e0e0',
                                            '& .MuiChip-label': { px: 0.5 }
                                          }}
                                        />
                                      </Box>
                                    ))}
                                    {allSharedConnections.length > 50 && (
                                      <Typography sx={{ fontSize: '0.55rem', color: isDarkMode ? '#666' : '#999', textAlign: 'center', mt: 0.5 }}>
                                        +{allSharedConnections.length - 50} more...
                                      </Typography>
                                    )}
                                  </Stack>
                                ) : (
                                  <Typography sx={{ fontSize: '0.6rem', color: isDarkMode ? '#666' : '#999', textAlign: 'center', mt: 2 }}>
                                    Click a cell to see shared connections
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>

                </Stack>
              </Box>
            )}

            {/* Timeline Heatmap Tab */}
            {activeTab === 2 && (
              <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <LinkAnalysisTimelineHeatmap
                  nodes={filteredNodes as any}
                  links={filteredLinks as any}
                  originalNodes={(originalNodes || nodes) as any}
                  originalLinks={(originalLinks || links) as any}
                  onDateFilter={onDateFilter}
                  onEntityFilter={onEntityFilter}
                  onConnectionTypeFilter={onConnectionTypeFilter}
                  selectedDates={selectedDates}
                  selectedConnectionTypes={selectedConnectionTypes}
                  height={600}
                />
              </Box>
            )}

            {/* Data Tabs Wrapper - for PDF export capture */}
            {activeTab >= 3 && (
              <Box id="network-right-panel" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Nodes Tab */}
            {activeTab === 3 && (
              <TableContainer sx={{ 
                flex: 1, 
                overflow: 'auto',
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.25)',
                  borderRadius: '3px',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: isDarkMode ? 'rgba(255, 255, 255, 0.3) transparent' : 'rgba(0, 0, 0, 0.25) transparent',
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={nodesSort.field === 'id'}
                          direction={nodesSort.order}
                          onClick={() => setNodesSort({ field: 'id', order: nodesSort.field === 'id' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Node ID</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={nodesSort.field === 'label'}
                          direction={nodesSort.order}
                          onClick={() => setNodesSort({ field: 'label', order: nodesSort.field === 'label' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Label</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={nodesSort.field === 'type'}
                          direction={nodesSort.order}
                          onClick={() => setNodesSort({ field: 'type', order: nodesSort.field === 'type' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Type</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={nodesSort.field === 'entity_type'}
                          direction={nodesSort.order}
                          onClick={() => setNodesSort({ field: 'entity_type', order: nodesSort.field === 'entity_type' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Entity Type</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell><strong>Location/Category</strong></TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={nodesSort.field === 'connections'}
                          direction={nodesSort.order}
                          onClick={() => setNodesSort({ field: 'connections', order: nodesSort.field === 'connections' && nodesSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Connections</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 48 }}>
                        <strong>View</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...filteredNodes]
                      .map((node) => ({
                        ...node,
                        connections: nodeDegrees.get(node.id) || 0
                      }))
                      .filter((node) => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        const nodeId = node.id.startsWith?.('phone:') ? node.id.replace('phone:', '') : node.id;
                        return nodeId.toLowerCase().includes(query) ||
                               (node.type || '').toLowerCase().includes(query) ||
                               (node.label || '').toLowerCase().includes(query) ||
                               (node.entity_type || '').toLowerCase().includes(query) ||
                               (node.place_name || '').toLowerCase().includes(query) ||
                               (node.place_type || '').toLowerCase().includes(query);
                      })
                      .sort((a: any, b: any) => {
                        const field = nodesSort.field;
                        const order = nodesSort.order === 'asc' ? 1 : -1;
                        if (field === 'connections') {
                          return (a.connections - b.connections) * order;
                        }
                        const aVal = String(a[field] || '');
                        const bVal = String(b[field] || '');
                        return aVal.localeCompare(bVal) * order;
                      })
                      .map((node: any) => {
                        const displayLabel = node.label || (node.id.startsWith?.('phone:') ? node.id.replace('phone:', '') : node.id);
                        return (
                          <TableRow 
                            key={node.id}
                            onClick={() => handleNodeRowClick(node.id)}
                            sx={{
                              bgcolor: selectedNodes.has(node.id) ? 'action.selected' : 'inherit',
                              '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' }
                            }}
                          >
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {node.id.startsWith?.('phone:') ? node.id.replace('phone:', '') : node.id}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: node.label ? 'bold' : 'normal' }}>
                              {displayLabel}
                            </TableCell>
                            <TableCell>
                              {node.type ? (
                                <Chip 
                                  label={node.type} 
                                  size="small" 
                                  sx={{ 
                                    bgcolor: typeColorMap[node.type] || '#9e9e9e',
                                    color: 'white',
                                    height: 20,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {node.entity_type ? (
                                <Chip 
                                  label={node.entity_type} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {node.place_name ? (
                                <Box>
                                  <Typography variant="caption" display="block" fontWeight="bold">
                                    {node.place_name}
                                  </Typography>
                                  {node.place_type && (
                                    <Chip 
                                      label={node.place_type} 
                                      size="small" 
                                      sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }}
                                    />
                                  )}
                                </Box>
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={node.connections} 
                                size="small" 
                                color={node.connections > 5 ? 'error' : node.connections > 2 ? 'warning' : 'default'}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ p: 0.5 }}>
                              <Tooltip title="View details &amp; filter graph by this node">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNodeRowClick(node.id);
                                  }}
                                  sx={{ p: 0.25 }}
                                >
                                  <OpenInNewIcon sx={{ fontSize: '0.95rem' }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* SMS Tab */}
            {activeTab === 4 && (
              <TableContainer sx={{ 
                flex: 1, 
                overflow: 'auto',
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.25)',
                  borderRadius: '3px',
                },
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={smsSort.field === 'source'}
                          direction={smsSort.order}
                          onClick={() => setSmsSort({ field: 'source', order: smsSort.field === 'source' && smsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Source</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={smsSort.field === 'target'}
                          direction={smsSort.order}
                          onClick={() => setSmsSort({ field: 'target', order: smsSort.field === 'target' && smsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Target</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell><strong>SMS #</strong></TableCell>
                      <TableCell><strong>Direction</strong></TableCell>
                      <TableCell sx={{ minWidth: 150 }}><strong>Message</strong></TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={smsSort.field === 'timestamp'}
                          direction={smsSort.order}
                          onClick={() => setSmsSort({ field: 'timestamp', order: smsSort.field === 'timestamp' && smsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Timestamp</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right"><strong>Weight</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLinks.filter(link => link.type === 'sms')
                      .map((link, index) => {
                        const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
                        const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
                        return { ...link, sourceId, targetId, index };
                      })
                      .filter((link) => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        const formatNodeId = (id: string) => id?.startsWith?.('phone:') ? id.replace('phone:', '') : id;
                        return formatNodeId(link.sourceId).toLowerCase().includes(query) ||
                               formatNodeId(link.targetId).toLowerCase().includes(query) ||
                               (link.sms_number || '').toLowerCase().includes(query) ||
                               (link.sms_message || '').toLowerCase().includes(query);
                      })
                      .sort((a: any, b: any) => {
                        const field = smsSort.field;
                        const order = smsSort.order === 'asc' ? 1 : -1;
                        if (field === 'timestamp') {
                          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                          return (aTime - bTime) * order;
                        }
                        if (field === 'source') return String(a.sourceId || '').localeCompare(String(b.sourceId || '')) * order;
                        if (field === 'target') return String(a.targetId || '').localeCompare(String(b.targetId || '')) * order;
                        return 0;
                      })
                      .map((link: any) => {
                        const formatNodeId = (id: string) => id?.startsWith?.('phone:') ? id.replace('phone:', '') : id;
                        const message = link.sms_message || '';
                        const truncatedMessage = message.length > 50 ? message.substring(0, 50) + '...' : message;
                        return (
                          <TableRow 
                            key={`sms-${link.sourceId}-${link.targetId}-${link.index}`}
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <TableCell 
                              sx={{ fontSize: '0.75rem', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                              onClick={() => handleTableNodeClick(link.sourceId)}
                            >
                              {formatNodeId(link.sourceId)}
                            </TableCell>
                            <TableCell 
                              sx={{ fontSize: '0.75rem', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                              onClick={() => handleTableNodeClick(link.targetId)}
                            >
                              {formatNodeId(link.targetId)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{link.sms_number || '-'}</TableCell>
                            <TableCell>
                              {link.comms_direction ? (
                                <Chip 
                                  label={link.comms_direction} 
                                  size="small"
                                  color={link.comms_direction === 'outgoing' ? 'primary' : 'secondary'}
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200 }}>
                              {message ? (
                                <Tooltip title={message} arrow placement="top">
                                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                                    {truncatedMessage}
                                  </Box>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{link.weight || 1}</TableCell>
                          </TableRow>
                        );
                      })}
                    {filteredLinks.filter(link => link.type === 'sms').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No SMS data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Calls Tab */}
            {activeTab === 5 && (
              <TableContainer sx={{ 
                flex: 1, 
                overflow: 'auto',
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.25)',
                  borderRadius: '3px',
                },
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={callsSort.field === 'source'}
                          direction={callsSort.order}
                          onClick={() => setCallsSort({ field: 'source', order: callsSort.field === 'source' && callsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Source</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={callsSort.field === 'target'}
                          direction={callsSort.order}
                          onClick={() => setCallsSort({ field: 'target', order: callsSort.field === 'target' && callsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Target</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell><strong>Call #</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={callsSort.field === 'call_logs_duration'}
                          direction={callsSort.order}
                          onClick={() => setCallsSort({ field: 'call_logs_duration', order: callsSort.field === 'call_logs_duration' && callsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Duration</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={callsSort.field === 'timestamp'}
                          direction={callsSort.order}
                          onClick={() => setCallsSort({ field: 'timestamp', order: callsSort.field === 'timestamp' && callsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Timestamp</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right"><strong>Weight</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLinks.filter(link => link.type === 'call')
                      .map((link, index) => {
                        const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
                        const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
                        return { ...link, sourceId, targetId, index };
                      })
                      .filter((link) => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        const formatNodeId = (id: string) => id?.startsWith?.('phone:') ? id.replace('phone:', '') : id;
                        return formatNodeId(link.sourceId).toLowerCase().includes(query) ||
                               formatNodeId(link.targetId).toLowerCase().includes(query) ||
                               (link.call_logs_number || '').toLowerCase().includes(query);
                      })
                      .sort((a: any, b: any) => {
                        const field = callsSort.field;
                        const order = callsSort.order === 'asc' ? 1 : -1;
                        if (field === 'timestamp') {
                          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                          return (aTime - bTime) * order;
                        }
                        if (field === 'call_logs_duration') return ((a.call_logs_duration || 0) - (b.call_logs_duration || 0)) * order;
                        if (field === 'source') return String(a.sourceId || '').localeCompare(String(b.sourceId || '')) * order;
                        if (field === 'target') return String(a.targetId || '').localeCompare(String(b.targetId || '')) * order;
                        return 0;
                      })
                      .map((link: any) => {
                        const formatNodeId = (id: string) => id?.startsWith?.('phone:') ? id.replace('phone:', '') : id;
                        return (
                          <TableRow 
                            key={`call-${link.sourceId}-${link.targetId}-${link.index}`}
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <TableCell 
                              sx={{ fontSize: '0.75rem', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                              onClick={() => handleTableNodeClick(link.sourceId)}
                            >
                              {formatNodeId(link.sourceId)}
                            </TableCell>
                            <TableCell 
                              sx={{ fontSize: '0.75rem', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                              onClick={() => handleTableNodeClick(link.targetId)}
                            >
                              {formatNodeId(link.targetId)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{link.call_logs_number || '-'}</TableCell>
                            <TableCell>
                              {link.comms_logs_type ? (
                                <Chip 
                                  label={link.comms_logs_type} 
                                  size="small"
                                  color={link.comms_logs_type === 'outgoing' ? 'primary' : link.comms_logs_type === 'incoming' ? 'secondary' : 'default'}
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {link.call_logs_duration ? `${link.call_logs_duration}s` : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{link.weight || 1}</TableCell>
                          </TableRow>
                        );
                      })}
                    {filteredLinks.filter(link => link.type === 'call').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No call data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Dwells Tab */}
            {activeTab === 6 && (
              <TableContainer sx={{ 
                flex: 1, 
                overflow: 'auto',
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.25)',
                  borderRadius: '3px',
                },
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={dwellsSort.field === 'source'}
                          direction={dwellsSort.order}
                          onClick={() => setDwellsSort({ field: 'source', order: dwellsSort.field === 'source' && dwellsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Source</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={dwellsSort.field === 'location'}
                          direction={dwellsSort.order}
                          onClick={() => setDwellsSort({ field: 'location', order: dwellsSort.field === 'location' && dwellsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Location Name</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Coordinates</strong></TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={dwellsSort.field === 'timestamp'}
                          direction={dwellsSort.order}
                          onClick={() => setDwellsSort({ field: 'timestamp', order: dwellsSort.field === 'timestamp' && dwellsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Timestamp</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right"><strong>Weight</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLinks.filter(link => link.type === 'dwell')
                      .map((link, index) => {
                        const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
                        const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
                        const locationNode = nodes.find(n => n.id === targetId);
                        return { 
                          ...link, 
                          sourceId, 
                          targetId, 
                          locationName: locationNode?.place_name || locationNode?.label || targetId,
                          category: locationNode?.place_type || '',
                          index 
                        };
                      })
                      .filter((link) => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        return (link.sourceId || '').toLowerCase().includes(query) ||
                               (link.targetId || '').toLowerCase().includes(query) ||
                               (link.locationName || '').toLowerCase().includes(query) ||
                               (link.category || '').toLowerCase().includes(query);
                      })
                      .sort((a: any, b: any) => {
                        const field = dwellsSort.field;
                        const order = dwellsSort.order === 'asc' ? 1 : -1;
                        if (field === 'timestamp') {
                          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                          return (aTime - bTime) * order;
                        }
                        if (field === 'source') return String(a.sourceId || '').localeCompare(String(b.sourceId || '')) * order;
                        if (field === 'location') return String(a.locationName || '').localeCompare(String(b.locationName || '')) * order;
                        return 0;
                      })
                      .map((link: any) => (
                        <TableRow 
                          key={`dwell-${link.sourceId}-${link.targetId}-${link.index}`}
                          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <TableCell 
                            sx={{ fontSize: '0.75rem', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                            onClick={() => handleTableNodeClick(link.sourceId)}
                          >
                            {link.sourceId}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                            {link.locationName}
                          </TableCell>
                          <TableCell>
                            {link.category ? (
                              <Chip 
                                label={link.category} 
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            ) : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>
                            {link.latitude && link.longitude ? 
                              `${link.latitude.toFixed(4)}, ${link.longitude.toFixed(4)}` : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{link.weight || 1}</TableCell>
                        </TableRow>
                      ))}
                    {filteredLinks.filter(link => link.type === 'dwell').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No dwell data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Apps Tab */}
            {activeTab === 7 && (
              <TableContainer sx={{ 
                flex: 1, 
                overflow: 'auto',
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.25)',
                  borderRadius: '3px',
                },
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={appsSort.field === 'source'}
                          direction={appsSort.order}
                          onClick={() => setAppsSort({ field: 'source', order: appsSort.field === 'source' && appsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Entity</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={appsSort.field === 'app'}
                          direction={appsSort.order}
                          onClick={() => setAppsSort({ field: 'app', order: appsSort.field === 'app' && appsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>App Name</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell><strong>Package</strong></TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={appsSort.field === 'weight'}
                          direction={appsSort.order}
                          onClick={() => setAppsSort({ field: 'weight', order: appsSort.field === 'weight' && appsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Use Count</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={appsSort.field === 'total_time'}
                          direction={appsSort.order}
                          onClick={() => setAppsSort({ field: 'total_time', order: appsSort.field === 'total_time' && appsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Total Time</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={appsSort.field === 'timestamp'}
                          direction={appsSort.order}
                          onClick={() => setAppsSort({ field: 'timestamp', order: appsSort.field === 'timestamp' && appsSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Last Used</strong>
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLinks.filter(link => link.type === 'app')
                      .map((link, index) => {
                        const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
                        const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
                        const appNode = nodes.find(n => n.id === targetId);
                        return { 
                          ...link, 
                          sourceId, 
                          targetId, 
                          appName: appNode?.app_name || appNode?.label || targetId?.replace('app:', ''),
                          packageName: link.package_name || targetId?.replace('app:', '') || '',
                          index 
                        };
                      })
                      .filter((link) => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        return (link.sourceId || '').toLowerCase().includes(query) ||
                               (link.appName || '').toLowerCase().includes(query) ||
                               (link.packageName || '').toLowerCase().includes(query);
                      })
                      .sort((a: any, b: any) => {
                        const field = appsSort.field;
                        const order = appsSort.order === 'asc' ? 1 : -1;
                        if (field === 'timestamp') {
                          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                          return (aTime - bTime) * order;
                        }
                        if (field === 'source') return String(a.sourceId || '').localeCompare(String(b.sourceId || '')) * order;
                        if (field === 'app') return String(a.appName || '').localeCompare(String(b.appName || '')) * order;
                        if (field === 'weight') return ((a.weight || 0) - (b.weight || 0)) * order;
                        if (field === 'total_time') return ((a.total_time || 0) - (b.total_time || 0)) * order;
                        return 0;
                      })
                      .map((link: any) => (
                        <TableRow 
                          key={`app-${link.sourceId}-${link.targetId}-${link.index}`}
                          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <TableCell 
                            sx={{ fontSize: '0.75rem', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                            onClick={() => handleTableNodeClick(link.sourceId)}
                          >
                            {link.sourceId}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                            {link.appName}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {link.packageName}
                              {getAppDescription(link.packageName) && (
                                <Tooltip 
                                  title={
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        {link.packageName}
                                      </Typography>
                                      <Typography variant="body2">
                                        {getAppDescription(link.packageName)}
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="right"
                                >
                                  <InfoIcon sx={{ fontSize: '0.9rem', color: 'info.main', cursor: 'help' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{link.weight || 1}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                            {link.total_time ? `${Math.round(link.total_time / 60)}m` : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    {filteredLinks.filter(link => link.type === 'app').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No app usage data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Browsing Tab */}
            {activeTab === 8 && (
              <TableContainer sx={{
                flex: 1,
                overflow: 'auto',
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: isDarkMode
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.25)',
                  borderRadius: '3px',
                },
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={browsingSort.field === 'source'}
                          direction={browsingSort.order}
                          onClick={() => setBrowsingSort({ field: 'source', order: browsingSort.field === 'source' && browsingSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Entity</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={browsingSort.field === 'domain'}
                          direction={browsingSort.order}
                          onClick={() => setBrowsingSort({ field: 'domain', order: browsingSort.field === 'domain' && browsingSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Domain</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}><strong>URL</strong></TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={browsingSort.field === 'weight'}
                          direction={browsingSort.order}
                          onClick={() => setBrowsingSort({ field: 'weight', order: browsingSort.field === 'weight' && browsingSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Visits</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={browsingSort.field === 'timestamp'}
                          direction={browsingSort.order}
                          onClick={() => setBrowsingSort({ field: 'timestamp', order: browsingSort.field === 'timestamp' && browsingSort.order === 'asc' ? 'desc' : 'asc' })}
                        >
                          <strong>Last Visited</strong>
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLinks.filter(link => link.type === 'browsing')
                      .map((link, index) => {
                        const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node)?.id;
                        const targetId = typeof link.target === 'string' ? link.target : (link.target as Node)?.id;
                        const domainNode = nodes.find(n => n.id === targetId);
                        const domain = domainNode?.domain || domainNode?.label || targetId?.replace('domain:', '') || '';
                        return { ...link, sourceId, targetId, domain, url: (link as any).url || '', index };
                      })
                      .filter((link) => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        return (link.sourceId || '').toLowerCase().includes(query) ||
                               (link.domain || '').toLowerCase().includes(query) ||
                               (link.url || '').toLowerCase().includes(query);
                      })
                      .sort((a: any, b: any) => {
                        const field = browsingSort.field;
                        const order = browsingSort.order === 'asc' ? 1 : -1;
                        if (field === 'timestamp') {
                          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                          return (aTime - bTime) * order;
                        }
                        if (field === 'source') return String(a.sourceId || '').localeCompare(String(b.sourceId || '')) * order;
                        if (field === 'domain') return String(a.domain || '').localeCompare(String(b.domain || '')) * order;
                        if (field === 'weight') return ((a.weight || 0) - (b.weight || 0)) * order;
                        return 0;
                      })
                      .map((link: any) => {
                        const url = link.url || '';
                        const truncatedUrl = url.length > 60 ? url.substring(0, 60) + '...' : url;
                        return (
                          <TableRow
                            key={`browsing-${link.sourceId}-${link.targetId}-${link.index}`}
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <TableCell
                              sx={{ fontSize: '0.75rem', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                              onClick={() => handleTableNodeClick(link.sourceId)}
                            >
                              {link.sourceId}
                            </TableCell>
                            <TableCell
                              sx={{ fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                              onClick={() => handleTableNodeClick(link.targetId)}
                            >
                              {link.domain || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.7rem', maxWidth: 280 }}>
                              {url ? (
                                <Tooltip title={url} arrow placement="top">
                                  <Box
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      color: 'primary.main',
                                      textDecoration: 'none',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '100%',
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{truncatedUrl}</span>
                                    <OpenInNewIcon sx={{ fontSize: '0.85rem', flexShrink: 0 }} />
                                  </Box>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{link.weight || 1}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {parseBackendDateTime(link.timestamp)?.toLocaleString() ?? '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {filteredLinks.filter(link => link.type === 'browsing').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No browsing history available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ────────────────────────────────────────────────────────────
                Search Tab — graph-wide search-term extraction
                Aggregates every browsing URL through the search-engine
                detector (Google, Bing, DuckDuckGo, YouTube, Amazon, eBay,
                Reddit, X, etc.) and shows analyst-useful aggregates: who
                searched what, how often, on which domain, when last.
            ──────────────────────────────────────────────────────────── */}
            {activeTab === 9 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  // Allow the entire Search tab to scroll as a single column
                  // when the combined controls (aggregates, engines,
                  // commonalities, keywords, filter) plus the list don't fit
                  // in the available height. Without this, the flex:1 list
                  // below collapses to zero height on shorter viewports and
                  // the user has nothing to scroll. The list itself keeps
                  // its own overflow:auto with a min-height so it never
                  // fully disappears.
                  overflow: 'auto',
                }}
              >
                {searchExtractions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary', flex: 1 }}>
                    <SearchTermIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                    <Typography variant="body2">No search queries detected in the current view.</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, px: 4 }}>
                      Searches are extracted automatically when browsing URLs match a known engine
                      (Google, Bing, DuckDuckGo, YouTube, Amazon, eBay, Reddit, X, etc.). Try
                      widening your filters or date range.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Aggregate metric strip — replaces four ~80px-tall
                        cards with a single ~28px row. Same four numbers,
                        same order. Each metric reads `<bold value> <label>`
                        inline; thin vertical dividers separate them. Saves
                        ~60px of header height and keeps the data scannable
                        left to right. */}
                    <Box
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderBottom: `1px solid ${
                          isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                      }}
                    >
                      {[
                        {
                          label: 'terms',
                          value: searchAggregates.uniqueTerms.toLocaleString(),
                        },
                        {
                          label: 'searches',
                          value: searchAggregates.totalSearches.toLocaleString(),
                        },
                        {
                          label: 'entities',
                          value: searchAggregates.uniqueEntities.toLocaleString(),
                        },
                        {
                          label: 'last',
                          value: searchAggregates.lastSearch
                            ? searchAggregates.lastSearch.toLocaleDateString()
                            : '—',
                        },
                      ].map(({ label, value }, i) => (
                        <React.Fragment key={label}>
                          {i > 0 && (
                            <Box
                              sx={{
                                width: '1px',
                                height: 14,
                                bgcolor: isDarkMode
                                  ? 'rgba(255,255,255,0.12)'
                                  : 'rgba(0,0,0,0.12)',
                              }}
                            />
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ fontSize: '0.85rem', lineHeight: 1 }}
                            >
                              {value}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.65rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.3,
                              }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>

                    {/* Engine chips (click to filter) */}
                    {searchAggregates.engines.length > 0 && (
                      <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>
                          Engines (click to filter)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label="All"
                            size="small"
                            onClick={() => setSearchEngineFilter(null)}
                            variant={searchEngineFilter === null ? 'filled' : 'outlined'}
                            color={searchEngineFilter === null ? 'primary' : 'default'}
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                          {searchAggregates.engines.map((e) => {
                            const selected = searchEngineFilter === e.name;
                            return (
                              <Chip
                                key={e.name}
                                label={`${e.name} · ${e.count}`}
                                size="small"
                                onClick={() => setSearchEngineFilter(selected ? null : e.name)}
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  bgcolor: selected ? e.color : `${e.color}22`,
                                  color: selected ? '#fff' : e.color,
                                  border: `1px solid ${e.color}${selected ? '' : '55'}`,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: selected ? e.color : `${e.color}33` },
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* ──────────────────────────────────────────────────────
                        Commonalities Graph — bipartite (entity ↔ term) view.
                        Compact inline version: top 6 × 6 keeps the search
                        list reachable without scrolling controls. Analysts
                        who need the full picture click the pop-out button
                        to open a larger floating dialog.
                    ────────────────────────────────────────────────────── */}
                    {commonalityGraph.entities.length > 1 && commonalityGraph.terms.length > 0 && (() => {
                      const INLINE_ENTITY_LIMIT = 6;
                      const INLINE_TERM_LIMIT = 6;
                      const inlineEntityCount = Math.min(
                        commonalityGraph.entities.length,
                        INLINE_ENTITY_LIMIT,
                      );
                      const inlineTermCount = Math.min(
                        commonalityGraph.terms.length,
                        INLINE_TERM_LIMIT,
                      );
                      // Overflow counts reference the FILTERED universe so
                      // the "+N more" hint reflects what's hidden within
                      // the user's current scope, not the global dataset.
                      const overflowEntities = Math.max(
                        0,
                        commonalityGraph.totalEntities - inlineEntityCount,
                      );
                      const overflowTerms = Math.max(
                        0,
                        commonalityGraph.totalTerms - inlineTermCount,
                      );
                      return (
                        <Box
                          sx={{
                            px: 2,
                            py: 1,
                            borderBottom: `1px solid ${
                              isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                            }`,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: commonalityCollapsed ? 0 : 0.5,
                              gap: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontSize: '0.6rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Search Commonalities
                              </Typography>
                              {!commonalityCollapsed && (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ fontSize: '0.6rem', ml: 0.5 }}
                                >
                                  (hover to trace · click to filter)
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.6rem' }}
                              >
                                {inlineEntityCount} of {commonalityGraph.totalEntities} ·{' '}
                                {inlineTermCount} of {commonalityGraph.totalTerms}
                              </Typography>
                              <Tooltip title="Open larger floating view">
                                <IconButton
                                  size="small"
                                  onClick={() => setCommonalityPopoutOpen(true)}
                                  sx={{ p: 0.25 }}
                                >
                                  <OpenInFullIcon sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  commonalityCollapsed
                                    ? 'Show inline commonalities'
                                    : 'Hide inline commonalities (keeps pop-out)'
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => setCommonalityCollapsed((v) => !v)}
                                  sx={{ p: 0.25 }}
                                >
                                  {commonalityCollapsed ? (
                                    <OpenInFullIcon sx={{ fontSize: '0.85rem' }} />
                                  ) : (
                                    <CloseFullscreenIcon sx={{ fontSize: '0.85rem' }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          {!commonalityCollapsed && (
                            <>
                              {renderCommonalityGraphBody({
                                entityLimit: INLINE_ENTITY_LIMIT,
                                termLimit: INLINE_TERM_LIMIT,
                                rowHeight: 22,
                              })}
                              {(overflowEntities > 0 || overflowTerms > 0) && (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem' }}
                                >
                                  {overflowEntities > 0
                                    ? `+${overflowEntities} more entities`
                                    : null}
                                  {overflowEntities > 0 && overflowTerms > 0 && ' · '}
                                  {overflowTerms > 0 ? `+${overflowTerms} more terms` : null}
                                  {' — open the floating view '}
                                  <Box
                                    component="span"
                                    onClick={() => setCommonalityPopoutOpen(true)}
                                    sx={{
                                      color: 'primary.main',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    here
                                  </Box>
                                  {' for the full picture'}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Top keywords — proportional strip chart.
                        One horizontal bar, segmented by keyword. Each segment's
                        width is proportional to its occurrence count so the
                        analyst sees the relative dominance of each term at a
                        glance instead of scanning a list of pills.
                        Clicking a segment filters the search list to terms
                        containing that keyword (same UX as the old chips).
                    */}
                    {topSearchKeywords.length > 0 && (() => {
                      const totalKeywordCount = topSearchKeywords.reduce(
                        (sum, k) => sum + k.count,
                        0,
                      );
                      // Soft sequential palette: the more frequent the keyword,
                      // the more opaque its segment. Anchors on MUI primary so
                      // the chart stays cohesive with the rest of the panel.
                      const segmentColor = (idx: number) => {
                        const t = topSearchKeywords.length > 1
                          ? idx / (topSearchKeywords.length - 1)
                          : 0;
                        const alpha = (1 - t * 0.7).toFixed(2); // 1.0 → 0.30
                        return isDarkMode
                          ? `rgba(100, 181, 246, ${alpha})`
                          : `rgba(25, 118, 210, ${alpha})`;
                      };
                      return (
                        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                              Top Keywords (click a segment to filter)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                              {topSearchKeywords.length} keywords · {totalKeywordCount.toLocaleString()} occurrences
                            </Typography>
                          </Box>

                          {/* Strip chart */}
                          <Box
                            sx={{
                              display: 'flex',
                              width: '100%',
                              height: 26,
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                              bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                            }}
                          >
                            {topSearchKeywords.map((k, idx) => {
                              const pct = totalKeywordCount > 0 ? (k.count / totalKeywordCount) * 100 : 0;
                              const isActive = searchTermFilter.trim().toLowerCase() === k.word;
                              // Show inline label only if the segment is wide
                              // enough to hold the word; ~6 % is the practical
                              // floor for a short word like "iran".
                              const showInlineLabel = pct >= 6;
                              return (
                                <Tooltip
                                  key={k.word}
                                  arrow
                                  title={`${k.word} — ${k.count.toLocaleString()} occurrence${k.count !== 1 ? 's' : ''} (${pct.toFixed(1)}%)`}
                                >
                                  <Box
                                    onClick={() => handleKeywordClick(k.word)}
                                    sx={{
                                      width: `${pct}%`,
                                      minWidth: 4,
                                      bgcolor: segmentColor(idx),
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      borderRight: idx < topSearchKeywords.length - 1
                                        ? `1px solid ${isDarkMode ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.6)'}`
                                        : 'none',
                                      outline: isActive
                                        ? `2px solid ${isDarkMode ? '#fff' : '#000'}`
                                        : 'none',
                                      outlineOffset: '-2px',
                                      transition: 'filter 0.15s',
                                      '&:hover': { filter: 'brightness(1.15)' },
                                      px: 0.5,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {showInlineLabel ? `${k.word} · ${k.count}` : ''}
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Box>

                          {/* Compact legend for the segments whose label was
                              suppressed because they'd be too narrow. Keeps the
                              chart scannable without forcing every analyst to
                              hover each tiny segment. */}
                          {(() => {
                            const hidden = topSearchKeywords.filter((k) => {
                              const pct = totalKeywordCount > 0 ? (k.count / totalKeywordCount) * 100 : 0;
                              return pct < 6;
                            });
                            if (hidden.length === 0) return null;
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                                {hidden.map((k) => {
                                  const isActive =
                                    searchTermFilter.trim().toLowerCase() === k.word;
                                  return (
                                    <Chip
                                      key={k.word}
                                      label={`${k.word} · ${k.count}`}
                                      size="small"
                                      variant={isActive ? 'filled' : 'outlined'}
                                      color={isActive ? 'primary' : 'default'}
                                      onClick={() => handleKeywordClick(k.word)}
                                      sx={{
                                        height: 18,
                                        fontSize: '0.6rem',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            );
                          })()}
                        </Box>
                      );
                    })()}

                    {/* Filter + sort controls */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        px: 2, py: 1,
                        borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                      }}
                    >
                      <TextField
                        size="small"
                        placeholder="Filter by term, engine, entity or domain..."
                        value={searchTermFilter}
                        onChange={(e) => setSearchTermFilter(e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Sort by</InputLabel>
                        <Select
                          value={searchSort}
                          label="Sort by"
                          onChange={(e) => setSearchSort(e.target.value as typeof searchSort)}
                        >
                          <MenuItem value="count">Most Searched</MenuItem>
                          <MenuItem value="recent">Most Recent</MenuItem>
                          <MenuItem value="alpha">A–Z</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Result count + active-filter summary so analysts can
                        always tell at a glance whether the list is scoped.
                        Export now lives at the right edge of this row so it
                        sits next to the count it acts on — saves the ~50px
                        the dedicated Export row used to occupy above the
                        Search Commonalities header. */}
                    <Box
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Showing {visibleSearches.length.toLocaleString()} of {searchExtractions.length.toLocaleString()} unique searches
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          alignItems: 'center',
                          ml: 'auto',
                        }}
                      >
                        {(searchEngineFilter || searchEntityFilter || searchTermFilter) && (
                          <>
                            {searchEngineFilter && (
                              <Chip
                                size="small"
                                label={`engine: ${searchEngineFilter}`}
                                onDelete={() => setSearchEngineFilter(null)}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            {searchEntityFilter && (
                              <Chip
                                size="small"
                                label={`entity: ${
                                  searchEntityFilter.startsWith('phone:')
                                    ? searchEntityFilter.replace('phone:', '')
                                    : searchEntityFilter
                                }`}
                                onDelete={() => {
                                  // Use the cross-filter handler so the network
                                  // graph un-focuses alongside the search list.
                                  handleCommonalityEntityClick(searchEntityFilter);
                                }}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            {searchTermFilter && (
                              <Chip
                                size="small"
                                label={`text: "${searchTermFilter}"`}
                                onDelete={() => {
                                  // Mirror the cross-filter clear so the main
                                  // graph selection state is reset too.
                                  setSearchTermFilter('');
                                  setSelectedNodes(new Set());
                                  setFocusedNodeId(null);
                                  if (onSelectionChange) onSelectionChange([]);
                                }}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            <Button
                              size="small"
                              onClick={() => {
                                setSearchEngineFilter(null);
                                setSearchEntityFilter(null);
                                setSearchTermFilter('');
                                setSelectedNodes(new Set());
                                setFocusedNodeId(null);
                                if (onSelectionChange) onSelectionChange([]);
                              }}
                              sx={{ fontSize: '0.65rem', minWidth: 0, py: 0, px: 0.75 }}
                            >
                              Clear all
                            </Button>
                          </>
                        )}
                        {/* Export — inlined here so it's always visible
                            without needing a dedicated row. Tooltip carries
                            the live filtered count so the affordance is at
                            least as clear as the old labeled button. */}
                        <Tooltip
                          title={`Export ${visibleSearches.length.toLocaleString()} filtered search${
                            visibleSearches.length === 1 ? '' : 'es'
                          } as CSV`}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={exportSearches}
                              disabled={visibleSearches.length === 0}
                              aria-label="Export filtered searches as CSV"
                              sx={{ p: 0.25 }}
                            >
                              <DownloadIcon sx={{ fontSize: '0.95rem' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Search list */}
                    <Box sx={{
                      flex: 1,
                      // Guarantee the list always has room even when the
                      // controls above are tall; otherwise flex:1 inside an
                      // auto-overflow column can render at zero height and
                      // hide the actual searches.
                      minHeight: 280,
                      overflow: 'auto',
                      px: 1.5,
                      py: 1,
                      '&::-webkit-scrollbar': { width: '6px' },
                      '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)',
                        borderRadius: '3px',
                      },
                    }}>
                      {visibleSearches.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                          <Typography variant="body2">No searches match the current filter</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                            Engine: <strong>{searchEngineFilter ?? '(any)'}</strong>
                            {' · '}
                            Entity: <strong>{searchEntityFilter ?? '(any)'}</strong>
                            {searchTermFilter ? <> · Text: <strong>&ldquo;{searchTermFilter}&rdquo;</strong></> : null}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ mt: 1.5 }}
                            onClick={() => {
                              setSearchTermFilter('');
                              setSearchEngineFilter(null);
                              setSearchEntityFilter(null);
                            }}
                          >
                            Clear filters
                          </Button>
                        </Box>
                      ) : (
                        <Stack spacing={0.75}>
                          {visibleSearches.map((s, idx) => {
                            // Deduplicate source occurrences by (entity, domain)
                            // so a single entity that searched this term 12
                            // times shows up as ONE row with "×12" instead of
                            // 12 identical chips. Sorted by count desc so the
                            // top searchers appear first.
                            const grouped = new Map<
                              string,
                              { sourceId: string; domain: string; count: number; lastVisit: number }
                            >();
                            for (const src of s.sources) {
                              const key = `${src.sourceId}|${src.domain}`;
                              const ex = grouped.get(key);
                              if (ex) {
                                ex.count += 1;
                                if (src.timestamp > ex.lastVisit) ex.lastVisit = src.timestamp;
                              } else {
                                grouped.set(key, {
                                  sourceId: src.sourceId,
                                  domain: src.domain,
                                  count: 1,
                                  lastVisit: src.timestamp,
                                });
                              }
                            }
                            // Total grouped (unfiltered) — used for the row's
                            // metadata stats so the "X entities" count keeps
                            // showing the real total even while we collapse the
                            // displayed list to a single entity.
                            const allGroupedSources = Array.from(grouped.values()).sort(
                              (a, b) => b.count - a.count,
                            );
                            // If an entity filter is active, collapse the row's
                            // "searched by" list to just that entity so the
                            // analyst sees a clean per-entity drill-in without
                            // visual noise from other entities.
                            let groupedSources = allGroupedSources;
                            if (searchEntityFilter) {
                              groupedSources = allGroupedSources.filter(
                                (g) => g.sourceId === searchEntityFilter,
                              );
                            }
                            const visibleSources = groupedSources.slice(0, 4);
                            const overflowSources = groupedSources.slice(4);
                            // When an entity filter is active, find that
                            // entity's own contribution so we can annotate the
                            // header (e.g. "8 searches · 5 entities · 2 by Student-37").
                            const filteredEntityCount = searchEntityFilter
                              ? allGroupedSources.find((g) => g.sourceId === searchEntityFilter)?.count
                              : undefined;

                            // Inline-metadata "dot" separator used between
                            // stat fields. Keeps the row scannable without
                            // resorting to a chip per value.
                            const Dot = (
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  mx: 0.75,
                                  color: 'text.disabled',
                                  fontSize: '0.7rem',
                                }}
                              >
                                ·
                              </Box>
                            );

                            return (
                              <Paper
                                key={`${s.engine}-${s.term}-${idx}`}
                                elevation={0}
                                sx={{
                                  p: 1.25,
                                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.012)',
                                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                                  borderLeft: `3px solid ${s.engineColor}`,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1.25,
                                  transition: 'background-color 0.15s',
                                  '&:hover': {
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.025)',
                                  },
                                }}
                              >
                                <SearchTermIcon
                                  sx={{
                                    fontSize: 18,
                                    mt: 0.25,
                                    color: s.engineColor,
                                    flexShrink: 0,
                                  }}
                                />

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  {/* Row 1: search term (primary, click to
                                      filter), engine label (subtle, click to
                                      filter). Clicking either toggles the
                                      matching filter so a second click clears. */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'baseline',
                                      gap: 1,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    {(() => {
                                      const isTermActive =
                                        searchTermFilter.trim().toLowerCase() ===
                                        s.term.toLowerCase();
                                      return (
                                        <Tooltip
                                          arrow
                                          placement="top-start"
                                          title={
                                            isTermActive
                                              ? 'Click to clear the term filter'
                                              : `Click to filter everything by "${s.term}" — also highlights the entities who searched it in the network graph`
                                          }
                                        >
                                          <Typography
                                            variant="body2"
                                            onClick={() =>
                                              // Use the cross-filter handler so the
                                              // main network graph also highlights
                                              // every entity that searched this term.
                                              handleCommonalityTermClick(s.term, s.sources)
                                            }
                                            sx={{
                                              fontSize: '0.92rem',
                                              fontWeight: 600,
                                              wordBreak: 'break-word',
                                              lineHeight: 1.3,
                                              cursor: 'pointer',
                                              color: isTermActive ? 'primary.main' : 'text.primary',
                                              textDecoration: isTermActive ? 'underline' : 'none',
                                              '&:hover': { color: 'primary.main' },
                                            }}
                                          >
                                            {s.term}
                                          </Typography>
                                        </Tooltip>
                                      );
                                    })()}
                                    {(() => {
                                      const isEngineActive = searchEngineFilter === s.engine;
                                      return (
                                        <Tooltip
                                          arrow
                                          placement="top-start"
                                          title={
                                            isEngineActive
                                              ? `Click to clear the ${s.engine} filter`
                                              : `Click to filter to ${s.engine} only`
                                          }
                                        >
                                          <Typography
                                            variant="caption"
                                            onClick={() =>
                                              setSearchEngineFilter(isEngineActive ? null : s.engine)
                                            }
                                            sx={{
                                              fontSize: '0.65rem',
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              textTransform: 'uppercase',
                                              color: s.engineColor,
                                              cursor: 'pointer',
                                              opacity: isEngineActive ? 1 : 0.85,
                                              textDecoration: isEngineActive ? 'underline' : 'none',
                                              '&:hover': { opacity: 1, textDecoration: 'underline' },
                                            }}
                                          >
                                            {s.engine}
                                          </Typography>
                                        </Tooltip>
                                      );
                                    })()}
                                  </Box>

                                  {/* Row 2: structured stats as inline metadata */}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: 'block',
                                      mt: 0.35,
                                      fontSize: '0.72rem',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                      {s.count.toLocaleString()}
                                    </Box>{' '}
                                    search{s.count !== 1 ? 'es' : ''}
                                    {Dot}
                                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                      {allGroupedSources.length.toLocaleString()}
                                    </Box>{' '}
                                    {allGroupedSources.length === 1 ? 'entity' : 'entities'}
                                    {filteredEntityCount !== undefined && (
                                      <>
                                        {Dot}
                                        <Box
                                          component="span"
                                          sx={{
                                            color: 'primary.main',
                                            fontWeight: 700,
                                          }}
                                        >
                                          {filteredEntityCount.toLocaleString()} by{' '}
                                          {searchEntityFilter?.startsWith('phone:')
                                            ? searchEntityFilter.replace('phone:', '')
                                            : searchEntityFilter}
                                        </Box>
                                      </>
                                    )}
                                    {s.lastVisit > 0 && (
                                      <>
                                        {Dot}
                                        <Tooltip title={new Date(s.lastVisit).toLocaleString()}>
                                          <Box component="span" sx={{ cursor: 'help' }}>
                                            <ScheduleIcon
                                              sx={{
                                                fontSize: '0.8rem',
                                                verticalAlign: '-0.15em',
                                                mr: 0.35,
                                                opacity: 0.7,
                                              }}
                                            />
                                            last {new Date(s.lastVisit).toLocaleDateString()}
                                          </Box>
                                        </Tooltip>
                                      </>
                                    )}
                                  </Typography>

                                  {/* Row 3: searched-by table.
                                      Compact entity-keyed rows: "Student-36 ×12 · google.com"
                                      Click an entity to highlight in the graph. */}
                                  {visibleSources.length > 0 && (
                                    <Box
                                      sx={{
                                        mt: 0.6,
                                        pt: 0.5,
                                        borderTop: `1px dashed ${isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`,
                                        display: 'grid',
                                        gridTemplateColumns: 'auto 1fr',
                                        columnGap: 1,
                                        rowGap: 0.15,
                                        alignItems: 'baseline',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          gridColumn: '1 / 2',
                                          gridRow: '1 / -1',
                                          fontSize: '0.6rem',
                                          textTransform: 'uppercase',
                                          letterSpacing: 0.4,
                                          color: 'text.disabled',
                                          fontWeight: 600,
                                          pt: 0.25,
                                        }}
                                      >
                                        Searched by
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        {visibleSources.map((src) => {
                                          const displayId = src.sourceId.startsWith('phone:')
                                            ? src.sourceId.replace('phone:', '')
                                            : src.sourceId;
                                          const isActive = searchEntityFilter === src.sourceId;
                                          return (
                                            <Tooltip
                                              key={`${src.sourceId}|${src.domain}`}
                                              arrow
                                              placement="left"
                                              title={
                                                isActive
                                                  ? `Click to clear the ${displayId} filter`
                                                  : `Click to filter all searches by ${displayId}`
                                              }
                                            >
                                            <Box
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Toggle behavior: clicking the
                                                // active entity clears the
                                                // filter; clicking a different
                                                // entity pivots to it AND
                                                // focuses the network graph on
                                                // the entity (same handler as
                                                // the Commonalities graph).
                                                handleCommonalityEntityClick(src.sourceId);
                                              }}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: 0.75,
                                                py: 0.15,
                                                cursor: 'pointer',
                                                borderRadius: 0.5,
                                                px: 0.5,
                                                mx: -0.5,
                                                bgcolor: isActive
                                                  ? (isDarkMode ? 'rgba(100,181,246,0.15)' : 'rgba(25,118,210,0.1)')
                                                  : 'transparent',
                                                '&:hover': {
                                                  bgcolor: isActive
                                                    ? (isDarkMode ? 'rgba(100,181,246,0.22)' : 'rgba(25,118,210,0.15)')
                                                    : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
                                                },
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: '0.78rem',
                                                  fontWeight: 500,
                                                  fontFamily: 'monospace',
                                                  color: 'text.primary',
                                                  flexShrink: 0,
                                                }}
                                              >
                                                {displayId}
                                              </Typography>
                                              {src.count > 1 && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.68rem',
                                                    fontWeight: 700,
                                                    color: 'text.secondary',
                                                    fontFamily: 'monospace',
                                                  }}
                                                >
                                                  ×{src.count}
                                                </Typography>
                                              )}
                                              {src.domain && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    minWidth: 0,
                                                  }}
                                                >
                                                  · {src.domain}
                                                </Typography>
                                              )}
                                            </Box>
                                            </Tooltip>
                                          );
                                        })}
                                        {overflowSources.length > 0 && (
                                          <Tooltip
                                            arrow
                                            title={
                                              <Box sx={{ maxWidth: 280 }}>
                                                {overflowSources.map((src) => {
                                                  const did = src.sourceId.startsWith('phone:')
                                                    ? src.sourceId.replace('phone:', '')
                                                    : src.sourceId;
                                                  return (
                                                    <Box key={`${src.sourceId}|${src.domain}`} sx={{ fontSize: '0.75rem', py: 0.15 }}>
                                                      {did} {src.count > 1 ? `×${src.count}` : ''}
                                                      {src.domain ? ` · ${src.domain}` : ''}
                                                    </Box>
                                                  );
                                                })}
                                              </Box>
                                            }
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: '0.7rem',
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                cursor: 'help',
                                                mt: 0.25,
                                                px: 0.5,
                                                mx: -0.5,
                                                width: 'fit-content',
                                              }}
                                            >
                                              + {overflowSources.length} more
                                              {overflowSources.reduce((sum, src) => sum + src.count, 0) >
                                                overflowSources.length && (
                                                <>
                                                  {' '}
                                                  ({overflowSources.reduce((sum, src) => sum + src.count, 0)} searches)
                                                </>
                                              )}
                                            </Typography>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                  <Tooltip title="Copy search term">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard?.writeText(s.term).catch(() => {});
                                      }}
                                    >
                                      <CopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Open original search URL">
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={s.url}
                                      target="_blank"
                                      rel="noreferrer"
                                  >
                                    <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Controls Tab */}
            {activeTab === 10 && (
              <Box sx={{ p: 2, overflow: 'auto' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Force Simulation
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption">Link Distance: {linkDistance}</Typography>
                  <Slider
                    value={linkDistance}
                    onChange={(_, value) => setLinkDistance(value as number)}
                    min={20}
                    max={300}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption">Charge Strength: {chargeStrength}</Typography>
                  <Slider
                    value={chargeStrength}
                    onChange={(_, value) => setChargeStrength(value as number)}
                    min={-1000}
                    max={0}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption">Link Curvature: {curvature.toFixed(2)}</Typography>
                  <Slider
                    value={curvature}
                    onChange={(_, value) => setCurvature(value as number)}
                    min={0}
                    max={1}
                    step={0.05}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption">Node Size: {nodeSize}</Typography>
                  <Slider
                    value={nodeSize}
                    onChange={(_, value) => setNodeSize(value as number)}
                    min={4}
                    max={20}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption">Link Opacity: {linkOpacity.toFixed(2)}</Typography>
                  <Slider
                    value={linkOpacity}
                    onChange={(_, value) => setLinkOpacity(value as number)}
                    min={0.1}
                    max={1}
                    step={0.05}
                    size="small"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Filters
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={showOnlyKnownDevices}
                      onChange={(e) => setShowOnlyKnownDevices(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show only known devices"
                />
              </Box>
            )}

            {/* Help Tab */}
            {activeTab === 11 && (
              <Box sx={{ p: 2, overflow: 'auto', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)', borderRadius: 3 } }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon /> Link Analysis Guide
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    <strong>Tip:</strong> Ask Sentinel anything! Use the input box on the left panel to ask questions like "Show me shared locations" or "Summarize SMS conversations".
                  </Typography>
                </Alert>

                {/* Overview Section */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GpsFixedIcon sx={{ fontSize: 18 }} /> What is Link Analysis?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Link Analysis visualizes connections between entities (devices, students, trainees) and their digital activity including calls, SMS, locations visited (dwells), browsing history, and app usage.
                  </Typography>
                  <Typography variant="body2">
                    Use it to discover patterns, find shared locations, analyze communication networks, and identify relationships between entities.
                  </Typography>
                </Paper>

                {/* Graph Navigation */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MouseIcon sx={{ fontSize: 18 }} /> Graph Navigation
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">• <strong>Pan</strong>: Click and drag the background</Typography>
                    <Typography variant="body2">• <strong>Zoom</strong>: Mouse wheel or pinch gesture</Typography>
                    <Typography variant="body2">• <strong>Click node</strong>: Highlights connections (see below)</Typography>
                    <Typography variant="body2">• <strong>Right-click node</strong>: Opens menu with Expand, Hide, Zoom to Location, View Details</Typography>
                    <Typography variant="body2">• <strong>Ctrl+Click</strong>: Add/remove nodes from selection</Typography>
                    <Typography variant="body2">• <strong>Labels</strong>: Zoom in to see labels, or use the label toggle button</Typography>
                    <Typography variant="body2">• <strong>White halo</strong>: Node has more connections in the full dataset than are currently shown</Typography>
                  </Stack>
                </Paper>

                {/* Click-to-Highlight */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? 'rgba(0, 255, 255, 0.08)' : 'rgba(0, 188, 212, 0.08)', border: '1px solid', borderColor: isDarkMode ? 'rgba(0, 255, 255, 0.3)' : 'rgba(0, 188, 212, 0.2)' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#00bcd4', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CenterFocusStrongIcon sx={{ fontSize: 18 }} /> Click-to-Highlight (NEW)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Click any node to instantly highlight its connections and fade unrelated nodes:
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">• <strong>Click a node</strong>: The node and its direct connections stay bright, others fade to ~10%</Typography>
                    <Typography variant="body2">• <strong>Cyan glow ring</strong>: Indicates the focused node</Typography>
                    <Typography variant="body2">• <strong>Info panel</strong>: Shows focused node name and connection count at top</Typography>
                    <Typography variant="body2">• <strong>Clear focus</strong>: Click same node again, click empty space, or press <strong>Escape</strong></Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    Tip: Click a phone number to instantly see which entities contacted it. Click a location to see who visited it.
                  </Typography>
                </Paper>

                {/* Selection & Filtering */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FilterAltIcon sx={{ fontSize: 18 }} /> Selection & Filtering
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">• <strong>Select Mode</strong>: Draw a box to select multiple nodes</Typography>
                    <Typography variant="body2">• <strong>Expand Node</strong>: Right-click → Expand to show all connections for a node</Typography>
                    <Typography variant="body2">• <strong>Hide Single-Entity</strong>: Filter button (funnel icon) hides nodes that only connect to one entity</Typography>
                    <Typography variant="body2">• <strong>Fan-out</strong>: Selecting a location shows ALL entities who visited it and ALL their other locations</Typography>
                    <Typography variant="body2">• <strong>Shared Locations</strong>: When multiple entities selected, locations within 200m are highlighted</Typography>
                    <Typography variant="body2">• <strong>Connection Types</strong>: Click pie chart segments to filter by SMS, Calls, Dwells, etc.</Typography>
                  </Stack>
                </Paper>

                {/* Tabs Overview */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TabIcon sx={{ fontSize: 18 }} /> Panel Tabs
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">• <strong>Map</strong>: Geographic view with Dwell Traffic Flow toggle</Typography>
                    <Typography variant="body2">• <strong>Charts</strong>: Activity calendar, connection breakdown, node degree distribution, shared connections matrix</Typography>
                    <Typography variant="body2">• <strong>Timeline</strong>: Temporal heatmap of activity</Typography>
                    <Typography variant="body2">• <strong>Nodes/SMS/Calls/Dwells/Apps</strong>: Data tables with export</Typography>
                    <Typography variant="body2">• <strong>Controls</strong>: Adjust simulation, node size, opacity</Typography>
                    <Typography variant="body2">• <strong>Legend</strong>: Visual guide to colors and symbols</Typography>
                  </Stack>
                </Paper>

                {/* Map Features */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MapIcon sx={{ fontSize: 18 }} /> Map Features
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">• <strong>Dwell Traffic Flow</strong>: Toggle to see movement patterns between locations</Typography>
                    <Typography variant="body2">• <strong>Clustered markers</strong>: Numbers show how many points are grouped</Typography>
                    <Typography variant="body2">• <strong>Draw selection</strong>: Draw a rectangle to filter graph to that area</Typography>
                    <Typography variant="body2">• <strong>Click markers</strong>: See details and connected entities</Typography>
                    <Typography variant="body2">• <strong>Map styles</strong>: Switch between streets, satellite, dark mode</Typography>
                  </Stack>
                </Paper>

                {/* Radial Graph View */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.08)', border: '1px solid', borderColor: isDarkMode ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.2)' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccountTreeIcon sx={{ fontSize: 18 }} /> Radial Graph View (NEW)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Switch to "Radial" view in the toolbar to access the ego-centric radial layout:
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2"><strong>Discovery Mode</strong> (no entity selected):</Typography>
                    <Stack spacing={0.5} sx={{ pl: 2 }}>
                      <Typography variant="body2">• Shows top 50 entities ranked by connection count</Typography>
                      <Typography variant="body2">• Depth bars show L1-L4 hop distances at a glance</Typography>
                      <Typography variant="body2">• Filter by connection type (Calls, SMS, Web, Apps, Dwells)</Typography>
                      <Typography variant="body2">• Click any entity card to investigate it</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Investigation Mode</strong> (entity selected):</Typography>
                    <Stack spacing={0.5} sx={{ pl: 2 }}>
                      <Typography variant="body2">• Seed entity at center, connections in concentric rings</Typography>
                      <Typography variant="body2">• L1 = Direct connections, L2 = 2 hops, L3 = 3 hops, L4 = 4 hops</Typography>
                      <Typography variant="body2">• Use Depth filter (1-4) to limit visible hops</Typography>
                      <Typography variant="body2">• Depth distribution chart shows node counts per level</Typography>
                    </Stack>
                  </Stack>
                </Paper>

                {/* Connection Depth Filter */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccountTreeIcon sx={{ fontSize: 18 }} /> Connection Depth Filter
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    When entities are selected, use the Depth buttons (1-4) in the toolbar:
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">• <strong>Level 1</strong>: Direct connections only (friends)</Typography>
                    <Typography variant="body2">• <strong>Level 2</strong>: Friends of friends</Typography>
                    <Typography variant="body2">• <strong>Level 3</strong>: 3 hops away</Typography>
                    <Typography variant="body2">• <strong>Level 4</strong>: 4 hops away (max depth)</Typography>
                    <Typography variant="body2">• <strong>All</strong>: No depth limit (show full network)</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    Tip: Start with Level 2 for manageable network exploration.
                  </Typography>
                </Paper>

                {/* Ask Sentinel */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? 'rgba(0,191,255,0.1)' : 'rgba(0,123,255,0.08)', border: '1px solid', borderColor: isDarkMode ? 'rgba(0,191,255,0.3)' : 'rgba(0,123,255,0.2)' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'info.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SmartToyIcon sx={{ fontSize: 18 }} /> Ask Sentinel (AI Assistant)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Use the input box in the left panel to ask questions in natural language:
                  </Typography>
                  <Stack spacing={0.5} sx={{ pl: 1 }}>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>{`"Show me shared locations between all ${pluralLower}"`}</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>"Summarize SMS conversations"</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>"Who has the most connections?"</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>"Find clusters in the network"</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>"Analyze call patterns"</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>{`"Show radial view for ${singular}-01"`}</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>"Which entities have the most Level 2 connections?"</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Click the lightbulb icon for pre-built prompt library.
                  </Typography>
                </Paper>

                {/* Performance Tips */}
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BoltIcon sx={{ fontSize: 18 }} /> Performance Tips
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">• <strong>Freeze simulation</strong> (lock icon) when done arranging to save CPU</Typography>
                    <Typography variant="body2">• <strong>Hide single-entity nodes</strong> (filter icon) to focus on shared connections</Typography>
                    <Typography variant="body2">• Use connection type filters to reduce visible links</Typography>
                    <Typography variant="body2">• Canvas mode handles 10,000+ nodes smoothly</Typography>
                    <Typography variant="body2">• Use "Known Only" toggle to hide external phone numbers</Typography>
                  </Stack>
                </Paper>

                {/* Keyboard & Mouse Shortcuts */}
                <Paper elevation={1} sx={{ p: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <KeyboardIcon sx={{ fontSize: 18 }} /> Keyboard & Mouse
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">• <strong>Right-click</strong>: Node context menu (Expand, Hide, Zoom, Details)</Typography>
                    <Typography variant="body2">• <strong>Ctrl + Click</strong>: Add/remove node from selection</Typography>
                    <Typography variant="body2">• <strong>Escape</strong>: Clear selection / Exit selection mode</Typography>
                    <Typography variant="body2">• <strong>Mouse wheel</strong>: Zoom in/out</Typography>
                    <Typography variant="body2">• <strong>Click + Drag</strong>: Pan the graph</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Legend Tab */}
            {activeTab === 12 && (
              <Box sx={{ p: 2, overflow: 'auto' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Network Graph Legend
                </Typography>

                {/* Node Types Section */}
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircleIcon sx={{ fontSize: 18, color: typeColorMap.device }} /> Node Types
                  </Typography>
                  <Stack spacing={1.5}>
                    {[
                      { type: 'device', label: `Device/${singular}`, color: typeColorMap.device, description: `Mobile devices, ${pluralLower}, trainees` },
                      { type: 'location', label: 'Location', color: typeColorMap.location, description: 'Physical places, dwells, geofences' },
                      { type: 'phone', label: 'Phone Number', color: typeColorMap.phone, description: 'External phone numbers' },
                      { type: 'website', label: 'Website', color: typeColorMap.website, description: 'Browsed websites' },
                    ].map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box 
                          sx={{ 
                            width: 20, 
                            height: 20, 
                            borderRadius: '50%', 
                            bgcolor: item.color,
                            border: '2px solid',
                            borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                            flexShrink: 0
                          }} 
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="600">
                            {item.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>

                {/* Connection Types Section */}
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon sx={{ fontSize: 18 }} /> Connection Types (Links)
                  </Typography>
                  <Stack spacing={1.5}>
                    {[
                      { type: 'sms', label: 'SMS', color: typeColorMap.sms, description: 'Text messages between devices' },
                      { type: 'call', label: 'Call', color: typeColorMap.call, description: 'Phone calls between devices' },
                      { type: 'dwell', label: 'Dwell', color: typeColorMap.dwell, description: 'Device presence at a location' },
                      { type: 'browsing', label: 'Browsing', color: typeColorMap.browsing, description: 'Device visited a website' },
                      { type: 'app', label: 'App', color: typeColorMap.app, description: 'Application usage' },
                    ].map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box 
                          sx={{ 
                            width: 40, 
                            height: 3, 
                            bgcolor: item.color,
                            borderRadius: 1,
                            flexShrink: 0
                          }} 
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="600">
                            {item.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>

                {/* Visual States Section */}
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 18 }} /> Visual Indicators
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          bgcolor: '#fff',
                          border: '3px solid #FFD700',
                          position: 'relative',
                          flexShrink: 0,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -6,
                            left: -6,
                            right: -6,
                            bottom: -6,
                            borderRadius: '50%',
                            border: '2px solid #00BFFF',
                            opacity: 0.6
                          }
                        }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600">
                          Selected Location
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          White fill with gold ring and cyan pulsing glow
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          bgcolor: typeColorMap.device,
                          border: '3px solid #EE6C4D',
                          flexShrink: 0
                        }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600">
                          Selected Entity
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Gold ring around node
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          bgcolor: typeColorMap.device,
                          border: `2px solid ${typeColorMap.device}`,
                          opacity: 0.9,
                          flexShrink: 0
                        }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600">
                          Connected Entity
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cyan ring, tracked in fan-out analysis
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Paper>

                {/* Map Legend Section */}
                <Paper elevation={2} sx={{ p: 2, bgcolor: isDarkMode ? '#0d1117' : '#f5f5f5' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MapIcon sx={{ fontSize: 18 }} /> Map Markers
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          bgcolor: '#ef4444',
                          flexShrink: 0
                        }} 
                      />
                      <Typography variant="body2">Dwell Location</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          bgcolor: '#3b82f6',
                          flexShrink: 0
                        }} 
                      />
                      <Typography variant="body2">Call Location</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          bgcolor: '#10b981',
                          flexShrink: 0
                        }} 
                      />
                      <Typography variant="body2">SMS Location</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          bgcolor: '#9ca3af',
                          flexShrink: 0
                        }} 
                      />
                      <Typography variant="body2">Clustered Points</Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Tip:</strong> Shared locations (within 200m) are highlighted when you select multiple entities.
                  </Typography>
                </Alert>
              </Box>
            )}
              </Box>
            )}
          </Box>
        )}

        {/* Tool rail: vertical grouped icon strip on the panel's outer edge,
            styled to match the Pathfinder tool rail. Hover a tool for its
            name and record count. Clicking a tool on a collapsed panel
            reopens the panel on that tool. */}
        <Box
          role="toolbar"
          aria-label="Link analysis panels"
          sx={{
            width: 48,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 0.5,
            gap: 0.25,
            borderLeft: sidePanelOpen ? 1 : 0,
            borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.background.paper, 0.72),
            backdropFilter: 'blur(10px)',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Tooltip
            title={sidePanelOpen ? 'Collapse panel' : 'Expand panel'}
            placement="left"
            enterDelay={300}
          >
            <IconButton
              size="small"
              aria-label={sidePanelOpen ? 'Collapse panel' : 'Expand panel'}
              onClick={() => setSidePanelOpen(!sidePanelOpen)}
              sx={{ mb: 0.25 }}
            >
              {sidePanelOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
          {sideRailGroups.map((group, gi) => (
            <Box
              key={group.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                pt: 0.75,
                mt: gi === 0 ? 0 : 0.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                  lineHeight: 1,
                  mb: 0.5,
                  userSelect: 'none',
                }}
              >
                {group.label}
              </Typography>
              {group.tools.map((tool) => {
                const selected = sidePanelOpen && tool.index === activeTab;
                return (
                  <Tooltip key={tool.index} title={tool.label} placement="left" enterDelay={300}>
                    <IconButton
                      size="small"
                      aria-label={tool.label}
                      aria-pressed={selected}
                      onClick={() => {
                        if (!sidePanelOpen) setSidePanelOpen(true);
                        setActiveTab(tool.index);
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        mb: 0.25,
                        borderRadius: 1,
                        color: selected ? 'primary.contrastText' : 'text.secondary',
                        bgcolor: selected ? 'primary.main' : 'transparent',
                        boxShadow: selected
                          ? (t) => `0 0 10px ${alpha(t.palette.primary.main, 0.45)}`
                          : 'none',
                        '&:hover': {
                          bgcolor: selected
                            ? 'primary.dark'
                            : (t) => alpha(t.palette.action.hover, 0.8),
                          color: selected ? 'primary.contrastText' : 'text.primary',
                        },
                        '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
                      }}
                    >
                      {tool.icon}
                    </IconButton>
                  </Tooltip>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? '#1a1a2e' : '#ffffff',
            border: isDarkMode ? '1px solid #00f0ff' : '1px solid #ccc',
            boxShadow: isDarkMode ? '0 0 20px rgba(0, 240, 255, 0.3)' : '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        {contextMenu && (
          <>
            <Box sx={{ px: 2, py: 1, borderBottom: isDarkMode ? '1px solid #30363d' : '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#8b949e' : '#666' }}>
                Node: {contextMenu.node.label || contextMenu.node.id}
              </Typography>
              {contextMenu.node.type && (
                <Chip 
                  label={contextMenu.node.type} 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: typeColorMap[contextMenu.node.type] || '#666',
                    color: '#fff'
                  }} 
                />
              )}
            </Box>
            
            <MenuItem onClick={() => contextMenu && handleExportSubgraph(contextMenu.node)} disabled={!onExportSubgraph}>
              <ListItemIcon>
                <AccountTreeIcon sx={{ color: isDarkMode ? '#00f0ff' : '#1976d2' }} />
              </ListItemIcon>
              <ListItemText primary="Export Subset Graph" />
              <OpenInNewIcon sx={{ color: isDarkMode ? '#8b949e' : '#666', fontSize: 16, ml: 1 }} />
            </MenuItem>
            
            {onToggleExpand && (
              <MenuItem 
                onClick={() => {
                  if (contextMenu) {
                    if (hiddenNodes?.has(contextMenu.node.id) && onToggleHide) {
                      onToggleHide(contextMenu.node.id);
                    }
                    if (!expandedNodes?.has(contextMenu.node.id)) {
                      onToggleExpand(contextMenu.node.id);
                    }
                  }
                  handleCloseContextMenu();
                }}
                disabled={
                  expandedNodes?.has(contextMenu.node.id) ||
                  !nodeExpandability.get(contextMenu?.node.id || '')
                }
              >
                <ListItemIcon>
                  <UnfoldMoreIcon sx={{ color: isDarkMode ? '#00f0ff' : '#1976d2' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Expand Node"
                  secondary={
                    expandedNodes?.has(contextMenu.node.id)
                      ? "Already expanded"
                      : nodeExpandability.get(contextMenu.node.id)
                        ? "Show all connections from full dataset"
                        : "No additional connections to reveal"
                  }
                />
              </MenuItem>
            )}
            
            {(onToggleExpand || onToggleHide) && (
              <MenuItem 
                onClick={() => {
                  if (contextMenu) {
                    if (expandedNodes?.has(contextMenu.node.id) && onToggleExpand) {
                      onToggleExpand(contextMenu.node.id);
                    } else if (onToggleHide) {
                      onToggleHide(contextMenu.node.id);
                    }
                  }
                  handleCloseContextMenu();
                }}
              >
                <ListItemIcon>
                  <UnfoldLessIcon sx={{ color: isDarkMode ? '#f59e0b' : '#ed6c02' }} />
                </ListItemIcon>
                <ListItemText 
                  primary={expandedNodes?.has(contextMenu.node.id) ? "Collapse Node" : "Hide Node"}
                />
              </MenuItem>
            )}
            
            <Divider />
            
            <MenuItem onClick={() => {
              if (contextMenu) {
                const node = contextMenu.node;
                // Calculate connection stats for this node
                const nodeLinks = filteredLinks.filter((l: any) => {
                  const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
                  const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
                  return sourceId === node.id || targetId === node.id;
                });
                
                const stats = {
                  totalConnections: nodeLinks.length,
                  callConnections: nodeLinks.filter((l: any) => l.type === 'call').length,
                  smsConnections: nodeLinks.filter((l: any) => l.type === 'sms').length,
                  browsingConnections: nodeLinks.filter((l: any) => l.type === 'browsing').length,
                  appConnections: nodeLinks.filter((l: any) => l.type === 'app').length,
                  dwellConnections: nodeLinks.filter((l: any) => l.type === 'dwell').length,
                  connectedDevices: Array.from(new Set(nodeLinks.map((l: any) => {
                    const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
                    const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
                    const otherId = sourceId === node.id ? targetId : sourceId;
                    const otherNode = filteredNodes.find((n: any) => n.id === otherId);
                    return otherNode?.type === 'device' ? otherId : null;
                  }).filter(Boolean))) as string[],
                  connectedLocations: Array.from(new Set(nodeLinks.map((l: any) => {
                    const sourceId = typeof l.source === 'string' ? l.source : l.source?.id;
                    const targetId = typeof l.target === 'string' ? l.target : l.target?.id;
                    const otherId = sourceId === node.id ? targetId : sourceId;
                    const otherNode = filteredNodes.find((n: any) => n.id === otherId);
                    return otherNode?.type === 'location' ? (otherNode.label || otherId) : null;
                  }).filter(Boolean))) as string[],
                };
                
                if (onViewDetails) {
                  onViewDetails(node, stats);
                } else if (onNodeClick) {
                  onNodeClick(node);
                }
              }
              handleCloseContextMenu();
            }}>
              <ListItemIcon>
                <InfoIcon sx={{ color: isDarkMode ? '#00ff88' : '#2e7d32' }} />
              </ListItemIcon>
              <ListItemText primary="View Details" />
            </MenuItem>
            
            <MenuItem onClick={() => contextMenu && handleZoomToLocation(contextMenu.node)}>
              <ListItemIcon>
                <LocationOnIcon sx={{ color: isDarkMode ? '#00f0ff' : '#1976d2' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Zoom to Location" 
                secondary={contextMenu.node.type === 'location' ? "Show on map" : "Show connected locations"} 
              />
            </MenuItem>
            
            {contextMenu.node.type === 'device' && onAddDeviceNote && (
              <>
                <Divider />
                <MenuItem onClick={() => {
                  const deviceId = contextMenu.node.id;
                  const deviceLabel = contextMenu.node.label || contextMenu.node.id;
                  onAddDeviceNote(deviceId, deviceLabel);
                  handleCloseContextMenu();
                }}>
                  <ListItemIcon>
                    <NoteAddIcon sx={{ color: isDarkMode ? '#f59e0b' : '#ed6c02' }} />
                  </ListItemIcon>
                  <ListItemText primary="Add Note" />
                </MenuItem>
              </>
            )}
            
            {contextMenu.node.type === 'device' && onFindSimilar && (
              <>
                <Divider />
                <MenuItem onClick={() => {
                  const deviceId = contextMenu.node.id;
                  const deviceLabel = contextMenu.node.label || contextMenu.node.id;
                  onFindSimilar(deviceId, deviceLabel);
                  handleCloseContextMenu();
                }}>
                  <ListItemIcon>
                    <PeopleIcon sx={{ color: isDarkMode ? '#a78bfa' : '#7c3aed' }} />
                  </ListItemIcon>
                  <ListItemText primary="Find Similar Profiles" />
                </MenuItem>
              </>
            )}
            
            {contextMenu.node.type === 'location' && (
              <>
                <Divider />
                {onSaveAsGeofence && (
                  <MenuItem onClick={() => {
                    const node = contextMenu.node as any;
                    const name = node.place_name || node.label || `Location ${node.id}`;
                    onSaveAsGeofence({ lat: 0, lng: 0, name, placeType: node.place_type });
                    handleCloseContextMenu();
                  }}>
                    <ListItemIcon>
                      <BookmarkAddIcon sx={{ color: isDarkMode ? '#ec4899' : '#d81b60' }} />
                    </ListItemIcon>
                    <ListItemText primary="Save as Geofence" />
                  </MenuItem>
                )}
                {onAddLocationNote && (
                  <MenuItem onClick={() => {
                    const node = contextMenu.node as any;
                    const name = node.place_name || node.label || `Location ${node.id}`;
                    onAddLocationNote({ lat: 0, lng: 0, name });
                    handleCloseContextMenu();
                  }}>
                    <ListItemIcon>
                      <NoteAddIcon sx={{ color: isDarkMode ? '#ec4899' : '#d81b60' }} />
                    </ListItemIcon>
                    <ListItemText primary="Add Location Note" />
                  </MenuItem>
                )}
              </>
            )}
          </>
        )}
      </Menu>

      {/* PowerPoint Theme Selection Dialog */}
      <Dialog 
        open={themeDialogOpen} 
        onClose={() => setThemeDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" component="span">
                Select PowerPoint Theme
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Choose a visual style for your exported presentation
              </Typography>
            </Box>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setThemeDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {(Object.entries(THEME_DEFINITIONS) as [PowerPointTheme, typeof THEME_DEFINITIONS[PowerPointTheme]][]).map(([themeKey, themeDef]) => (
              <Grid item xs={12} sm={6} md={4} key={themeKey}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedTheme === themeKey ? '2px solid' : '1px solid',
                    borderColor: selectedTheme === themeKey ? 'primary.main' : 'divider',
                    bgcolor: selectedTheme === themeKey ? 'action.selected' : 'background.paper',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.light',
                      bgcolor: 'action.hover',
                    }
                  }}
                >
                  <CardActionArea onClick={() => setSelectedTheme(themeKey)}>
                    {/* Theme Preview */}
                    <Box 
                      sx={{ 
                        height: 80, 
                        background: `linear-gradient(135deg, #${themeDef.colors.titleBg} 0%, #${themeDef.colors.primary}40 50%, #${themeDef.colors.contentBg} 100%)`,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {/* Accent line */}
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          bgcolor: `#${themeDef.colors.accent}`
                        }}
                      />
                      {/* Selected indicator */}
                      {selectedTheme === themeKey && (
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      )}
                    </Box>
                    <CardContent sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Radio 
                          checked={selectedTheme === themeKey}
                          size="small"
                          sx={{ p: 0 }}
                        />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {themeDef.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {themeDef.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setThemeDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => exportToPowerPoint(selectedTheme)} 
            variant="contained" 
            color="primary"
            startIcon={<PowerPointIcon />}
          >
            Export with {THEME_DEFINITIONS[selectedTheme].name}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──────────────────────────────────────────────────────────────────
          Search Commonalities — floating pop-out panel.
          A non-modal floating Paper (not a Dialog) so analysts can still see
          and interact with the link graph and side panel underneath while
          they study the larger commonalities view. Shows up to 15 entities ×
          15 terms with comfortable row heights for deeper reading. All
          clicks here cross-filter the same way the inline graph does.
      ────────────────────────────────────────────────────────────────── */}
      {commonalityPopoutOpen &&
        commonalityGraph.entities.length > 0 &&
        commonalityGraph.terms.length > 0 && (
          <Paper
            elevation={12}
            sx={{
              position: 'fixed',
              top: 88,
              right: 24,
              width: { xs: 'calc(100vw - 48px)', md: 'min(680px, 55vw)' },
              maxHeight: 'calc(100vh - 120px)',
              zIndex: (t) => t.zIndex.modal + 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 1.5,
              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
              bgcolor: isDarkMode ? 'rgba(20,24,32,0.98)' : 'background.paper',
              backdropFilter: 'blur(8px)',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                borderBottom: `1px solid ${
                  isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
                }`,
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Search Commonalities
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.65rem' }}
                >
                  Hover to trace · click to filter the list and the link graph
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setCommonalityPopoutOpen(false)}
                aria-label="Close commonalities pop-out"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Stats strip */}
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
                borderBottom: `1px solid ${
                  isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                }`,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                top {Math.min(commonalityGraph.entities.length, 15)} of{' '}
                {commonalityGraph.totalEntities} entities · top{' '}
                {Math.min(commonalityGraph.terms.length, 15)} of {commonalityGraph.totalTerms} terms
                {(searchEngineFilter || searchEntityFilter || searchTermFilter.trim()) && (
                  <> · scoped to current filters</>
                )}
              </Typography>
              {(searchEntityFilter || searchTermFilter.trim() || searchEngineFilter) && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearchEngineFilter(null);
                    setSearchEntityFilter(null);
                    setSearchTermFilter('');
                    setSelectedNodes(new Set());
                    setFocusedNodeId(null);
                    if (onSelectionChange) onSelectionChange([]);
                  }}
                  sx={{ fontSize: '0.65rem', minWidth: 0, py: 0, px: 0.75 }}
                >
                  Clear filters
                </Button>
              )}
            </Box>

            {/* Graph body */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {renderCommonalityGraphBody({
                entityLimit: 15,
                termLimit: 15,
                rowHeight: 30,
              })}

              {(commonalityGraph.totalEntities > 15 || commonalityGraph.totalTerms > 15) && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', mt: 1, fontSize: '0.65rem' }}
                >
                  {commonalityGraph.totalEntities > 15
                    ? `+${commonalityGraph.totalEntities - 15} more entities`
                    : null}
                  {commonalityGraph.totalEntities > 15 &&
                    commonalityGraph.totalTerms > 15 &&
                    ' · '}
                  {commonalityGraph.totalTerms > 15
                    ? `+${commonalityGraph.totalTerms - 15} more terms`
                    : null}
                  {' — use the filter in the side panel to narrow the view'}
                </Typography>
              )}
            </Box>
          </Paper>
        )}

      <LinkAnalysisToolPalette
        open={toolPaletteOpen}
        onClose={() => setToolPaletteOpen(false)}
        tools={sideRailTools}
        activeIndex={activeTab}
        onSelect={(index) => {
          if (!sidePanelOpen) setSidePanelOpen(true);
          setActiveTab(index);
        }}
      />
    </Box>
  );
});

export default NetworkGraph;

